/**
 * Mitarbeiter-Detail — Detailansicht eines Mitarbeiters mit Tabs fuer alle Bereiche.
 *
 * Features:
 * - Stammdaten, Vertragsverwaltung (Upload/Download), Arbeitszeiten
 * - Anwesenheits-Kalender, Urlaubs-/Abwesenheitsuebersicht
 * - Performance-Daten (Wave/Pack), Effektive Arbeitszeit
 * - Ueberstunden-Saldo, App-Zugang verwalten, Stundensatz
 *
 * @page /mitarbeiter/:id
 * @roles admin, manager
 * @api /api/mitarbeiter/:id, /api/mitarbeiter/:id/vertraege, /api/mitarbeiter/:id/arbeitszeiten
 * @api /api/mitarbeiter/:id/attendance (lokal), /api/mitarbeiter/:id/leaves (lokal)
 * @api /api/mitarbeiter/:id/ueberstunden (lokal), /api/mitarbeiter/:id/urlaub (lokal)
 * @api /api/mitarbeiter/:id/kpi-zusammenfassung, /api/mitarbeiter/:id/effektive-arbeitszeit
 * @api /api/mitarbeiter/:id/app-zugang, /api/performance/mitarbeiter/:id
 * @i18n employees.detail
 */
import React, { useState, useEffect, useMemo, useCallback, useRef, createContext, useContext } from 'react';
import { useParams, NavLink, Link, useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import useTabParam from '../hooks/useTabParam';
import { PageHeader, Badge, Button, ToggleSwitch } from '../components/ui';
import BackLink from '../components/BackLink';
import ConfirmDialog from '../components/ConfirmDialog';
import { useDialog } from '../hooks/useDialog';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { CalendarDays, RefreshCw, Pencil, Trash2, Plus, AlertTriangle, X, LogOut, Activity, UserX, StickyNote, FileDown } from 'lucide-react';
import AustrittModal from '../components/AustrittModal';
import EmptyState from '../components/EmptyState';
import SchulungenTab from '../components/mitarbeiter/SchulungenTab';
import OverrideReasonModal from '../components/mitarbeiter/OverrideReasonModal';

// ─── Helpers ───
function api(path) {
 return fetch(path).then(r => r.json());
}
function fmtDate(d, locale = 'de-AT') {
 if (!d) return '–';
 return new Date(d).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function fmtTime(iso) {
 if (!iso) return '–';
 return iso.slice(11, 16);
}
function fmtHours(h, locale = 'de-DE') {
 if (h == null || h === 0) return '–';
 return h.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + 'h';
}
function fmtNum(n, dec = 1, locale = 'de-DE') {
 if (n == null) return '–';
 return Number(n).toLocaleString(locale, { minimumFractionDigits: dec, maximumFractionDigits: dec });
}
function pad2(n) { return String(n).padStart(2, '0'); }

// #1208 Pre-Eintritts-State — true wenn eintritt_datum strikt in der Zukunft liegt.
// String-Vergleich auf YYYY-MM-DD (TZ-immun) gegen heute (lokal).
function isVorEintritt(ma) {
  if (!ma?.eintritt_datum) return false;
  const heuteStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  })();
  const eintrittStr = String(ma.eintritt_datum).slice(0, 10);
  return eintrittStr > heuteStr;
}

// #1208 Wartefrist-Ende — eintritt + 6 Kalendermonate, YYYY-MM-DD. Null bei fehlendem Eintritt.
// Konsistent mit berechneAufgebauteTage() im Backend (urlaub-berechnung.js).
function wartefristEndeStr(ma) {
  if (!ma?.eintritt_datum) return null;
  const [y, m, d] = String(ma.eintritt_datum).slice(0, 10).split('-').map(Number);
  const ende = new Date(y, m - 1 + 6, d);
  return `${ende.getFullYear()}-${pad2(ende.getMonth() + 1)}-${pad2(ende.getDate())}`;
}

// #1208 Wartefrist-Aware — true wenn heute strikt vor Wartefrist-Ende liegt.
function inWartefrist(ma) {
  const ende = wartefristEndeStr(ma);
  if (!ende) return false;
  const heuteStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  })();
  return heuteStr < ende;
}

// addDaysStr: add n days to a YYYY-MM-DD string using pure string math.
// Keine Date-Objekte → TZ-immun, kein UTC-Shift wie bei new Date('YYYY-MM-DDT00:00:00').
// Siehe CLAUDE.md "Timezone-Fix global seit v2.370.0".
function addDaysStr(ymd, n) {
 if (!ymd) return null;
 let [y, m, d] = ymd.slice(0, 10).split('-').map(Number);
 const isLeap = (yr) => (yr % 4 === 0 && yr % 100 !== 0) || yr % 400 === 0;
 const dim = (yr, mo) => mo === 2 ? (isLeap(yr) ? 29 : 28) : ([4, 6, 9, 11].includes(mo) ? 30 : 31);
 d += n;
 while (d < 1) { m--; if (m < 1) { m = 12; y--; } d += dim(y, m); }
 while (d > dim(y, m)) { d -= dim(y, m); m++; if (m > 12) { m = 1; y++; } }
 return `${y}-${pad2(m)}-${pad2(d)}`;
}
function dateStr(d) { return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; }
function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function startOfWeek(d) {
 const r = new Date(d);
 const day = r.getDay();
 const diff = day === 0 ? 6 : day - 1;
 r.setDate(r.getDate() - diff);
 return r;
}
function timeSince(dateStr, t) {
 if (!dateStr) return null;
 const start = new Date(dateStr);
 const now = new Date();
 let years = now.getFullYear() - start.getFullYear();
 let months = now.getMonth() - start.getMonth();
 if (months < 0) { years--; months += 12; }
 const parts = [];
 if (years > 0) parts.push(years > 1 ? t('employees.detail.years_plural', { n: years }) : t('employees.detail.years_singular', { n: years }));
 if (months > 0) parts.push(months > 1 ? t('employees.detail.months_plural', { n: months }) : t('employees.detail.months_singular', { n: months }));
 return parts.join(', ') || t('employees.detail.less_than_1_month');
}

// WEEKDAYS: Mon–Sun mapped from JS getDay() convention (1=Mon,...,0=Sun)
const WEEKDAY_KEYS = [1, 2, 3, 4, 5, 6, 0]; // Mon, Tue, Wed, Thu, Fri, Sat, Sun

const stateColor = {
 confirm: 'text-emerald-400', validate: 'text-emerald-400', validate1: 'text-yellow-400',
 refuse: 'text-red-400', draft: 'text-[var(--text-secondary)]', cancel: 'text-[var(--text-tertiary)]',
};

// ─── Card Component ───
function Card({ title, icon, children, className = '' }) {
 return (
 <div className={`bg-surface-card border border-line rounded-card ${className}`}>
 <div className="flex items-center gap-2 px-5 py-3 border-b border-line">
 {icon && <span className="text-[var(--text-secondary)]">{icon}</span>}
 <h3 className="text-sm font-bold text-content">{title}</h3>
 </div>
 <div className="p-5">{children}</div>
 </div>
 );
}

function InfoRow({ label, value, mono = false }) {
 return (
 <div className="flex justify-between py-1.5 border-b border-line/50 last:border-0">
 <span className="text-xs text-[var(--text-secondary)]">{label}</span>
 <span className={`text-sm text-content-secondary ${mono ? 'font-mono' : ''}`}>{value || '–'}</span>
 </div>
 );
}

// ═══ KPI-Summary (4 Mini-KPIs unter Header, #1745) ═══
function KpiSummaryCard({ id }) {
 const { t, locale } = useLanguage();
 const [data, setData] = useState(null);
 useEffect(() => {
 api(`/api/mitarbeiter/${id}/kpi-zusammenfassung`)
 .then(d => { if (!d.error) setData(d); })
 .catch(() => {});
 }, [id]);

 if (!data) return null;

 const kpis = [
 {
 label: t('employees.detail.kpi_ist_monat'),
 value: `${data.ist_stunden_monat}h`,
 sub: data.soll_stunden_monat > 0 ? `/ ${data.soll_stunden_monat}h` : null,
 },
 {
 // #1745: Stand heute (aktuell_verfuegbar) — Format wie VacationCard-Hero (2 Dezimalen)
 label: t('employees.detail.kpi_urlaub_rest'),
 value: fmtNum(data.urlaub_rest, 2, locale),
 sub: t('employees.detail.kpi_tage'),
 },
 {
 // #1745: echter ZA-Saldo (SSoT #1006) — Format wie OvertimeCard-Hero (Vorzeichen + 1 Dezimale)
 label: t('employees.detail.kpi_ueberstunden'),
 value: `${data.ueberstunden_saldo >= 0 ? '+' : ''}${fmtNum(data.ueberstunden_saldo, 1, locale)}h`,
 sub: null,
 },
 {
 // #1745: Dezimalen nur wenn vorhanden — identisch zur KrankenstandCard im Tab
 label: t('employees.detail.kpi_krank_tage'),
 value: fmtNum(data.krank_tage_jahr, data.krank_tage_jahr % 1 ? 1 : 0, locale),
 sub: t('employees.detail.kpi_tage'),
 },
 ];

 return (
 <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
 {kpis.map((k, i) => (
 <div key={i} className="bg-surface-card border border-line rounded-card px-4 py-3 text-center">
 <div className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider mb-1">{k.label}</div>
 <div className={`text-lg font-mono font-extrabold text-[var(--text-primary)]`}>{k.value}</div>
 {k.sub && <div className="text-[10px] text-[var(--text-tertiary)]">{k.sub}</div>}
 </div>
 ))}
 </div>
 );
}

// ═══ NEUE KARTE: Überstunden & Arbeitszeit (v2.353.0 Redesign #808) ═══
function OvertimeCard({ id }) {
 const { t, locale } = useLanguage();
 const navigate = useNavigate();
 const [data, setData] = useState(null);
 const [loading, setLoading] = useState(true);
 const [showBreakdown, setShowBreakdown] = useState(false);

 useEffect(() => {
 api(`/api/mitarbeiter/${id}/ueberstunden`)
 .then(d => { if (!d.error) setData(d); })
 .finally(() => setLoading(false));
 }, [id]);

 // Farb-Logik für ZA-Saldo Hauptzahl: grün wenn positiv, rot wenn negativ, grau wenn 0
 const saldoColor = (v) => {
 if (v > 0.1) return '#10B981'; // emerald
 if (v < -0.1) return '#EF4444'; // red
 return 'var(--text-primary)';
 };

 // Diff-Farbe für Periode-Werte: grün = gut (ist>=soll), gelb = +Überstunden, rot = zu wenig
 const diffColor = (v) => {
 if (v >= -0.5 && v <= 0.5) return 'text-emerald-500';
 if (v > 0.5) return 'text-amber-500';
 return 'text-red-500';
 };
 const diffBg = (v) => {
 if (v >= -0.5 && v <= 0.5) return 'bg-emerald-500';
 if (v > 0.5) return 'bg-amber-500';
 return 'bg-red-500';
 };

 // Bar-Farbe für Recharts-Wochenverlauf
 const barColor = (diff, laufend) => {
 if (laufend) return '#9CA3AF'; // grau für laufende Woche
 if (diff > 0.5) return '#F59E0B'; // amber
 if (diff < -0.5) return '#EF4444'; // red
 return '#10B981'; // emerald
 };

 // Trend-Icon + Farbe
 const trendInfo = (v) => {
 if (v == null) return { icon: '→', color: 'var(--text-tertiary)' };
 if (v > 2) return { icon: '↑', color: '#F59E0B' };
 if (v > 0.5) return { icon: '↗', color: '#10B981' };
 if (v < -2) return { icon: '↓', color: '#EF4444' };
 if (v < -0.5) return { icon: '↘', color: '#F59E0B' };
 return { icon: '→', color: 'var(--text-tertiary)' };
 };

 // Stand-Datum formatieren
 const formatStand = (dateStr, isEingestempelt, arbeitstagStart) => {
 if (!dateStr) return '–';
 const d = new Date(dateStr + 'T00:00:00');
 const short = d.toLocaleDateString(locale === 'de' ? 'de-AT' : 'en-US', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
 if (isEingestempelt && arbeitstagStart) {
 const hhmm = new Date(arbeitstagStart).toLocaleTimeString(locale === 'de' ? 'de-AT' : 'en-US', { hour: '2-digit', minute: '2-digit' });
 return { short, running: t('employees.detail.overtime_running_since', { time: hhmm }) };
 }
 return { short, running: null };
 };

 // Custom Tooltip für Wochenverlauf-Chart
 const ChartTooltip = ({ active, payload }) => {
 if (!active || !payload?.length) return null;
 const p = payload[0].payload;
 const fmt = (v) => (v >= 0 ? '+' : '') + fmtNum(v, 1, locale);
 return (
 <div className="bg-surface-card border border-line rounded-lg px-3 py-2 shadow-sm text-xs">
 <div className="font-bold text-[var(--text-primary)] mb-1">{p.kw} ({p.jahr})</div>
 <div className="text-[var(--text-secondary)]">
 {p.start_datum.slice(-5).replace('-', '.')} – {p.ende_datum.slice(-5).replace('-', '.')}
 </div>
 <div className="mt-1 space-y-0.5">
 <div className="flex gap-3 justify-between">
 <span className="text-[var(--text-tertiary)]">{t('employees.detail.target_col')}:</span>
 <span className="font-mono text-[var(--text-secondary)]">{fmtNum(p.soll, 1, locale)}h</span>
 </div>
 <div className="flex gap-3 justify-between">
 <span className="text-[var(--text-tertiary)]">IST:</span>
 <span className="font-mono text-[var(--text-secondary)]">{fmtNum(p.ist, 1, locale)}h</span>
 </div>
 <div className="flex gap-3 justify-between border-t border-line pt-0.5 mt-0.5">
 <span className="text-[var(--text-tertiary)]">Δ:</span>
 <span className="font-mono font-bold" style={{ color: barColor(p.diff, p.laufend) }}>{fmt(p.diff)}h</span>
 </div>
 {p.laufend && <div className="text-[10px] text-[var(--text-tertiary)] italic mt-0.5">{t('employees.detail.overtime_week_running')}</div>}
 </div>
 </div>
 );
 };

 const handleBarClick = (entry) => {
 if (!entry) return;
 const monat = `${entry.jahr}-${String(entry.monat).padStart(2, '0')}-01`;
 navigate(`/zeitkonto?ma=${id}&monat=${monat}`);
 };

 // Haupt-Zahl & Derived-Werte
 const vorjahr = data?.za_saldo_vorjahr || 0;
 const ytd = data?.ytd_delta || 0;
 const saldo = data?.za_saldo || 0;
 const trend = data?.trend_h_per_month;
 const trendI = trendInfo(trend);
 const standInfo = data ? formatStand(data.stand_datum, data.is_eingestempelt, data.arbeitstag_start) : null;
 const jahrVorjahr = new Date().getFullYear() - 1;

 return (
 <Card title={t('employees.detail.overtime_title')} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#338edc" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>}>
 {loading ? (
 <div className="text-xs text-[var(--text-tertiary)] py-4 text-center">{t('employees.detail.loading_overtime')}</div>
 ) : !data ? (
 <div className="text-xs text-[var(--text-tertiary)] py-4 text-center">{t('employees.detail.no_data_available')}</div>
 ) : (
 <>
 {/* ── HERO: ZA-Saldo als EINE Hauptzahl ── */}
 <div className="text-center mb-4 pb-4 border-b border-line">
 <div className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider mb-1" title={t('employees.detail.overtime_za_saldo_tooltip')}>
 {t('employees.detail.overtime_za_saldo_label')}
 </div>
 <div className="text-4xl font-mono font-extrabold leading-tight" style={{ color: saldoColor(saldo) }}>
 {saldo >= 0 ? '+' : ''}{fmtNum(saldo, 1, locale)}h
 </div>
 {/* Sublabel: YTD-Delta + Trend */}
 <div className="flex items-center justify-center gap-3 mt-1 text-xs">
 <span className="text-[var(--text-secondary)]">
 {t('employees.detail.overtime_ytd_prefix')}: <span className="font-mono font-bold" style={{ color: saldoColor(ytd) }}>{ytd >= 0 ? '+' : ''}{fmtNum(ytd, 1, locale)}h</span>
 </span>
 {trend != null && (
 <>
 <span className="text-[var(--text-tertiary)]">·</span>
 <span className="flex items-center gap-1">
 <span className="text-base font-bold" style={{ color: trendI.color }}>{trendI.icon}</span>
 <span className="font-mono" style={{ color: trendI.color }}>
 {trend >= 0 ? '+' : ''}{fmtNum(trend, 1, locale)}h/{t('employees.detail.overtime_per_month')}
 </span>
 </span>
 </>
 )}
 </div>
 {/* Stand-Info */}
 {standInfo && (
 <div className="text-[11px] text-[var(--text-tertiary)] mt-2">
 <span>{t('employees.detail.stand_per_short')}: {standInfo.short}</span>
 {standInfo.running && (
 <span className="ml-1 inline-flex items-center gap-1">
 <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
 <span className="text-emerald-500 font-medium">{standInfo.running}</span>
 </span>
 )}
 </div>
 )}
 {/* Aufschlüsselung (Disclosure) */}
 <button
 onClick={() => setShowBreakdown(!showBreakdown)}
 className="mt-2 text-[10px] text-[#338edc] hover:underline"
 >
 {showBreakdown ? t('employees.detail.overtime_hide_breakdown') : t('employees.detail.overtime_show_breakdown')}
 </button>
 {showBreakdown && (
 <div className="mt-2 rounded-lg bg-surface-inset border border-line px-3 py-2 text-left text-[11px] font-mono">
 <div className="flex justify-between">
 <span className="text-[var(--text-secondary)]">{t('employees.detail.overtime_vorjahr_label', { jahr: jahrVorjahr })}:</span>
 <span style={{ color: saldoColor(vorjahr) }} className="font-bold">{vorjahr >= 0 ? '+' : ''}{fmtNum(vorjahr, 1, locale)}h</span>
 </div>
 <div className="flex justify-between">
 <span className="text-[var(--text-secondary)]">+ {t('employees.detail.overtime_ytd_breakdown_label', { year: new Date().getFullYear() })}:</span>
 <span style={{ color: saldoColor(ytd) }} className="font-bold">{ytd >= 0 ? '+' : ''}{fmtNum(ytd, 1, locale)}h</span>
 </div>
 <div className="flex justify-between border-t border-line mt-1 pt-1">
 <span className="text-[var(--text-primary)] font-bold">= {t('employees.detail.overtime_za_saldo_label')}:</span>
 <span style={{ color: saldoColor(saldo) }} className="font-bold">{saldo >= 0 ? '+' : ''}{fmtNum(saldo, 1, locale)}h</span>
 </div>
 </div>
 )}
 </div>

 {/* ── Diese Woche (SOLL nur abgeschlossene Tage) ── */}
 <div className="mb-4">
 <div className="flex items-center justify-between mb-1">
 <span className="text-xs text-[var(--text-secondary)] font-medium">{t('employees.detail.this_week')}</span>
 <span className={`text-xs font-mono font-bold ${diffColor(data.diese_woche.diff)}`}>
 {data.diese_woche.diff >= 0 ? '+' : ''}{fmtNum(data.diese_woche.diff, 1, locale)}h
 </span>
 </div>
 <div className="flex items-center gap-2">
 <div className="flex-1 h-2 bg-surface-inset rounded-full overflow-hidden">
 <div className={`h-full rounded-full transition-all ${diffBg(data.diese_woche.diff)}`}
 style={{ width: `${data.diese_woche.soll > 0 ? Math.min(100, (data.diese_woche.ist / data.diese_woche.soll) * 100) : 0}%` }} />
 </div>
 <span className="text-[10px] font-mono text-[var(--text-tertiary)] w-28 text-right">
 {fmtNum(data.diese_woche.ist, 1, locale)}/{fmtNum(data.diese_woche.soll, 1, locale)}h
 </span>
 </div>
 {data.diese_woche.offener_tag && (
 <div className="text-[10px] text-[var(--text-tertiary)] mt-1 italic">
 {t('employees.detail.overtime_week_open_day', {
 abgeschlossen: data.diese_woche.abgeschlossene_tage,
 })}
 </div>
 )}
 </div>

 {/* ── Dieser Monat ── */}
 <div className="mb-4">
 <div className="flex items-center justify-between mb-1">
 <span className="text-xs text-[var(--text-secondary)] font-medium">{t('employees.detail.this_month')}</span>
 <span className={`text-xs font-mono font-bold ${diffColor(data.dieser_monat.diff)}`}>
 {data.dieser_monat.diff >= 0 ? '+' : ''}{fmtNum(data.dieser_monat.diff, 1, locale)}h
 </span>
 </div>
 <div className="flex items-center gap-2">
 <div className="flex-1 h-2 bg-surface-inset rounded-full overflow-hidden">
 <div className={`h-full rounded-full transition-all ${diffBg(data.dieser_monat.diff)}`}
 style={{ width: `${data.dieser_monat.soll > 0 ? Math.min(100, (data.dieser_monat.ist / data.dieser_monat.soll) * 100) : 0}%` }} />
 </div>
 <span className="text-[10px] font-mono text-[var(--text-tertiary)] w-28 text-right">
 {fmtNum(data.dieser_monat.ist, 1, locale)}/{fmtNum(data.dieser_monat.soll, 1, locale)}h
 </span>
 </div>
 </div>

 {/* ── Dieses Jahr (Progress IST/SOLL %) ── */}
 <div className="mb-4">
 <div className="flex items-center justify-between mb-1">
 <span className="text-xs text-[var(--text-secondary)] font-medium">{t('employees.detail.this_year')}</span>
 <span className={`text-xs font-mono font-bold ${diffColor(data.dieses_jahr.diff)}`}>
 {data.dieses_jahr.soll > 0 ? Math.round((data.dieses_jahr.ist / data.dieses_jahr.soll) * 100) : 0}%
 </span>
 </div>
 <div className="flex items-center gap-2">
 <div className="flex-1 h-2 bg-surface-inset rounded-full overflow-hidden relative">
 <div className={`h-full rounded-full transition-all ${diffBg(data.dieses_jahr.diff)}`}
 style={{ width: `${data.dieses_jahr.soll > 0 ? Math.min(120, (data.dieses_jahr.ist / data.dieses_jahr.soll) * 100) : 0}%` }} />
 {/* 100% Marker */}
 {data.dieses_jahr.soll > 0 && (data.dieses_jahr.ist / data.dieses_jahr.soll) * 100 > 100 && (
 <div className="absolute top-0 bottom-0 w-[1px] bg-[var(--text-primary)] opacity-40" style={{ left: 'calc(100% * 1.0 / 1.2)' }} />
 )}
 </div>
 <span className="text-[10px] font-mono text-[var(--text-tertiary)] w-28 text-right">
 {fmtNum(data.dieses_jahr.ist, 1, locale)}/{fmtNum(data.dieses_jahr.soll, 1, locale)}h
 </span>
 </div>
 </div>

 {/* ── Wochenverlauf (Recharts BarChart, klickbar) ── */}
 {data.wochen_trend?.length >= 2 && (
 <div className="pt-3 border-t border-line">
 <div className="flex items-center justify-between mb-2">
 <div className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider">{t('employees.detail.weekly_trend_12')}</div>
 <div className="text-[9px] text-[var(--text-tertiary)]">{t('employees.detail.overtime_click_hint')}</div>
 </div>
 <div className="h-32 -mx-2">
 <ResponsiveContainer width="100%" height="100%">
 <BarChart data={data.wochen_trend} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
 <XAxis
 dataKey="kw_nummer"
 tick={{ fontSize: 9, fill: 'var(--text-tertiary)' }}
 tickFormatter={(v) => `${v}`}
 axisLine={{ stroke: '#E5E7EB' }}
 tickLine={false}
 />
 <YAxis
 tick={{ fontSize: 9, fill: 'var(--text-tertiary)' }}
 tickFormatter={(v) => `${v >= 0 ? '+' : ''}${v}h`}
 axisLine={{ stroke: '#E5E7EB' }}
 tickLine={false}
 width={38}
 />
 <ReTooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(51,142,220,0.08)' }} />
 <ReferenceLine y={0} stroke="#E5E7EB" strokeWidth={1} />
 <Bar dataKey="diff" radius={[3, 3, 0, 0]} cursor="pointer" onClick={handleBarClick}>
 {data.wochen_trend.map((w, i) => (
 <Cell
 key={i}
 fill={barColor(w.diff, w.laufend)}
 fillOpacity={w.laufend ? 0.5 : 1}
 stroke={w.laufend ? '#9CA3AF' : 'none'}
 strokeDasharray={w.laufend ? '3 2' : '0'}
 strokeWidth={w.laufend ? 1.5 : 0}
 />
 ))}
 </Bar>
 </BarChart>
 </ResponsiveContainer>
 </div>
 </div>
 )}

 {/* Link zu Zeitkonto */}
 <div className="mt-3 pt-3 border-t border-line flex justify-end">
 <Link to={`/zeitkonto?ma=${id}`} className="text-xs text-[#338edc] hover:underline flex items-center gap-1">
 {t('employees.detail.overtime_open_zeitkonto')} →
 </Link>
 </div>
 </>
 )}
 </Card>
 );
}

// ═══ NEUE KARTE: Urlaub & Abwesenheiten ═══
function VacationCard({ id }) {
 const { t, locale } = useLanguage();
 const [data, setData] = useState(null);
 const [loading, setLoading] = useState(true);
 const [recalcing, setRecalcing] = useState(false);
 const [monatsSaldi, setMonatsSaldi] = useState([]);
 const [pdfStart, setPdfStart] = useState('');
 const [pdfLoading, setPdfLoading] = useState(false);
 const [ueberplanung, setUeberplanung] = useState(null); // #1748
 const year = new Date().getFullYear();

 const loadData = () => {
 api(`/api/mitarbeiter/${id}/urlaub?jahr=${year}`)
 .then(d => { if (!d.error) setData(d); })
 .finally(() => setLoading(false));
 api(`/api/mitarbeiter/${id}/urlaub/saldo-monate?jahr=${year}`)
 .then(d => { if (d && d.success) setMonatsSaldi(d.data || []); })
 .catch(() => {});
 // #1748: Überplanungs-Payload aus SSoT-Endpoint (mitarbeiter.js-Endpoint bleibt unangetastet)
 api(`/api/urlaub/konto/${id}`)
 .then(d => { setUeberplanung(d?.aktuell?.ueberplanung || null); })
 .catch(() => {});
 };

 useEffect(() => { loadData(); }, [id]);

 const handleRecalc = async () => {
 setRecalcing(true);
 try {
 await api(`/api/urlaub/konto/${id}/recalc`, { method: 'POST' });
 loadData();
 } finally { setRecalcing(false); }
 };

 // #1710 — Urlaubsaufbau als PDF
 const handleUrlaubsaufbauPdf = async () => {
 setPdfLoading(true);
 try {
 const url = `/api/mitarbeiter/${id}/urlaubsaufbau-pdf${pdfStart ? `?start=${pdfStart}` : ''}`;
 const res = await fetch(url, { credentials: 'include' });
 if (!res.ok) throw new Error('PDF-Fehler');
 const blob = await res.blob();
 const cd = res.headers.get('Content-Disposition') || '';
 const match = cd.match(/filename="([^"]+)"/);
 const a = document.createElement('a');
 a.href = URL.createObjectURL(blob);
 a.download = match ? match[1] : `urlaubsaufbau-${id}.pdf`;
 document.body.appendChild(a);
 a.click();
 a.remove();
 URL.revokeObjectURL(a.href);
 } catch (e) {
 alert(t('urlaub.aufbauPdf.error', 'PDF konnte nicht erstellt werden'));
 } finally { setPdfLoading(false); }
 };

 return (
 <Card title={t('employees.detail.vacation_title', { year })} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 2l4 4-4 4"/><path d="M3 11V9a4 4 0 014-4h14"/><path d="M7 22l-4-4 4-4"/><path d="M21 13v2a4 4 0 01-4 4H3"/></svg>}>
 {loading ? (
 <div className="text-xs text-[var(--text-tertiary)] py-4 text-center">{t('employees.detail.loading_vacation')}</div>
 ) : !data || data.error ? (
 <div className="text-xs text-[var(--text-tertiary)] py-4 text-center">{t('employees.detail.no_vacation_data')}</div>
 ) : (
 <>
 {/* Main vacation display (v2.348.0: Zwei-Saldo-Logik) */}
 {data.anspruch > 0 && (() => {
 const aktuell = Number(data.aktuell_verfuegbar ?? data.rest ?? 0);
 // #1698: UJ-Prognose statt JE-Kachel; Genommen/Geplant im UJ-Fenster
 const prognoseUJ = data.prognose_urlaubsjahr;
 const hasPrognoseUJ = prognoseUJ !== null && prognoseUJ !== undefined;
 const projiziert = Number(prognoseUJ ?? data.projiziert_jahresende ?? data.rest ?? 0);
 const genommen = Number(data.genommen_uj ?? data.genommen ?? data.verbraucht ?? 0);
 const geplant = Number(data.geplant_uj ?? data.geplant ?? 0);
 const basis = Number(data.anspruch_pro_jahr || data.anspruch);
 const colorAkt = aktuell < 0 ? 'text-red-600' : aktuell < 3 ? 'text-amber-600' : 'text-emerald-600';
 const colorProj = projiziert < 0 ? 'text-red-600' : 'text-emerald-600';
 const rest = Math.max(0, basis - genommen - geplant);
 const pct = (v) => basis > 0 ? Math.max(0, Math.min(100, (v / basis) * 100)) : 0;
 return (
 <div className="mb-4">
 <div className="flex items-center justify-center gap-2 mb-3">
 <CalendarDays size={16} className="text-[#8B5CF6]" />
 <span className="text-[10px] uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('urlaub.saldo.title', 'Urlaubssaldo')}</span>
 </div>
 <div className="grid grid-cols-2 gap-2 mb-3">
 <div className="rounded-lg border border-[var(--border-default,#E5E7EB)] px-3 py-2.5 text-center bg-[var(--bg-card,#fff)]">
 <p className="text-[10px] uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('urlaub.saldo.standHeute', 'Stand heute')}</p>
 <p className={`text-xl font-bold ${colorAkt}`}>{fmtNum(aktuell, 2, locale)}</p>
 <p className="text-[10px] text-[var(--text-muted)]">{t('employees.detail.days_unit', 'Tage')}</p>
 </div>
 <div className="rounded-lg border border-[var(--border-default,#E5E7EB)] px-3 py-2.5 text-center bg-[var(--bg-card,#fff)]">
 <p className="text-[10px] uppercase tracking-wider font-medium text-[var(--text-secondary)]">{hasPrognoseUJ ? t('urlaub.saldo.prognoseUJ', 'Prognose Ende Urlaubsjahr') : t('urlaub.saldo.prognoseJE', 'Prognose JE')}</p>
 <p className={`text-xl font-bold ${colorProj}`}>{fmtNum(projiziert, 2, locale)}</p>
 <p className="text-[10px] text-[var(--text-muted)]">{t('employees.detail.days_unit', 'Tage')}</p>
 </div>
 </div>
 {/* #1748: Überplanungs-Badge — geplant übersteigt Saldo + zulässigen Vorgriff */}
 {ueberplanung && ueberplanung.ueberzogen_um > 0 && (
 <div className="flex justify-center mb-3">
 <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold"
 style={ueberplanung.severity === 'fehler'
 ? { backgroundColor: '#FEE2E2', color: '#DC2626' }
 : { backgroundColor: '#FEF3C7', color: '#D97706' }}>
 <AlertTriangle size={12} />
 {t('urlaub.saldo.ueberplantBadge', {
 tage: fmtNum(ueberplanung.ueberzogen_um, 1, locale),
 vorgriff: fmtNum(ueberplanung.eff_vorgriff, 1, locale),
 defaultValue: 'Überplant −{{tage}} Tage (Vorgriff {{vorgriff}} ausgeschöpft)',
 })}
 </span>
 </div>
 )}
 {/* Progress bar: Genommen | Geplant | Rest */}
 <div className="h-3 bg-surface-inset rounded-full overflow-hidden flex border border-line">
 {genommen > 0 && <div className="h-full bg-red-400" style={{ width: `${pct(genommen)}%` }} title={`${t('urlaub.saldo.genommen', 'Genommen')}: ${fmtNum(genommen, 2, locale)}`} />}
 {geplant > 0 && <div className="h-full bg-amber-400" style={{ width: `${pct(geplant)}%` }} title={`${t('urlaub.saldo.geplant', 'Geplant')}: ${fmtNum(geplant, 2, locale)}`} />}
 {rest > 0 && <div className="h-full bg-emerald-400" style={{ width: `${pct(rest)}%` }} title={`${t('urlaub.saldo.puffer', 'Puffer')}: ${fmtNum(rest, 2, locale)}`} />}
 </div>
 <div className="flex items-center gap-3 mt-1.5 text-[9px] text-[var(--text-tertiary)]">
 {genommen > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-400" /> {t('urlaub.saldo.genommen', 'Genommen')}: {fmtNum(genommen, 1, locale)}</span>}
 {geplant > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-400" /> {t('urlaub.saldo.geplant', 'Geplant')}: {fmtNum(geplant, 1, locale)}</span>}
 {rest > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-400" /> {t('urlaub.saldo.puffer', 'Puffer')}: {fmtNum(rest, 1, locale)}</span>}
 </div>
 </div>
 );
 })()}

 {/* Breakdown section */}
 <div className="space-y-1.5 mb-3">
 <div className="flex justify-between text-xs py-1 border-b border-line/30">
 <span className="text-[var(--text-secondary)]">{t('employees.detail.vacation_anspruch_jahr')}</span>
 <span className="font-mono text-[var(--text-primary)]">{fmtNum(data.anspruch_pro_jahr || data.anspruch, 1, locale)} {t('employees.detail.days_unit', 'Tage')}</span>
 </div>
 {data.startsaldo != null && (
 <div className="flex justify-between text-xs py-1 border-b border-line/30">
 <span className="text-[var(--text-secondary)]">{t('employees.detail.vacation_startsaldo_per', { date: fmtDate(data.startsaldo_datum, locale) })}</span>
 <span className="font-mono text-[var(--text-primary)]">{fmtNum(Number(data.startsaldo), 1, locale)} {t('employees.detail.days_unit', 'Tage')}</span>
 </div>
 )}
 {data.aufgebaut > 0 && (
 <div className="flex justify-between text-xs py-1 border-b border-line/30">
 <span className="text-[var(--text-secondary)]">{t('employees.detail.vacation_aufgebaut_seit', { date: data.startsaldo_datum ? fmtDate(data.startsaldo_datum, locale) : `01.01.${year}` })}</span>
 <span className="font-mono text-green-600">+{fmtNum(Number(data.aufgebaut), 2, locale)} {t('employees.detail.days_unit', 'Tage')}</span>
 </div>
 )}
 {Number(data.genommen ?? data.verbraucht ?? 0) > 0 && (
 <div className="flex justify-between text-xs py-1 border-b border-line/30">
 <span className="text-[var(--text-secondary)]">{t('urlaub.saldo.genommen', 'Genommen')}{data.laufend_anzahl > 0 ? <span className="ml-1 text-blue-600">({t('urlaub.saldo.laufend', 'laufend')})</span> : null}</span>
 <span className="font-mono text-red-500">-{fmtNum(Number(data.genommen ?? data.verbraucht), 2, locale)} {t('employees.detail.days_unit', 'Tage')}</span>
 </div>
 )}
 {Number(data.geplant || 0) > 0 && (
 <div className="flex justify-between text-xs py-1 border-b border-line/30">
 <span className="text-[var(--text-secondary)]">{t('urlaub.saldo.geplant', 'Geplant')}{data.geplant_anzahl > 0 ? ` (${data.geplant_anzahl})` : ''}</span>
 <span className="font-mono text-amber-600">-{fmtNum(Number(data.geplant), 2, locale)} {t('employees.detail.days_unit', 'Tage')}</span>
 </div>
 )}
 </div>

 {/* Next accrual */}
 {data.naechster_aufbau_datum && (
 <div className="px-3 py-2 bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)] rounded-lg mb-3">
 <div className="flex justify-between text-xs">
 <span className="text-[var(--text-secondary)]">{t('employees.detail.next_accrual', 'Nächster Aufbau')}</span>
 <span className="font-mono text-[var(--text-primary)]">{fmtDate(data.naechster_aufbau_datum, locale)} (+{fmtNum(Number(data.naechster_aufbau_tage), 2, locale)})</span>
 </div>
 </div>
 )}

 {/* Next vacation */}
 {data.naechster_urlaub && (
 <div className="px-3 py-2 bg-primary-500/5 border border-primary-500/20 rounded-lg mb-3">
 <div className="text-[10px] text-primary-400 font-semibold mb-0.5">{t('employees.detail.next_vacation')}</div>
 <div className="text-xs text-content-secondary">
 {fmtDate(data.naechster_urlaub.von, locale)} – {fmtDate(data.naechster_urlaub.bis, locale)} ({t('employees.detail.days_count', { n: data.naechster_urlaub.tage })})
 </div>
 </div>
 )}

 {/* Extra info */}
 {(data.krankenstand > 0 || data.zeitausgleich > 0) && (
 <div className="space-y-1">
 {data.krankenstand > 0 && (
 <div className="flex justify-between text-xs">
 <span className="text-[var(--text-secondary)]">{t('employees.detail.sick_leave_year', { year })}</span>
 <span className="font-mono text-[var(--text-primary)]">{t('employees.detail.days_count', { n: data.krankenstand })}</span>
 </div>
 )}
 {data.zeitausgleich > 0 && (
 <div className="flex justify-between text-xs">
 <span className="text-[var(--text-secondary)]">{t('employees.detail.time_off_taken')}</span>
 <span className="font-mono text-[var(--text-primary)]">{t('employees.detail.days_count', { n: data.zeitausgleich })}</span>
 </div>
 )}
 </div>
 )}
 {/* Monats-Timeline (#889) */}
 {monatsSaldi.length > 0 && (() => {
 const maxAbs = Math.max(1, ...monatsSaldi.map(r => Math.abs(Number(r.saldo_tage))));
 return (
 <div className="mt-3 pt-3 border-t border-line/30">
 <div className="text-[10px] uppercase tracking-wider font-medium text-[var(--text-secondary)] mb-2">
 {t('urlaub.monatsTimeline.title', 'Monats-Aufbau')}
 </div>
 <div className="flex items-end gap-1 h-16">
 {monatsSaldi.map(row => {
 const saldo = Number(row.saldo_tage);
 const height = Math.max(2, (Math.abs(saldo) / maxAbs) * 52);
 const color = saldo < 0 ? 'bg-red-400' : saldo < 3 ? 'bg-amber-400' : 'bg-emerald-400';
 return (
 <div key={`${row.jahr}-${row.monat}`} className="flex-1 flex flex-col items-center justify-end" title={`${String(row.monat).padStart(2,'0')}/${row.jahr}: ${t('urlaub.monatsTimeline.aufgebaut', 'Aufgebaut')} ${fmtNum(Number(row.aufgebaut_kumulativ), 2, locale)} / ${t('urlaub.monatsTimeline.genommen', 'Genommen')} ${fmtNum(Number(row.genommen_kumulativ), 2, locale)} → ${t('urlaub.monatsTimeline.saldo', 'Saldo')} ${fmtNum(saldo, 2, locale)}`}>
 <div className={`w-full rounded-t ${color}`} style={{ height: `${height}px` }} />
 <div className="text-[8px] text-[var(--text-tertiary)] mt-0.5">{String(row.monat).padStart(2,'0')}</div>
 </div>
 );
 })}
 </div>
 <div className="text-[10px] text-[var(--text-tertiary)] mt-1 text-center">
 {t('urlaub.monatsTimeline.hint', 'Saldo je Monat (+ Aufbau, − Genommen, ± Rest-Vorjahr)')}
 </div>
 </div>
 );
 })()}

 {/* Recalc button + #1710 PDF-Export */}
 <div className="mt-3 pt-3 border-t border-line/30 flex flex-wrap items-center gap-2">
 <button onClick={handleRecalc} disabled={recalcing}
 className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium rounded-[10px] border border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] disabled:opacity-50">
 <RefreshCw size={12} className={recalcing ? 'animate-spin' : ''} />
 {recalcing ? t('urlaub.recalculating', 'Wird berechnet...') : t('urlaub.saldoNeuBerechnen', 'Saldo neu berechnen')}
 </button>
 <div className="flex items-center gap-1.5 ml-auto">
 <input type="date" value={pdfStart} onChange={e => setPdfStart(e.target.value)}
 title={t('urlaub.aufbauPdf.startTooltip', 'Startdatum der Tabelle (leer = Beginn des Urlaubsaufbaus)')}
 className="px-2 py-1.5 text-[10px] rounded-[10px] border border-[var(--border-default)] text-[var(--text-secondary)] bg-white" />
 <button onClick={handleUrlaubsaufbauPdf} disabled={pdfLoading}
 className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium rounded-[10px] border border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] disabled:opacity-50">
 <FileDown size={12} className={pdfLoading ? 'animate-pulse' : ''} />
 {pdfLoading ? t('urlaub.aufbauPdf.loading', 'PDF wird erstellt...') : t('urlaub.aufbauPdf.button', 'Urlaubsaufbau als PDF')}
 </button>
 </div>
 </div>
 </>
 )}
 </Card>
 );
}

// ═══ #926 — Dienstvertrag Edit-Modal ═══
function DvEditModal({ vertrag, onClose, onSaved, t, overrideGrund }) {
 const requiresGrund = vertrag.edit_state === 'active' || vertrag.edit_state === 'ended';
 const isOdooLocked = vertrag.edit_state === 'odoo_locked';
 const [form, setForm] = useState({
 bezeichnung: vertrag.bezeichnung || '',
 start_datum: vertrag.start_datum ? String(vertrag.start_datum).slice(0, 10) : '',
 end_datum: vertrag.end_datum ? String(vertrag.end_datum).slice(0, 10) : '',
 brutto_monatlich: vertrag.brutto_monatlich || '',
 stunden_pro_woche: vertrag.stunden_pro_woche || '',
 position: vertrag.position || '',
 vertrag_typ: vertrag.vertrag_typ || 'vollzeit',
 status: vertrag.status || 'Laufend',
 ist_aktiv: !!vertrag.ist_aktiv,
 ist_zusatzvereinbarung: !!vertrag.ist_zusatzvereinbarung,
 notiz: vertrag.notiz || '',
 });
 const [grund, setGrund] = useState('');
 const [saving, setSaving] = useState(false);
 const [error, setError] = useState('');

 const handleSubmit = async (e) => {
 e.preventDefault();
 setError('');
 if (requiresGrund && grund.trim().length < 3) { setError(t('vertrag.grundRequired', 'Begründung erforderlich (min. 3 Zeichen).')); return; }
 setSaving(true);
 try {
 const body = isOdooLocked ? { notiz: form.notiz, __grund: grund || null } : { ...form, __grund: grund || null };
 if (overrideGrund) body.__override_grund = overrideGrund;
 const res = await fetch(`/api/dienstvertraege/${vertrag.id}`, {
 method: 'PUT', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify(body),
 });
 if (!res.ok) { const d = await res.json().catch(() => ({})); setError(d.error || 'Fehler'); }
 else onSaved();
 } catch (err) { setError(err.message); }
 setSaving(false);
 };

 return (
 <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={onClose}>
 <form onSubmit={handleSubmit} className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
 <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
 <h2 className="text-base font-semibold text-[var(--text-primary)]">{t('vertrag.editTitle', 'Vertrag bearbeiten')}</h2>
 <button type="button" onClick={onClose} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
 <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" d="M18 6L6 18M6 6l12 12"/></svg>
 </button>
 </div>
 <div className="p-6 space-y-3">
 {isOdooLocked && (
 <div className="text-xs px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
 {t('vertrag.odooEditWarning', '⚠️ Odoo-Vertrag — nur Notiz-Feld editierbar (Sync-Konflikte vermeiden).')}
 </div>
 )}
 {!isOdooLocked && (
 <>
 <div>
 <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('vertrag.fieldBezeichnung', 'Bezeichnung')}</label>
 <input type="text" value={form.bezeichnung} onChange={e => setForm({...form, bezeichnung: e.target.value})}
 className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)]" />
 </div>
 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('vertrag.fieldStartDate', 'Startdatum')}</label>
 <input type="date" value={form.start_datum} onChange={e => setForm({...form, start_datum: e.target.value})}
 className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)]" />
 </div>
 <div>
 <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('vertrag.fieldEndDate', 'Enddatum')}</label>
 <input type="date" value={form.end_datum} onChange={e => setForm({...form, end_datum: e.target.value})}
 className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)]" />
 </div>
 <div>
 <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('vertrag.fieldBrutto', 'Brutto € / Monat')}</label>
 <input type="number" step="0.01" value={form.brutto_monatlich} onChange={e => setForm({...form, brutto_monatlich: e.target.value})}
 className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)]" />
 </div>
 <div>
 <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('vertrag.fieldHoursWeek', 'Stunden / Woche')}</label>
 <input type="number" step="0.5" value={form.stunden_pro_woche} onChange={e => setForm({...form, stunden_pro_woche: e.target.value})}
 className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)]" />
 </div>
 <div>
 <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('vertrag.fieldPosition', 'Position')}</label>
 <input type="text" value={form.position} onChange={e => setForm({...form, position: e.target.value})}
 className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)]" />
 </div>
 <div>
 <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('vertrag.fieldType', 'Vertragstyp')}</label>
 <select value={form.vertrag_typ} onChange={e => setForm({...form, vertrag_typ: e.target.value})}
 className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)]">
 <option value="vollzeit">Vollzeit</option>
 <option value="teilzeit">Teilzeit</option>
 <option value="geringfuegig">Geringfügig</option>
 <option value="praktikum">Praktikum</option>
 <option value="sonstig">Sonstig</option>
 </select>
 </div>
 </div>
 <div>
 <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('vertrag.fieldStatus', 'Status')}</label>
 <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
 className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)]">
 <option value="Entwurf">Entwurf</option>
 <option value="Laufend">Laufend</option>
 <option value="Abgeschlossen">Abgeschlossen</option>
 </select>
 </div>
 <label className="flex items-center gap-2 text-xs">
 <input type="checkbox" checked={form.ist_aktiv} onChange={e => setForm({...form, ist_aktiv: e.target.checked})} />
 <span className="text-[var(--text-secondary)]">{t('vertrag.fieldActive', 'Aktiver Vertrag (deaktiviert vorherige automatisch)')}</span>
 </label>
 <label className="flex items-center gap-2 text-xs">
 <input type="checkbox" checked={form.ist_zusatzvereinbarung} onChange={e => setForm({...form, ist_zusatzvereinbarung: e.target.checked})} />
 <span className="text-[var(--text-secondary)]">{t('vertrag.fieldZusatz', 'Zusatzvereinbarung / Änderung (Brutto, Stunden & Stelle nicht zwingend)')}</span>
 </label>
 </>
 )}
 <div>
 <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('vertrag.fieldNotiz', 'Notiz')}</label>
 <textarea rows={2} value={form.notiz} onChange={e => setForm({...form, notiz: e.target.value})}
 className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)]" />
 </div>
 {requiresGrund && (
 <div>
 <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
 {t('vertrag.fieldGrund', 'Begründung')} <span className="text-rose-500">*</span>
 </label>
 <textarea rows={2} value={grund} onChange={e => setGrund(e.target.value)} required
 placeholder={t('vertrag.grundPlaceholder', 'z.B. Korrektur Tippfehler, Brutto-Anpassung, …')}
 className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)]" />
 <p className="text-[10px] text-[var(--text-tertiary)] mt-1">{t('vertrag.grundHint', 'Wird im Audit-Trail gespeichert.')}</p>
 </div>
 )}
 {error && <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</div>}
 </div>
 <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[var(--border-card,#E5E7EB)]">
 <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs font-medium rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)]">
 {t('common.cancel', 'Abbrechen')}
 </button>
 <button type="submit" disabled={saving} className="px-3 py-1.5 text-xs font-medium rounded-lg bg-[#338edc] text-white hover:bg-[#2a7bc8] disabled:opacity-50">
 {saving ? t('common.saving', 'Speichern…') : t('common.save', 'Speichern')}
 </button>
 </div>
 </form>
 </div>
 );
}

// ═══ #926 — Dienstvertrag Delete Confirm ═══
function DvDeleteModal({ vertrag, busy, onClose, onConfirm, t }) {
 const isOdoo = vertrag.quelle === 'odoo';
 const [grund, setGrund] = useState('');
 return (
 <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={onClose}>
 <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
 <div className="px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
 <h2 className="text-base font-semibold text-[var(--text-primary)]">{t('vertrag.deleteTitle', 'Vertrag löschen?')}</h2>
 </div>
 <div className="p-6 space-y-3">
 <div className="text-sm text-[var(--text-secondary)]">
 {t('vertrag.deleteConfirm', 'Dieser Vertrag wird soft-gelöscht und ist später wiederherstellbar. Die PDF-Datei bleibt erhalten.')}
 </div>
 <div className="text-xs px-3 py-2 rounded-lg bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)]">
 <div className="font-semibold text-[var(--text-primary)]">{vertrag.bezeichnung || t('employees.detail.contract', 'Vertrag')}</div>
 <div className="text-[var(--text-tertiary)]">{vertrag.start_datum} {vertrag.end_datum ? `– ${vertrag.end_datum}` : ''}</div>
 </div>
 {isOdoo && (
 <div className="text-xs px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
 {t('vertrag.odooDeleteWarning', '⚠️ Bei nächstem Odoo-Sync wird dieser Vertrag möglicherweise wiederhergestellt. Bitte prüfen ob auch in Odoo zu löschen.')}
 </div>
 )}
 <div>
 <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
 {t('vertrag.fieldGrund', 'Begründung')} <span className="text-rose-500">*</span>
 </label>
 <textarea rows={2} value={grund} onChange={e => setGrund(e.target.value)} required
 placeholder={t('vertrag.deleteGrundPlaceholder', 'z.B. Doppelt erstellt, Tippfehler in Bezeichnung, …')}
 className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)]" />
 </div>
 </div>
 <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[var(--border-card,#E5E7EB)]">
 <button onClick={onClose} className="px-3 py-1.5 text-xs font-medium rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)]">
 {t('common.cancel', 'Abbrechen')}
 </button>
 <button onClick={() => onConfirm(grund)} disabled={busy || grund.trim().length < 3}
 className="px-3 py-1.5 text-xs font-medium rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50">
 {busy ? t('common.deleting', 'Lösche…') : t('vertrag.deleteAction', 'Löschen')}
 </button>
 </div>
 </div>
 </div>
 );
}

// ═══ #926 — Dienstvertrag Restore Confirm ═══
function DvRestoreModal({ vertrag, busy, onClose, onConfirm, t }) {
 const [grund, setGrund] = useState('');
 return (
 <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={onClose}>
 <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
 <div className="px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
 <h2 className="text-base font-semibold text-[var(--text-primary)]">{t('vertrag.restoreTitle', 'Vertrag wiederherstellen?')}</h2>
 </div>
 <div className="p-6 space-y-3">
 <div className="text-sm text-[var(--text-secondary)]">
 {t('vertrag.restoreConfirm', 'Der Vertrag wird wieder sichtbar. Aktiv-Status bleibt deaktiviert — bitte separat setzen.')}
 </div>
 <div>
 <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
 {t('vertrag.fieldGrund', 'Begründung')} <span className="text-rose-500">*</span>
 </label>
 <textarea rows={2} value={grund} onChange={e => setGrund(e.target.value)} required
 placeholder={t('vertrag.restoreGrundPlaceholder', 'z.B. Versehentlich gelöscht, …')}
 className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)]" />
 </div>
 </div>
 <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[var(--border-card,#E5E7EB)]">
 <button onClick={onClose} className="px-3 py-1.5 text-xs font-medium rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)]">
 {t('common.cancel', 'Abbrechen')}
 </button>
 <button onClick={() => onConfirm(grund)} disabled={busy || grund.trim().length < 3}
 className="px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
 {busy ? t('common.saving', 'Speichern…') : t('vertrag.restoreAction', 'Wiederherstellen')}
 </button>
 </div>
 </div>
 </div>
 );
}

// ═══ #926 — Dienstvertrag Audit-Trail Slideover ═══
function DvAuditSlideover({ vertrag, entries, loading, onClose, t, locale }) {
 const aktionLabel = (a) => ({
 created: t('vertrag.auditCreated', 'Erstellt'),
 updated: t('vertrag.auditUpdated', 'Bearbeitet'),
 deleted: t('vertrag.auditDeleted', 'Gelöscht'),
 restored: t('vertrag.auditRestored', 'Wiederhergestellt'),
 }[a] || a);
 const aktionColor = (a) => ({
 created: 'bg-emerald-500/15 text-emerald-600',
 updated: 'bg-[#338edc]/15 text-[#338edc]',
 deleted: 'bg-rose-500/15 text-rose-600',
 restored: 'bg-emerald-500/15 text-emerald-600',
 }[a] || 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary)]');
 return (
 <div className="fixed inset-0 bg-black/30 z-50 flex justify-end" onClick={onClose}>
 <div className="bg-[var(--bg-card,#fff)] border-l border-[var(--border-card,#E5E7EB)] shadow-xl w-full max-w-md h-full overflow-y-auto" onClick={e => e.stopPropagation()}>
 <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card,#E5E7EB)] sticky top-0 bg-[var(--bg-card,#fff)]">
 <div>
 <h2 className="text-base font-semibold text-[var(--text-primary)]">{t('vertrag.auditTitle', 'Audit-Verlauf')}</h2>
 <div className="text-xs text-[var(--text-tertiary)] mt-0.5">{vertrag.bezeichnung}</div>
 </div>
 <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
 <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" d="M18 6L6 18M6 6l12 12"/></svg>
 </button>
 </div>
 <div className="p-6">
 {loading ? (
 <div className="text-center text-xs text-[var(--text-tertiary)] py-8">{t('common.loading', 'Lädt …')}</div>
 ) : entries.length === 0 ? (
 <div className="text-center text-xs text-[var(--text-tertiary)] py-8">{t('vertrag.auditEmpty', 'Noch keine Einträge.')}</div>
 ) : (
 <ol className="space-y-3">
 {entries.map(e => (
 <li key={e.id} className="border border-[var(--border-default,#E5E7EB)] rounded-lg p-3">
 <div className="flex items-center justify-between gap-2 mb-1">
 <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${aktionColor(e.aktion)}`}>{aktionLabel(e.aktion)}</span>
 <span className="text-[10px] text-[var(--text-tertiary)] font-mono">{fmtDate(e.ausgefuehrt_at, locale)}</span>
 </div>
 <div className="text-xs text-[var(--text-secondary)]">
 {e.ausgefuehrt_von_name || t('common.system', 'System')}
 </div>
 {e.grund && (
 <div className="mt-1 text-xs text-[var(--text-secondary)] italic">„{e.grund}"</div>
 )}
 {(e.alter_wert || e.neuer_wert) && (
 <div className="mt-2 text-[10px] font-mono text-[var(--text-tertiary)] space-y-0.5">
 {Object.keys(e.neuer_wert || {}).map(k => (
 <div key={k}>
 <span className="font-semibold">{k}:</span>{' '}
 <span className="line-through opacity-60">{String(e.alter_wert?.[k] ?? '—')}</span>
 {' → '}
 <span className="text-[var(--text-primary)]">{String(e.neuer_wert?.[k] ?? '—')}</span>
 </div>
 ))}
 </div>
 )}
 </li>
 ))}
 </ol>
 )}
 </div>
 </div>
 </div>
 );
}

// ═══ KARTE: Dienstverträge (konsolidiert + digitale Verträge) ═══
function DienstvertraegeCard({ id, isAdmin, showCreateModal, setShowCreateModal, compact, onShowAll, austrittDatum }) {
 const { t, locale } = useLanguage();
 const dialog = useDialog();
 const [data, setData] = useState(null);
 const [digitalList, setDigitalList] = useState([]);
 const [templates, setTemplates] = useState([]);
 const [loading, setLoading] = useState(true);
 const [uploading, setUploading] = useState(false);

 // Create modal state
 const [selectedTemplate, setSelectedTemplate] = useState('');
 const [variablen, setVariablen] = useState({});
 const [loadingVars, setLoadingVars] = useState(false);
 const [generating, setGenerating] = useState(false);
 const [generatedResult, setGeneratedResult] = useState(null);
 const [sending, setSending] = useState(false);

 // #926 — Edit / Delete / Audit-Trail / Soft-Delete
 const [editingVertrag, setEditingVertrag] = useState(null);
 const [deletingVertrag, setDeletingVertrag] = useState(null);
 const [restoringVertrag, setRestoringVertrag] = useState(null);
 const [auditVertrag, setAuditVertrag] = useState(null);
 const [auditEntries, setAuditEntries] = useState([]);
 const [auditLoading, setAuditLoading] = useState(false);
 const [includeDeleted, setIncludeDeleted] = useState(false);
 const [actionBusy, setActionBusy] = useState(false);

 // #966 Bundle C — Override-Modal für ausgetretene MAs
 const [pendingEditVertrag, setPendingEditVertrag] = useState(null);
 const [pendingCreate, setPendingCreate] = useState(false);
 const [overrideGrund, setOverrideGrund] = useState('');

 const requestEdit = (v) => {
 if (austrittDatum) setPendingEditVertrag(v);
 else setEditingVertrag(v);
 };
 const requestCreate = () => {
 if (austrittDatum) setPendingCreate(true);
 else setShowCreateModal(true);
 };

 // #966 Bundle C — wenn Parent showCreateModal direkt setzt (z.B. Toolbar-Button)
 // und MA ausgetreten ist, vorgeschalteten Override-Modal aufrufen.
 useEffect(() => {
 if (showCreateModal && austrittDatum && !overrideGrund) {
 setShowCreateModal(false);
 setPendingCreate(true);
 }
 }, [showCreateModal, austrittDatum, overrideGrund, setShowCreateModal]);

 const loadData = useCallback(() => {
 Promise.all([
 api(`/api/dienstvertraege/ma/${id}?include_deleted=${includeDeleted ? 'true' : 'false'}`),
 fetch(`/api/vertraege?mitarbeiter_id=${id}`, { credentials: 'include' }).then(r => r.json()).catch(() => ({ vertraege: [] })),
 fetch(`/api/vertraege/templates`, { credentials: 'include' }).then(r => r.json()).catch(() => ({ templates: [] })),
 ]).then(([dv, digital, tmpl]) => {
 if (!dv.error) setData(dv);
 setDigitalList(digital.vertraege || []);
 setTemplates((tmpl.templates || []).filter(t => t.aktiv));
 }).finally(() => setLoading(false));
 }, [id, includeDeleted]);

 useEffect(() => { loadData(); }, [loadData]);

 // #926 — Audit-Trail laden wenn auditVertrag gesetzt
 useEffect(() => {
 if (!auditVertrag) { setAuditEntries([]); return; }
 setAuditLoading(true);
 fetch(`/api/dienstvertraege/${auditVertrag.id}/audit-log`, { credentials: 'include' })
 .then(r => r.json())
 .then(d => setAuditEntries(d.audit_log || []))
 .catch(() => setAuditEntries([]))
 .finally(() => setAuditLoading(false));
 }, [auditVertrag]);

 // #926 — Edit-Lock-Tooltip-Texte
 const lockTooltip = (state) => {
 if (state === 'signed') return t('vertrag.lockTooltipSigned', 'Unterschriebene Verträge können aus rechtlichen Gründen nicht editiert werden. Bei Korrekturbedarf: Vertrag beenden und neuen anlegen.');
 if (state === 'odoo_locked') return t('vertrag.lockTooltipOdoo', 'Odoo-Vertrag — nur Notiz-Feld editierbar (Sync-Konflikte vermeiden).');
 if (state === 'active') return t('vertrag.lockTooltipActive', 'Laufender Vertrag — nur Administratoren mit Begründung.');
 if (state === 'ended') return t('vertrag.lockTooltipEnded', 'Beendeter Vertrag — nur Administratoren mit Begründung.');
 return '';
 };

 const handlePdfUpload = async (dvId) => {
 const input = document.createElement('input');
 input.type = 'file';
 input.accept = 'application/pdf';
 input.onchange = async (e) => {
 const file = e.target.files[0];
 if (!file) return;
 setUploading(true);
 try {
 const fd = new FormData();
 fd.append('file', file);
 await fetch(`/api/dienstvertraege/${dvId}/pdf`, { method: 'POST', credentials: 'include', body: fd });
 loadData();
 } catch { /* */ }
 setUploading(false);
 };
 input.click();
 };

 // Load variables when template selected
 const handleTemplateChange = async (tmplId) => {
 setSelectedTemplate(tmplId);
 setGeneratedResult(null);
 if (!tmplId) { setVariablen({}); return; }
 setLoadingVars(true);
 try {
 const res = await fetch(`/api/vertraege/ma-variablen/${id}?template_id=${tmplId}`, { credentials: 'include' });
 const d = await res.json();
 setVariablen(d.werte || {});
 } catch { /* */ }
 setLoadingVars(false);
 };

 // Generate digital contract
 const handleGenerate = async () => {
 if (!selectedTemplate) return;
 setGenerating(true);
 try {
 const res = await fetch(`/api/vertraege/generieren`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ template_id: Number(selectedTemplate), mitarbeiter_id: Number(id), variablen_werte: variablen, __override_grund: overrideGrund || undefined }),
 });
 const d = await res.json();
 if (res.ok) {
 setGeneratedResult(d);
 loadData();
 } else { dialog.alert(t('common.error', 'Fehler'), d.error || t('common.error', 'Fehler')); }
 } catch (err) { dialog.alert(t('common.error', 'Fehler'), err.message); }
 setGenerating(false);
 };

 // Send contract
 const handleSend = async (vertragId, kanal) => {
 const ok = await dialog.confirm(t('vertrag.sendConfirm'));
 if (!ok) return;
 setSending(true);
 try {
 const res = await fetch(`/api/vertraege/${vertragId}/senden`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ kanal }),
 });
 if (res.ok) {
 loadData();
 if (generatedResult?.id === vertragId) { setShowCreateModal(false); setGeneratedResult(null); setSelectedTemplate(''); }
 } else {
 const d = await res.json().catch(() => ({}));
 dialog.alert(t('common.error', 'Fehler'), d.error || t('common.error', 'Fehler'));
 }
 } catch (err) { dialog.alert(t('common.error', 'Fehler'), err.message); }
 setSending(false);
 };

 const copyLink = (token) => {
 navigator.clipboard.writeText(`${window.location.origin}/vertrag/unterschreiben/${token}`);
 };

 const closeModal = () => {
 setShowCreateModal(false);
 setSelectedTemplate('');
 setVariablen({});
 setGeneratedResult(null);
 setOverrideGrund('');
 };

 const statusMap = {
 'Laufend': { label: t('employees.detail.status_running'), color: 'bg-emerald-500', badge: 'bg-emerald-500/15 text-emerald-400' },
 'Entwurf': { label: t('employees.detail.status_draft'), color: 'bg-[#338edc]', badge: 'bg-primary-500/15 text-primary-400' },
 'Abgeschlossen': { label: t('employees.detail.status_completed'), color: 'bg-[var(--text-tertiary,#6B7280)]', badge: 'bg-[rgba(107,114,128,0.15)] text-[var(--text-tertiary,#6B7280)]' },
 'Storniert': { label: t('employees.detail.status_cancelled'), color: 'bg-red-500', badge: 'bg-red-500/15 text-red-400' },
 };

 const digitalStatusMap = {
 entwurf: { label: t('vertrag.entwurf'), badge: 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary,#6B7280)] dark:bg-[rgba(55,65,81,0.3)] dark:text-[var(--text-muted,#9CA3AF)]' },
 gesendet: { label: t('employees.detail.dv_waiting_signature'), badge: 'bg-amber-100 text-amber-700 dark:bg-amber-700/30 dark:text-amber-400' },
 resend_pending: { label: t('vertrag.statusResendPending'), badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-700/30 dark:text-emerald-400' },
 unterschrieben: { label: t('vertrag.unterschrieben'), badge: 'bg-green-100 text-green-700 dark:bg-green-700/30 dark:text-green-400' },
 storniert: { label: t('vertrag.statusStorniert'), badge: 'bg-red-100 text-red-700 dark:bg-red-700/30 dark:text-red-400' },
 };
 const digitalStatusFallback = { badge: 'bg-amber-100 text-amber-800 dark:bg-amber-700/30 dark:text-amber-300' };

 const typBadge = (typ) => {
 const labels = { vollzeit: 'VZ', teilzeit: 'TZ', geringfuegig: 'GF', praktikum: 'PR', sonstig: '?' };
 return labels[typ] || typ || '';
 };

 const hasContracts = data?.vertraege?.length > 0;
 const hasDigital = digitalList.length > 0;

 // #975 Bundle C — vereinheitlichte „Alle Verträge"-Liste statt zwei getrennter Sektionen.
 // Normalisierung mv + dv → einheitliche Felder + Sort-Date desc.
 const today = new Date().toISOString().slice(0, 10);
 const fmtMoney = (v) => v == null ? '—' : `€${Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
 const normalize = (items) => {
   const out = [];
   for (const v of (items || [])) {
     out.push({
       _src: 'legacy',
       id: v.id,
       vertrag_nummer: v.vertrag_nummer,
       label: v.bezeichnung || t('employees.detail.contract'),
       vertrag_typ: v.vertrag_typ,
       start: v.start_datum, end: v.end_datum,
       laufend: v.ist_aktiv === true || v.status === 'Laufend' || (!v.end_datum && v.status !== 'Abgeschlossen'),
       stunden: v.stunden_pro_woche, lohn: v.brutto_monatlich, position: v.position || v.stelle,
       status: v.status, edit_state: v.edit_state, geloescht_am: v.geloescht_am,
       sort_date: v.start_datum || v.created_at || '',
       raw: v,
     });
   }
   for (const dv of (digitalList || [])) {
     const isSigned = dv.status === 'unterschrieben';
     const inRange = dv.gueltig_ab && dv.gueltig_ab <= today && (!dv.gueltig_bis || dv.gueltig_bis >= today);
     out.push({
       _src: 'digital',
       id: dv.id,
       vertrag_nummer: dv.vertrag_nummer,
       label: dv.template_name || t('vertrag.title', 'Vertrag'),
       vertrag_typ: dv.vertrag_typ,
       start: dv.gueltig_ab, end: dv.gueltig_bis,
       laufend: isSigned && inRange,
       stunden: dv.stunden_pro_woche, lohn: dv.monatslohn, position: null,
       status: dv.status, unterschrift_am: dv.unterschrift_am,
       sort_date: dv.gueltig_ab || dv.unterschrift_am || dv.created_at || '',
       raw: dv,
     });
   }
   out.sort((a, b) => String(b.sort_date).localeCompare(String(a.sort_date)));
   return out;
 };
 const allEntries = normalize(data?.vertraege);
 const displayCombined = compact ? allEntries.slice(0, 3) : allEntries;

 return (
 <>
 <Card title={t('employees.detail.contract_history')} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/></svg>}>
 {loading ? (
 <div className="text-xs text-[var(--text-tertiary)] py-4 text-center">{t('employees.detail.loading_contracts')}</div>
 ) : !hasContracts && !hasDigital ? (
 <div className="flex flex-col items-center py-4 gap-2">
 <div className="text-xs text-[var(--text-tertiary)] text-center">{t('employees.detail.no_contract_data')}</div>
 {isAdmin && !compact && templates.length > 0 && (
 <button onClick={requestCreate}
 className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-[#338edc] text-white hover:bg-[#2a7bc8] transition-colors">
 <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
 {t('employees.detail.dv_create_from_template')}
 </button>
 )}
 </div>
 ) : (
 <>
 {/* #975 Bundle C — Vereinheitlichte „Alle Verträge"-Timeline */}
 <div className="space-y-3">
 {displayCombined.map((e, i) => {
 const isDigital = e._src === 'digital';
 const isDeleted = !!e.geloescht_am;
 const sm = !isDigital ? (statusMap[e.status] || { color: 'bg-[var(--text-tertiary,#6B7280)]' }) : { color: e.laufend ? 'bg-emerald-500' : 'bg-[var(--text-tertiary,#6B7280)]' };
 const dotColor = e.laufend ? 'bg-emerald-500' : sm.color;
 const editState = e.edit_state || 'active';
 const canEdit = !isDigital && isAdmin && !isDeleted && editState !== 'signed';
 const canDelete = !isDigital && isAdmin && !isDeleted;
 const editTitle = !isDigital ? lockTooltip(editState) : '';
 const v = e.raw;
 const hasPdf = isDigital ? !!(v.unterschrieben_pdf || v.pdf_pfad) : !!v.pdf_pfad;
 const pdfHref = isDigital ? `/api/vertraege/${e.id}/pdf` : `/api/dienstvertraege/${e.id}/pdf`;
 const dvStatusInfo = isDigital ? (digitalStatusMap[e.status] || { ...digitalStatusFallback, label: e.status || '—' }) : null;
 return (
 <div key={`${e._src}-${e.id}`} data-vertrag-nummer={e.vertrag_nummer || ''}
   className={`relative pl-5 ${i < displayCombined.length - 1 ? 'pb-3 border-l border-line/40 ml-[3px]' : 'ml-[3px]'} ${isDeleted ? 'opacity-50' : ''}`}>
 <div className={`absolute left-0 top-0.5 w-[7px] h-[7px] rounded-full -translate-x-[3.5px] ${dotColor} ${e.laufend ? 'ring-2 ring-emerald-500/30' : ''}`} />
 <div className="text-xs">
 <div className="flex items-center gap-2 flex-wrap">
 {e.vertrag_nummer && (
   <span className="font-mono font-bold text-[11px] text-[#338edc] tracking-tight">{e.vertrag_nummer}</span>
 )}
 <span className={`font-semibold ${e.laufend ? 'text-content' : 'text-content-tertiary'} ${isDeleted ? 'line-through' : ''}`}>{e.label}</span>
 {e.vertrag_typ && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-purple-500/10 text-purple-400">{typBadge(e.vertrag_typ)}</span>}
 <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)] text-[var(--text-tertiary)]">
   {isDigital ? t('employees.detail.source_digital', 'Digital') : t('employees.detail.source_paper', 'Papier')}
 </span>
 <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${e.laufend ? 'bg-emerald-500/15 text-emerald-600' : 'bg-[rgba(107,114,128,0.15)] text-[var(--text-tertiary,#6B7280)]'}`}>
   {e.laufend ? t('vertrag.tagLaufend', 'Laufend') : t('vertrag.tagAbgeschlossen', 'Abgeschlossen')}
 </span>
 {isDigital && dvStatusInfo && e.status !== 'unterschrieben' && (
   <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${dvStatusInfo.badge}`}>{dvStatusInfo.label}</span>
 )}
 {isDeleted && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-rose-500/15 text-rose-500">{t('vertrag.statusDeleted', 'Gelöscht')}</span>}
 {editState === 'signed' && !isDigital && <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-amber-500/10 text-amber-600" title={editTitle}>🔒 {t('vertrag.editLocked', 'Edit gesperrt')}</span>}
 </div>
 <div className="text-[var(--text-tertiary)] mt-0.5 flex items-center gap-2 flex-wrap">
 <span>
 {e.start ? fmtDate(e.start, locale) : '—'}
 {e.end ? ` – ${fmtDate(e.end, locale)}` : ` – ${t('employees.detail.ongoing')}`}
 </span>
 {e.stunden != null && <span className="font-mono">{Number(e.stunden)}h/Wo</span>}
 <span className="font-mono">{fmtMoney(e.lohn)}</span>
 {e.position && <span>{e.position}</span>}
 </div>
 {isDigital && e.unterschrift_am && (
   <div className="mt-0.5 text-[10px] text-emerald-600">
     {t('employees.detail.dv_signed_on')} {fmtDate(e.unterschrift_am, locale)}
   </div>
 )}
 {!isDigital && isDeleted && v.geloescht_grund && (
 <div className="mt-1 text-[10px] text-rose-500/80 italic">
 {t('vertrag.geloeschtAm', 'Gelöscht am')} {fmtDate(v.geloescht_am, locale)}
 {v.geloescht_von_name ? ` (${v.geloescht_von_name})` : ''} — „{v.geloescht_grund}"
 </div>
 )}
 <div className="flex items-center gap-1.5 mt-1 flex-wrap">
 {hasPdf && (
 <a href={pdfHref} target="_blank" rel="noopener noreferrer"
 className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-primary-600/10 hover:bg-primary-600/20 text-primary-400 rounded text-[10px] font-medium transition-colors">
 <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6" /></svg>
 PDF
 </a>
 )}
 {!isDigital && isAdmin && !hasPdf && !isDeleted && (
 <button onClick={() => handlePdfUpload(e.id)} disabled={uploading}
 className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-dashed border-line-hover rounded text-[10px] text-[var(--text-secondary)] hover:text-primary-400 hover:border-primary-500/40 transition-colors">
 <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
 PDF
 </button>
 )}
 {canEdit && !compact && (
 <button onClick={() => requestEdit(v)} title={editTitle || t('vertrag.editAction', 'Bearbeiten')}
 className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-line-hover rounded text-[10px] text-[var(--text-secondary)] hover:text-[#338edc] hover:border-[#338edc]/40 transition-colors">
 <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
 {t('vertrag.editAction', 'Bearbeiten')}
 </button>
 )}
 {canDelete && !compact && (
 <button onClick={() => setDeletingVertrag(v)} title={t('vertrag.deleteAction', 'Löschen')}
 className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-line-hover rounded text-[10px] text-[var(--text-secondary)] hover:text-rose-500 hover:border-rose-500/40 transition-colors">
 <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a2 2 0 012-2h2a2 2 0 012 2v3" /></svg>
 {t('vertrag.deleteAction', 'Löschen')}
 </button>
 )}
 {!isDigital && isAdmin && isDeleted && !compact && (
 <button onClick={() => setRestoringVertrag(v)} title={t('vertrag.restoreAction', 'Wiederherstellen')}
 className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-emerald-500/40 text-emerald-600 rounded text-[10px] hover:bg-emerald-50 transition-colors">
 <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
 {t('vertrag.restoreAction', 'Wiederherstellen')}
 </button>
 )}
 {!isDigital && isAdmin && !compact && (
 <button onClick={() => setAuditVertrag(v)} title={t('vertrag.auditAction', 'Verlauf')}
 className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-line-hover rounded text-[10px] text-[var(--text-tertiary)] hover:text-[#338edc] hover:border-[#338edc]/40 transition-colors">
 <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2" /></svg>
 {t('vertrag.auditAction', 'Verlauf')}
 </button>
 )}
 {isDigital && e.status === 'entwurf' && isAdmin && (
 <>
 <button onClick={() => handleSend(e.id, 'sms')} disabled={sending}
 className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#338edc]/10 hover:bg-[#338edc]/20 text-[#338edc] rounded text-[10px] font-medium transition-colors">
 SMS
 </button>
 <button onClick={() => handleSend(e.id, 'email')} disabled={sending}
 className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#338edc]/10 hover:bg-[#338edc]/20 text-[#338edc] rounded text-[10px] font-medium transition-colors">
 E-Mail
 </button>
 </>
 )}
 {isDigital && e.status === 'gesendet' && isAdmin && (
 <>
 <button onClick={() => handleSend(e.id, v.gesendet_per || 'sms')} disabled={sending}
 className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-line-hover rounded text-[10px] text-[var(--text-secondary)] hover:text-[#338edc] transition-colors">
 {t('employees.detail.dv_resend')}
 </button>
 <button onClick={() => copyLink(v.unterschrift_token)}
 className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-line-hover rounded text-[10px] text-[var(--text-secondary)] hover:text-[#338edc] transition-colors">
 Link
 </button>
 </>
 )}
 </div>
 </div>
 </div>
 );
 })}
 </div>

 {data?.dabei_seit && (
 <div className="mt-3 pt-3 border-t border-line flex items-center justify-between">
 <span className="text-xs text-[var(--text-secondary)]">{t('employees.detail.since_label')}</span>
 <span className="text-xs font-semibold text-content-secondary">
 {fmtDate(data.dabei_seit, locale)} ({timeSince(data.dabei_seit, t)})
 </span>
 </div>
 )}

 {/* Compact: "Alle anzeigen →" link */}
 {compact && onShowAll && (hasContracts || hasDigital) && (
 <div className="mt-3 pt-3 border-t border-line text-center">
 <button onClick={onShowAll}
 className="text-xs font-medium text-[#338edc] hover:text-[#2b7bc4] transition-colors">
 {t('mitarbeiter.dokumente.alle_anzeigen', 'Alle anzeigen')} →
 </button>
 </div>
 )}

 {/* #926 — Gelöschte anzeigen Toggle */}
 {!compact && isAdmin && (
 <div className="mt-3 pt-3 border-t border-line flex items-center justify-between">
 <span className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">{t('vertrag.showDeleted', 'Gelöschte anzeigen')}</span>
 <label className="inline-flex items-center cursor-pointer">
 <input type="checkbox" className="sr-only peer" checked={includeDeleted} onChange={e => setIncludeDeleted(e.target.checked)} />
 <div className="relative w-9 h-5 bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)] rounded-full peer-checked:bg-[#338edc]/20 peer-checked:border-[#338edc]/40 transition-colors after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:rounded-full after:h-4 after:w-4 after:shadow-sm after:transition-transform peer-checked:after:translate-x-4"></div>
 </label>
 </div>
 )}

 {/* Create from template button */}
 {!compact && isAdmin && templates.length > 0 && (
 <div className="mt-3 pt-3 border-t border-line">
 <button onClick={requestCreate}
 className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-dashed border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:text-[#338edc] hover:border-[#338edc]/40 transition-colors">
 <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
 {t('employees.detail.dv_create_from_template')}
 </button>
 </div>
 )}
 </>
 )}
 </Card>

 {/* #926 — Edit Modal */}
 {editingVertrag && (
 <DvEditModal
 vertrag={editingVertrag}
 onClose={() => { setEditingVertrag(null); setOverrideGrund(''); }}
 onSaved={() => { setEditingVertrag(null); setOverrideGrund(''); loadData(); }}
 t={t}
 overrideGrund={overrideGrund}
 />
 )}

 {/* #966 Bundle C — Override-Reason Modal für ausgetretene MAs */}
 {(pendingEditVertrag || pendingCreate) && (
 <OverrideReasonModal
 open={true}
 title={t('vertrag.override.title', 'Vertrag für ausgetretenen Mitarbeiter bearbeiten')}
 description={t('vertrag.override.description', 'Dieser Mitarbeiter ist seit {{datum}} ausgetreten. Bitte begründe die nachträgliche Vertragsänderung.').replace('{{datum}}', fmtDate(austrittDatum, locale))}
 placeholder={t('vertrag.override.placeholder', 'z.B. Korrektur Brutto-Lohn rückwirkend, Tippfehler im Stundensatz, …')}
 confirmLabel={t('vertrag.override.confirm', 'Bestätigen und bearbeiten')}
 cancelLabel={t('vertrag.override.cancel', 'Abbrechen')}
 onCancel={() => { setPendingEditVertrag(null); setPendingCreate(false); }}
 onConfirm={(grund) => {
 setOverrideGrund(grund);
 if (pendingEditVertrag) {
 setEditingVertrag(pendingEditVertrag);
 setPendingEditVertrag(null);
 } else if (pendingCreate) {
 setShowCreateModal(true);
 setPendingCreate(false);
 }
 }}
 />
 )}

 {/* #926 — Delete Confirm */}
 {deletingVertrag && (
 <DvDeleteModal
 vertrag={deletingVertrag}
 busy={actionBusy}
 onClose={() => setDeletingVertrag(null)}
 onConfirm={async (grund) => {
 setActionBusy(true);
 try {
 const res = await fetch(`/api/dienstvertraege/${deletingVertrag.id}`, {
 method: 'DELETE', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ grund }),
 });
 if (!res.ok) { const d = await res.json().catch(() => ({})); dialog.alert(t('common.error', 'Fehler'), d.error || t('common.error', 'Fehler')); }
 else { setDeletingVertrag(null); loadData(); }
 } finally { setActionBusy(false); }
 }}
 t={t}
 />
 )}

 {/* #926 — Restore Confirm */}
 {restoringVertrag && (
 <DvRestoreModal
 vertrag={restoringVertrag}
 busy={actionBusy}
 onClose={() => setRestoringVertrag(null)}
 onConfirm={async (grund) => {
 setActionBusy(true);
 try {
 const res = await fetch(`/api/dienstvertraege/${restoringVertrag.id}/restore`, {
 method: 'POST', credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ grund }),
 });
 if (!res.ok) { const d = await res.json().catch(() => ({})); dialog.alert(t('common.error', 'Fehler'), d.error || t('common.error', 'Fehler')); }
 else { setRestoringVertrag(null); loadData(); }
 } finally { setActionBusy(false); }
 }}
 t={t}
 />
 )}

 {/* #926 — Audit-Trail Slideover */}
 {auditVertrag && (
 <DvAuditSlideover
 vertrag={auditVertrag}
 entries={auditEntries}
 loading={auditLoading}
 onClose={() => setAuditVertrag(null)}
 t={t}
 locale={locale}
 />
 )}

 {/* ═══ Modal: Vertrag aus Vorlage generieren ═══ */}
 {showCreateModal && (
 <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={closeModal}>
 <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
 {/* Header */}
 <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
 <h2 className="text-base font-semibold text-[var(--text-primary)]">{t('employees.detail.dv_generate_title')}</h2>
 <button onClick={closeModal} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
 <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" d="M18 6L6 18M6 6l12 12"/></svg>
 </button>
 </div>

 <div className="p-6 space-y-4">
 {!generatedResult ? (
 <>
 {/* Template selection */}
 <div>
 <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">{t('vertrag.template')}</label>
 <select value={selectedTemplate} onChange={e => handleTemplateChange(e.target.value)}
 className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)]">
 <option value="">{t('vertrag.selectTemplate')}</option>
 {templates.map(tmpl => <option key={tmpl.id} value={tmpl.id}>{tmpl.name} ({tmpl.typ})</option>)}
 </select>
 </div>

 {/* Auto-filled variables */}
 {selectedTemplate && !loadingVars && Object.keys(variablen).length > 0 && (
 <div>
 <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">{t('employees.detail.dv_variables')}</label>
 <div className="space-y-2">
 {Object.entries(variablen).map(([key, val]) => (
 <div key={key} className="flex items-center gap-2">
 <span className="text-xs text-[var(--text-tertiary)] w-28 flex-shrink-0 truncate">{key}</span>
 <input
 value={val}
 onChange={e => setVariablen(prev => ({ ...prev, [key]: e.target.value }))}
 className="flex-1 border border-[var(--border-default,#E5E7EB)] rounded-lg px-2 py-1.5 text-xs bg-[var(--bg-card,#fff)] text-[var(--text-primary)]"
 />
 </div>
 ))}
 </div>
 </div>
 )}

 {loadingVars && (
 <div className="flex items-center justify-center py-4">
 <div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" />
 </div>
 )}
 </>
 ) : (
 /* Generated — show result + send options */
 <div className="text-center space-y-4">
 <div className="w-14 h-14 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
 <svg className="w-7 h-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
 </div>
 <div>
 <p className="text-sm font-semibold text-[var(--text-primary)]">{t('employees.detail.dv_generated_success')}</p>
 <p className="text-xs text-[var(--text-tertiary)] mt-1">{t('employees.detail.dv_generated_desc')}</p>
 </div>
 <div className="flex flex-col gap-2">
 <a href={`/api/vertraege/${generatedResult.id}/pdf`} target="_blank" rel="noopener noreferrer"
 className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]">
 <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6" /></svg>
 {t('employees.detail.dv_preview_pdf')}
 </a>
 <button onClick={() => handleSend(generatedResult.id, 'sms')} disabled={sending}
 className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#338edc] hover:bg-[#2a7bc8] text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors">
 <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/></svg>
 {t('employees.detail.dv_send_sms')}
 </button>
 <button onClick={() => handleSend(generatedResult.id, 'email')} disabled={sending}
 className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-[var(--border-default,#E5E7EB)] rounded-lg text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] disabled:opacity-50 transition-colors">
 <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 7l-10 6L2 7"/></svg>
 {t('employees.detail.dv_send_email')}
 </button>
 <button onClick={closeModal}
 className="text-xs text-[var(--text-tertiary)] hover:text-[var(--text-primary)] mt-1">
 {t('employees.detail.dv_save_draft')}
 </button>
 </div>
 </div>
 )}
 </div>

 {/* Footer — only during template selection phase */}
 {!generatedResult && (
 <div className="flex justify-end gap-2 px-6 py-4 border-t border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)]">
 <button onClick={closeModal}
 className="px-4 py-2 rounded-[10px] border border-[var(--border-card,#E5E7EB)] text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover,#FAFAFE)]">
 {t('vertrag.abbrechen')}
 </button>
 <button onClick={handleGenerate} disabled={generating || !selectedTemplate}
 className="px-4 py-2 rounded-[10px] bg-[#338edc] hover:bg-[#2a7bc8] text-white text-sm font-semibold disabled:opacity-50 transition-colors">
 {generating ? '...' : t('vertrag.generieren')}
 </button>
 </div>
 )}
 </div>
 </div>
 )}
 {dialog.element}
 </>
 );
}

// ═══ KARTE: Wochenplan (Arbeitszeitmodell) — ALWAYS editable ═══
// #893 — Gleitzeit-Perioden-Card (laufende Periode + Historie)
function GleitzeitPeriodenCard({ id, gleitzeitAktiv }) {
 const { t } = useLanguage();
 const [data, setData] = useState(null);
 const [loading, setLoading] = useState(true);
 const [showHistory, setShowHistory] = useState(false);

 useEffect(() => {
 if (!gleitzeitAktiv) { setLoading(false); return; }
 fetch(`/api/gleitzeit/mitarbeiter/${id}/perioden`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : null)
 .then(d => { setData(d); setLoading(false); })
 .catch(() => setLoading(false));
 }, [id, gleitzeitAktiv]);

 if (!gleitzeitAktiv) return null;

 const fmtMin = (min) => {
 if (min == null) return '—';
 const n = parseInt(min, 10);
 const h = Math.floor(Math.abs(n) / 60);
 const m = Math.abs(n) % 60;
 return `${n < 0 ? '-' : ''}${h}h ${String(m).padStart(2, '0')}min`;
 };

 return (
 <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-xl p-5 lg:col-span-2">
 <div className="flex items-center justify-between mb-3">
 <h3 className="text-sm font-semibold text-[var(--text-primary)]">
 {t('gleitzeit.card_title', 'Gleitzeit-Periode')}
 </h3>
 {data?.config?.modell_typ && (
 <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary)]">
 {data.config.modell_typ}
 </span>
 )}
 </div>

 {loading ? (
 <div className="text-xs text-[var(--text-muted)]">{t('common.loading', 'Lädt…')}</div>
 ) : !data?.laufend ? (
 <div className="text-xs text-[var(--text-muted)]">
 {t('gleitzeit.no_active_period', 'Keine laufende Periode')}
 </div>
 ) : (
 <>
 <div className="grid grid-cols-3 gap-3 mb-3">
 <div>
 <div className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
 {t('gleitzeit.period_soll', 'SOLL')}
 </div>
 <div className="text-lg font-semibold text-[var(--text-primary)] font-mono">
 {fmtMin(data.laufend.soll_min)}
 </div>
 </div>
 <div>
 <div className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
 {t('gleitzeit.period_ist', 'IST')}
 </div>
 <div className="text-lg font-semibold text-[var(--text-primary)] font-mono">
 {fmtMin(data.laufend.ist_min)}
 </div>
 </div>
 <div>
 <div className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
 {t('gleitzeit.period_delta', 'Delta')}
 </div>
 <div className={`text-lg font-semibold font-mono ${data.laufend.delta_min > 0 ? 'text-emerald-600' : data.laufend.delta_min < 0 ? 'text-amber-600' : 'text-[var(--text-primary)]'}`}>
 {data.laufend.delta_min > 0 ? '+' : ''}{fmtMin(data.laufend.delta_min)}
 </div>
 </div>
 </div>
 <div className="text-[11px] text-[var(--text-muted)] border-t border-line/40 pt-2">
 {t('gleitzeit.period_range', 'Periode')}: {data.laufend.start} – {data.laufend.end}
 </div>
 </>
 )}

 {data?.perioden?.length > 0 && (
 <>
 <button
 className="mt-3 text-xs text-[var(--text-secondary)] hover:text-[#338edc] flex items-center gap-1"
 onClick={() => setShowHistory(!showHistory)}
 >
 {showHistory ? '▾' : '▸'} {t('gleitzeit.history', 'Historie')} ({data.perioden.length})
 </button>
 {showHistory && (
 <div className="mt-2 border-t border-line/40 pt-2 max-h-60 overflow-y-auto">
 <table className="w-full text-xs">
 <thead>
 <tr className="text-[var(--text-secondary)] text-[10px] uppercase tracking-wider">
 <th className="text-left py-1">{t('gleitzeit.period_range', 'Periode')}</th>
 <th className="text-right py-1">{t('gleitzeit.period_soll', 'SOLL')}</th>
 <th className="text-right py-1">{t('gleitzeit.period_ist', 'IST')}</th>
 <th className="text-right py-1">{t('gleitzeit.period_delta', 'Delta')}</th>
 <th className="text-right py-1">{t('gleitzeit.mehr', 'Mehr')}</th>
 <th className="text-right py-1">{t('gleitzeit.ueber', 'Über')}</th>
 <th className="text-center py-1">{t('gleitzeit.closed', 'Abg.')}</th>
 </tr>
 </thead>
 <tbody>
 {data.perioden.map(p => (
 <tr key={p.id} className="border-t border-line/30">
 <td className="py-1 text-[var(--text-primary)] whitespace-nowrap">
 {p.start_datum} – {p.end_datum}
 </td>
 <td className="py-1 text-right font-mono">{fmtMin(p.periode_soll_min)}</td>
 <td className="py-1 text-right font-mono">{fmtMin(p.periode_ist_min)}</td>
 <td className={`py-1 text-right font-mono ${p.delta_min > 0 ? 'text-emerald-600' : p.delta_min < 0 ? 'text-amber-600' : ''}`}>
 {p.delta_min > 0 ? '+' : ''}{fmtMin(p.delta_min)}
 </td>
 <td className="py-1 text-right font-mono">{p.mehrstunden_min > 0 ? fmtMin(p.mehrstunden_min) : '—'}</td>
 <td className="py-1 text-right font-mono">{p.ueberstunden_min > 0 ? fmtMin(p.ueberstunden_min) : '—'}</td>
 <td className="py-1 text-center">{p.abgeschlossen ? '✓' : '—'}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </>
 )}
 </div>
 );
}

function WorkScheduleCard({ id, modellTyp, onModellTypChange, className = '' }) {
 const { t, locale } = useLanguage();
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [saveSuccess, setSaveSuccess] = useState(false);
 const [showHistory, setShowHistory] = useState(false);
 const [history, setHistory] = useState([]);
 const [gueltigAb, setGueltigAb] = useState(new Date().toISOString().slice(0, 10));
 const [hasChanges, setHasChanges] = useState(false);
 const [initialRows, setInitialRows] = useState(null);
 // #876: Vertrags-Coverage fuer Warn-Badge
 const [vertragsCoverage, setVertragsCoverage] = useState([]);
 const TAGE_DE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
 const MODELL_TYPEN = [
 { value: 'vollzeit', label: t('workSchedule.type_fulltime') || 'Vollzeit' },
 { value: 'teilzeit', label: t('workSchedule.type_parttime') || 'Teilzeit' },
 { value: 'lehrling', label: t('workSchedule.type_apprentice') || 'Lehrling' },
 { value: 'geringfuegig', label: t('workSchedule.type_marginal') || 'Geringfügig' },
 { value: 'praktikum', label: t('workSchedule.type_internship') || 'Praktikum' },
 { value: 'sonstig', label: t('workSchedule.type_other') || 'Sonstiges' },
 ];

 // Dezimale Stunden ↔ "HH:MM" Konvertierung
 const decimalToTime = (dec) => {
 if (dec == null || dec === '') return '';
 const d = parseFloat(dec);
 if (isNaN(d)) return '';
 const h = Math.floor(d);
 const m = Math.round((d - h) * 60);
 return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
 };
 const timeToDecimal = (time) => {
 if (!time) return 0;
 const [h, m] = time.split(':').map(Number);
 return h + (m || 0) / 60;
 };
 // Dezimale Stunden → "Xh Ymin"
 const formatHM = (dec) => {
 if (dec == null || isNaN(dec)) return '0h 0min';
 const total = Math.round(parseFloat(dec) * 60);
 const h = Math.floor(total / 60);
 const m = total % 60;
 if (h === 0) return `${m}min`;
 if (m === 0) return `${h}h`;
 return `${h}h ${m}min`;
 };
 const formatMin = (min) => {
 const total = Math.max(0, parseInt(min, 10) || 0);
 const h = Math.floor(total / 60);
 const m = total % 60;
 if (h === 0) return `${m}min`;
 if (m === 0) return `${h}h`;
 return `${h}h ${m}min`;
 };

 // Leer-Row für Tage ohne Daten (ist_arbeitstag: false). NICHT verwechseln mit
 // `defaultRow` für die "Neue Phase"-Vorlage, die bewusst Mo-Fr als aktiv startet.
 // #878: auto_pause_von/bis (HH:MM string) pro Wochentag.
 const emptyRow = (d) => ({ wochentag: d, ist_arbeitstag: false, von_zeit: 6, bis_zeit: 14, soll_stunden: 0, mittagspause_min: 0, auto_pause_von: null, auto_pause_bis: null });
 const defaultRow = (d) => ({ wochentag: d, ist_arbeitstag: d < 5, von_zeit: 6, bis_zeit: 14, soll_stunden: d < 5 ? 7.5 : 0, mittagspause_min: d < 5 ? 30 : 0, auto_pause_von: null, auto_pause_bis: null });
 const [editRows, setEditRows] = useState(() => Array.from({ length: 7 }, (_, i) => emptyRow(i)));

 // v2.372.4 (#864): Race-Schutz — Fetch von MA A darf State von MA B nicht
 // überschreiben, wenn id während der Ladezeit wechselt.
 const loadSeqRef = useRef(0);

 const loadData = useCallback(() => {
 const mySeq = ++loadSeqRef.current;
 setLoading(true);
 api(`/api/mitarbeiter/${id}/arbeitszeiten`)
 .then(d => {
 if (mySeq !== loadSeqRef.current) return; // Newer fetch is in flight
 const rows = Array.from({ length: 7 }, (_, day) => {
 const existing = d?.schedule?.find(s => s.wochentag === day);
 if (existing && existing.slots.length > 0) {
 const von = existing.slots[0].von;
 const bis = existing.slots[existing.slots.length - 1].bis;
 const pauseMin = existing.mittagspause_min ?? existing.slots[0].mittagspause_min ?? 0;
 // v2.372.5 (#864): ist_arbeitstag aus Backend übernehmen (Fallback: true, falls alt).
 return {
 wochentag: day,
 ist_arbeitstag: existing.ist_arbeitstag !== undefined ? existing.ist_arbeitstag : true,
 von_zeit: von,
 bis_zeit: bis,
 soll_stunden: existing.soll_stunden,
 mittagspause_min: pauseMin,
 // #878: Auto-Pause-Fenster (HH:MM) oder null
 auto_pause_von: existing.auto_pause_von || null,
 auto_pause_bis: existing.auto_pause_bis || null,
 };
 }
 return emptyRow(day);
 });
 // v2.372.4 (#864): gueltigAb VOR editRows setzen, damit Form beim Render
 // sofort die aktive Phase zeigt und kein `heute`-Wert durchblitzt.
 if (d?.aktive_gueltig_ab) setGueltigAb(d.aktive_gueltig_ab);
 setEditRows(rows);
 setInitialRows(JSON.stringify(rows));
 setHasChanges(false);
 })
 .finally(() => {
 if (mySeq === loadSeqRef.current) setLoading(false);
 });
 }, [id]);

 useEffect(() => { loadData(); }, [loadData]);

 // #876: Vertrags-Coverage laden (Phasen ohne gueltigen Dienstvertrag)
 useEffect(() => {
 if (!id) return;
 api(`/api/mitarbeiter/${id}/vertrags-coverage`)
 .then(d => setVertragsCoverage(Array.isArray(d?.data) ? d.data : []))
 .catch(() => setVertragsCoverage([]));
 }, [id]);

 const loadHistory = () => {
 if (showHistory) { setShowHistory(false); return; }
 api(`/api/mitarbeiter/${id}/arbeitszeiten/historie`)
 .then(d => { if (d.data) setHistory(d.data); setShowHistory(true); });
 };

 // #975 Bundle D: Click auf Phase-Vertrag-Tag scrollt zur entsprechenden Card-Row + 2s-Pulse.
 const scrollToVertrag = useCallback((nummer) => {
 if (!nummer) return;
 const el = document.querySelector(`[data-vertrag-nummer="${CSS.escape(nummer)}"]`);
 if (!el) return;
 el.scrollIntoView({ behavior: 'smooth', block: 'center' });
 el.classList.add('vertrag-pulse-highlight');
 setTimeout(() => el.classList.remove('vertrag-pulse-highlight'), 2000);
 }, []);

 // Netto-SOLL = (Bis − Von) − Mittagspause (in Stunden)
 const calcNetto = (von, bis, pauseMin) => {
 if (von == null || bis == null || bis <= von) return 0;
 const brutto = bis - von;
 const nettoH = brutto - (parseInt(pauseMin, 10) || 0) / 60;
 return Math.max(0, Math.round(nettoH * 100) / 100);
 };

 // #878: HH:MM → Minuten-Dauer. Null-safe.
 const timeDiffMin = (vonHHMM, bisHHMM) => {
 if (!vonHHMM || !bisHHMM) return null;
 const [vh, vm] = vonHHMM.split(':').map(Number);
 const [bh, bm] = bisHHMM.split(':').map(Number);
 const diff = (bh * 60 + bm) - (vh * 60 + vm);
 return diff > 0 ? diff : null;
 };

 const updateRow = (idx, field, value) => {
 setEditRows(prev => {
 const next = [...prev];
 next[idx] = { ...next[idx], [field]: value };
 if (field === 'ist_arbeitstag' && !value) {
 next[idx].soll_stunden = 0;
 // #878: Auto-Pause bei Nicht-Arbeitstag loeschen.
 next[idx].auto_pause_von = null;
 next[idx].auto_pause_bis = null;
 } else if (field === 'von_zeit' || field === 'bis_zeit' || field === 'mittagspause_min' || field === 'auto_pause_von' || field === 'auto_pause_bis') {
 // #878: Wenn Auto-Pause-Fenster vollstaendig → mittagspause_min = bis-von synchronisieren.
 const autoVon = field === 'auto_pause_von' ? value : next[idx].auto_pause_von;
 const autoBis = field === 'auto_pause_bis' ? value : next[idx].auto_pause_bis;
 const autoDauer = timeDiffMin(autoVon, autoBis);
 if (autoDauer != null) {
 next[idx].mittagspause_min = autoDauer;
 }
 const von = field === 'von_zeit' ? value : next[idx].von_zeit;
 const bis = field === 'bis_zeit' ? value : next[idx].bis_zeit;
 const pause = autoDauer != null ? autoDauer : (field === 'mittagspause_min' ? value : next[idx].mittagspause_min);
 next[idx].soll_stunden = calcNetto(von, bis, pause);
 }
 // Check if changed vs initial
 const serialized = JSON.stringify(next);
 setHasChanges(serialized !== initialRows);
 return next;
 });
 };

 const weeklyNetto = useMemo(() => {
 return Math.round(editRows.reduce((s, r) => s + (r.ist_arbeitstag ? (r.soll_stunden || 0) : 0), 0) * 100) / 100;
 }, [editRows]);

 const weeklyBrutto = useMemo(() => {
 return Math.round(editRows.reduce((s, r) => {
 if (!r.ist_arbeitstag) return s;
 const von = r.von_zeit, bis = r.bis_zeit;
 if (von != null && bis != null && bis > von) return s + (bis - von);
 return s;
 }, 0) * 100) / 100;
 }, [editRows]);

 const weeklyPauseMin = useMemo(() => {
 return editRows.reduce((s, r) => s + (r.ist_arbeitstag ? (parseInt(r.mittagspause_min, 10) || 0) : 0), 0);
 }, [editRows]);

 const [saveError, setSaveError] = useState(null);
 const [phaseError, setPhaseError] = useState(null);
 const [confirmDelete, setConfirmDelete] = useState(null); // { gueltig_ab, gueltig_bis, stunden_pro_woche }
 const [showNewPhase, setShowNewPhase] = useState(false);
 const [confirmCascade, setConfirmCascade] = useState(false); // #966 Bundle C
 // #1021 BR-14: gueltig_ab/_bis einer Phase manuell anpassen (Edge-Case Manuel Wagner).
 const [dateEditPhase, setDateEditPhase] = useState(null); // { gueltig_ab, gueltig_bis }

 const reloadHistoryAndData = async () => {
 await loadData();
 const d = await api(`/api/mitarbeiter/${id}/arbeitszeiten/historie`);
 if (d.data) setHistory(d.data);
 };

 const handleDeletePhase = async () => {
 if (!confirmDelete) return;
 setPhaseError(null);
 try {
 const res = await fetch(`/api/mitarbeiter/${id}/arbeitszeiten/phase`, {
 method: 'DELETE',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ gueltig_ab: confirmDelete.gueltig_ab }),
 });
 const r = await res.json();
 if (!res.ok || !r.success) {
 setPhaseError(r.error || t('common.error', 'Fehler'));
 setConfirmDelete(null);
 return;
 }
 setConfirmDelete(null);
 await reloadHistoryAndData();
 } catch (err) {
 setPhaseError(err.message || t('common.networkError', 'Netzwerkfehler'));
 setConfirmDelete(null);
 }
 };

 // #1021 BR-14: Manuell gueltig_ab/_bis einer bestehenden Phase anpassen (admin-only).
 const handlePhaseDateEdit = async ({ aktuelle_gueltig_ab, neue_gueltig_ab, neue_gueltig_bis, grund }) => {
 setPhaseError(null);
 try {
 const res = await fetch(`/api/mitarbeiter/${id}/arbeitszeiten/phase`, {
 method: 'PATCH',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ aktuelle_gueltig_ab, neue_gueltig_ab, neue_gueltig_bis, grund }),
 });
 const r = await res.json();
 if (!res.ok || !r.success) {
 setPhaseError(r.error || t('common.error', 'Fehler'));
 return false;
 }
 setDateEditPhase(null);
 await reloadHistoryAndData();
 return true;
 } catch (err) {
 setPhaseError(err.message || t('common.networkError', 'Netzwerkfehler'));
 return false;
 }
 };

 const handleNewPhaseSave = async ({ gueltig_ab, tage }) => {
 setPhaseError(null);
 try {
 const res = await fetch(`/api/mitarbeiter/${id}/arbeitszeiten`, {
 method: 'PUT',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ gueltig_ab, tage }),
 });
 const r = await res.json();
 if (!res.ok || !r.success) {
 setPhaseError(r.error || t('common.error', 'Fehler'));
 return false;
 }
 setShowNewPhase(false);
 await reloadHistoryAndData();
 return true;
 } catch (err) {
 setPhaseError(err.message || t('common.networkError', 'Netzwerkfehler'));
 return false;
 }
 };

 // v2.372.4 (#864): Recompute-Feedback — zeigt nach Save, dass tages_arbeitszeit
 // + Zeitkonto neu berechnet wurden (Backend-Antwort `recalc`-Objekt).
 const [recalcInfo, setRecalcInfo] = useState(null);

 const handleSave = async () => {
 setSaving(true);
 setSaveError(null);
 setSaveSuccess(false);
 setRecalcInfo(null);
 try {
 const res = await fetch(`/api/mitarbeiter/${id}/arbeitszeiten`, {
 method: 'PUT',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ gueltig_ab: gueltigAb, tage: editRows }),
 });
 const result = await res.json();
 if (result.success) {
 setInitialRows(JSON.stringify(editRows));
 setHasChanges(false);
 setSaveSuccess(true);
 if (result.recalc) setRecalcInfo(result.recalc);
 setTimeout(() => setSaveSuccess(false), 4000);
 setTimeout(() => setRecalcInfo(null), 8000);
 // Reload editor so UI shows persisted state (brutto/netto/pausen-Aggregation).
 loadData();
 } else {
 setSaveError(result.error || t('common.error', 'Fehler beim Speichern'));
 }
 } catch (err) {
 setSaveError(err.message || t('common.error', 'Netzwerkfehler'));
 } finally {
 setSaving(false);
 }
 };

 return (
 <Card className={className} title={t('employees.detail.work_schedule')} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>}>
 <div className="flex items-center justify-between -mt-2 mb-2">
 <button onClick={loadHistory} className="text-[var(--text-tertiary)] hover:text-primary transition-colors text-[10px] flex items-center gap-1" title={t('workSchedule.history') || 'Historie'}>
 <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
 {t('workSchedule.history') || 'Historie'}
 </button>
 <div className="flex items-center gap-1.5">
 {vertragsCoverage.length > 0 && (
 <span
 className="text-[10px] px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-800 font-medium cursor-help"
 title={vertragsCoverage.map(p => {
 const tage = p.ungedeckte_tage;
 const range = `${p.phase_ab} – ${p.phase_bis || t('employees.konsistenz.open', 'offen')}`;
 return `${range} (${tage} ${t('employees.konsistenz.daysUncovered', 'Tage ungedeckt')})`;
 }).join('\n')}
 >
 {t('workSchedule.noContractBadge', 'Ohne Vertrag')} ({vertragsCoverage.reduce((s, p) => s + (p.ungedeckte_tage || 0), 0)} {t('employees.konsistenz.daysShort', 'T')})
 </span>
 )}
 {modellTyp && (
 <span className="text-[10px] px-2 py-0.5 rounded-full border border-line bg-[var(--bg-secondary)] text-[var(--text-secondary)] font-medium">
 {MODELL_TYPEN.find(m => m.value === modellTyp)?.label || modellTyp}
 </span>
 )}
 </div>
 </div>

 {loading ? (
 <div className="text-xs text-[var(--text-tertiary)] py-4 text-center">{t('employees.detail.loading_short')}</div>
 ) : (
 <>
 {/* Modell-Typ + Gültig ab */}
 <div className="grid grid-cols-2 gap-3 mb-3">
 <div>
 <label className="text-[10px] text-[var(--text-secondary)] mb-0.5 block">{t('workSchedule.model_type') || 'Modell-Typ'}</label>
 <select value={modellTyp || 'vollzeit'} onChange={e => onModellTypChange?.(e.target.value)}
 className="w-full text-xs bg-[var(--bg-secondary)] border border-line rounded px-2 py-1 text-content">
 {MODELL_TYPEN.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
 </select>
 </div>
 <div>
 <label className="text-[10px] text-[var(--text-secondary)] mb-0.5 block">{t('workSchedule.valid_from') || 'Gültig ab'}</label>
 <input type="date" value={gueltigAb} onChange={e => setGueltigAb(e.target.value)}
 className="w-full text-xs bg-[var(--bg-secondary)] border border-line rounded px-2 py-1 text-content font-mono" />
 </div>
 </div>

 <div className="table-scroll-x overflow-x-auto">
 <table className="w-full min-w-[520px] text-xs">
 <thead>
 <tr className="text-[var(--text-secondary)] whitespace-nowrap">
 <th className="text-left py-1 font-semibold w-10">{t('employees.detail.day_col')}</th>
 <th className="text-center py-1 font-semibold">{t('employees.detail.active_col') || 'Aktiv'}</th>
 <th className="text-center py-1 font-semibold">{t('employees.detail.from_col') || 'Von'}</th>
 <th className="text-center py-1 font-semibold">{t('employees.detail.to_col') || 'Bis'}</th>
 <th className="text-center py-1 font-semibold">{t('workSchedule.pause_col', 'Pause')}</th>
 <th className="text-center py-1 font-semibold" title={t('autoPause.column_tooltip', 'Fixes Pausen-Zeitfenster pro Wochentag — System stempelt automatisch')}>{t('autoPause.column', 'Auto-Pause')}</th>
 <th className="text-right py-1 pl-2 font-semibold">{t('workSchedule.netto_col', 'Netto')}</th>
 </tr>
 </thead>
 <tbody>
 {editRows.map((r, i) => {
 const autoPauseAktiv = !!(r.auto_pause_von && r.auto_pause_bis);
 return (
 <tr key={i} className={`border-t border-line/30 ${!r.ist_arbeitstag ? 'opacity-40' : ''}`}>
 <td className="py-1.5 font-bold text-content-secondary">{TAGE_DE[i]}</td>
 <td className="py-1.5 text-center">
 <input type="checkbox" checked={r.ist_arbeitstag} onChange={e => updateRow(i, 'ist_arbeitstag', e.target.checked)}
 className="rounded border-line accent-primary" />
 </td>
 <td className="py-1.5 text-center">
 <input type="time" step="900" value={decimalToTime(r.von_zeit)} disabled={!r.ist_arbeitstag}
 onChange={e => updateRow(i, 'von_zeit', timeToDecimal(e.target.value))}
 className="w-20 text-center text-xs font-mono bg-[var(--bg-secondary)] border border-line rounded px-1 py-0.5 disabled:opacity-30" />
 </td>
 <td className="py-1.5 text-center">
 <input type="time" step="900" value={decimalToTime(r.bis_zeit)} disabled={!r.ist_arbeitstag}
 onChange={e => updateRow(i, 'bis_zeit', timeToDecimal(e.target.value))}
 className="w-20 text-center text-xs font-mono bg-[var(--bg-secondary)] border border-line rounded px-1 py-0.5 disabled:opacity-30" />
 </td>
 <td className="py-1.5 text-center">
 <div className="inline-flex items-center gap-1">
 <input type="number" step="5" min="0" max="120" value={r.mittagspause_min ?? 0} disabled={!r.ist_arbeitstag || autoPauseAktiv}
 onFocus={e => e.target.select()}
 onChange={e => {
 const v = e.target.value.replace(/^0+(?=\d)/, '');
 updateRow(i, 'mittagspause_min', v === '' ? 0 : parseInt(v, 10) || 0);
 }}
 className="w-14 text-center text-xs font-mono bg-[var(--bg-secondary)] border border-line rounded px-1 py-0.5 disabled:opacity-30"
 title={autoPauseAktiv ? t('autoPause.synced_tooltip', 'Automatisch synchronisiert mit Auto-Pause (Bis − Von)') : t('workSchedule.pause_tooltip', 'Mittagspause in Minuten')} />
 <span className="text-[10px] text-[var(--text-tertiary)]">min</span>
 </div>
 </td>
 <td className="py-1.5 text-center">
 <div className="inline-flex items-center gap-1">
 <input type="time" step="900" value={r.auto_pause_von || ''} disabled={!r.ist_arbeitstag}
 onChange={e => updateRow(i, 'auto_pause_von', e.target.value || null)}
 placeholder="—"
 className="w-20 text-center text-xs font-mono bg-[var(--bg-secondary)] border border-line rounded px-1 py-0.5 disabled:opacity-30"
 title={t('autoPause.from_tooltip', 'Start des fixen Pausen-Fensters (HH:MM)')} />
 <span className="text-[10px] text-[var(--text-tertiary)]">–</span>
 <input type="time" step="900" value={r.auto_pause_bis || ''} disabled={!r.ist_arbeitstag}
 onChange={e => updateRow(i, 'auto_pause_bis', e.target.value || null)}
 placeholder="—"
 className="w-20 text-center text-xs font-mono bg-[var(--bg-secondary)] border border-line rounded px-1 py-0.5 disabled:opacity-30"
 title={t('autoPause.to_tooltip', 'Ende des fixen Pausen-Fensters (HH:MM)')} />
 </div>
 </td>
 <td className="py-1.5 pl-2 text-right font-mono font-bold text-content whitespace-nowrap">{r.ist_arbeitstag ? formatHM(r.soll_stunden) : '–'}</td>
 </tr>
 );
 })}
 </tbody>
 </table>
 </div>
 {editRows.some(r => r.ist_arbeitstag && r.auto_pause_von && r.auto_pause_bis) && (
 <div className="mt-2 px-3 py-2 text-[11px] text-blue-800 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
 <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mt-0.5 shrink-0"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
 <div className="flex-1">
 <div className="font-semibold">{t('autoPause.hint_title', 'Auto-Pause aktiv')}</div>
 <div className="text-[10px] text-blue-700 mt-0.5">{t('autoPause.hint_text', 'System stempelt die Mittagspause automatisch im hinterlegten Zeitfenster. Manueller Vorrang: Wenn der MA im Fenster selbst stempelt, wird die Auto-Pause übersprungen.')}</div>
 </div>
 </div>
 )}
 <div className="mt-3 pt-3 border-t border-line space-y-1">
 <div className="flex items-center justify-between text-[10px] text-[var(--text-tertiary)]">
 <span>{t('workSchedule.brutto', 'Brutto')} − {t('workSchedule.pause', 'Pausen')}</span>
 <span className="font-mono">{formatHM(weeklyBrutto)} − {formatMin(weeklyPauseMin)}</span>
 </div>
 <div className="flex items-center justify-between">
 <span className="text-xs text-[var(--text-secondary)]">{t('workSchedule.netto_weekly', 'Netto-Wochenstunden')}</span>
 <span className={`text-sm font-mono font-extrabold ${weeklyNetto >= 38.5 ? 'text-content' : 'text-[var(--text-secondary)]'}`}>
 {formatHM(weeklyNetto)}
 <span className="ml-1 text-[10px] font-normal text-[var(--text-tertiary)]">{weeklyNetto >= 38.5 ? 'VZ' : 'TZ'}</span>
 </span>
 </div>
 </div>

 {/* Save button — ALWAYS visible */}
 {saveError && (
 <div className="mt-2 px-3 py-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg">{saveError}</div>
 )}
 {saveSuccess && (
 <div className="mt-2 px-3 py-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg">{t('common.saved', 'Gespeichert!')}</div>
 )}
 {recalcInfo && (
 <div className="mt-2 px-3 py-2 text-[11px] text-blue-800 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
 <RefreshCw size={12} className="mt-0.5 shrink-0" />
 <div className="flex-1">
 <div className="font-semibold">
 {recalcInfo.recalculated
 ? t('workSchedule.recalc_done', 'Neuberechnung abgeschlossen')
 : t('workSchedule.recalc_skipped', 'Keine Neuberechnung nötig')}
 </div>
 {recalcInfo.recalculated && (
 <div className="text-[10px] text-blue-700 mt-0.5">
 {t('workSchedule.recalc_days', { defaultValue: '{{tage}} Tage aktualisiert · Zeitkonto ab {{ab}}', tage: recalcInfo.tage, ab: recalcInfo.zeitkonto_ab })}
 </div>
 )}
 {recalcInfo.geschuetzt_ab_angehoben && (
 <div className="text-[10px] text-amber-800 mt-1">
 {t('workSchedule.recalc_schutzgrenze', {
 defaultValue: 'Tage vor {{grenze}} wurden nicht angefasst (Phase beginnt {{phase}}) — ausgestellte Zeitnachweise bleiben unverändert.',
 grenze: recalcInfo.schutzgrenze,
 phase: recalcInfo.geschuetzt_ab_angehoben,
 })}
 </div>
 )}
 {!recalcInfo.recalculated && recalcInfo.reason === 'future_phase' && (
 <div className="text-[10px] text-blue-700 mt-0.5">{t('workSchedule.recalc_future', 'Phase liegt in der Zukunft — wird bei Erreichen berechnet.')}</div>
 )}
 {!recalcInfo.recalculated && recalcInfo.reason === 'error' && (
 <div className="text-[10px] text-red-700 mt-0.5">{t('workSchedule.recalc_error', 'Fehler bei Neuberechnung')}: {recalcInfo.error || '–'}</div>
 )}
 </div>
 </div>
 )}
 <button onClick={() => setConfirmCascade(true)} disabled={saving || !hasChanges}
 className={`w-full mt-3 px-4 py-2 text-xs font-medium rounded-lg transition-colors ${
 hasChanges
 ? 'bg-[#338edc] text-white hover:bg-[#2b7bc4]'
 : 'bg-[var(--bg-secondary)] text-[var(--text-tertiary)] border border-line cursor-default'
 } disabled:opacity-50`}>
 {saving ? '...' : hasChanges ? t('common.save') : t('workSchedule.no_changes', 'Keine Änderungen')}
 </button>
 </>
 )}

 {/* History View */}
 {showHistory && (
 <div className="mt-3 pt-3 border-t border-line">
 <div className="flex items-center justify-between mb-2">
 <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase">{t('workSchedule.history') || 'Historie'}</div>
 <button
 onClick={() => { setPhaseError(null); setShowNewPhase(true); }}
 className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-medium bg-[#338edc] text-white hover:bg-[#2b7bc4] transition-colors"
 title={t('workSchedule.new_phase_hint', 'Neue Phase anlegen (schließt vorherige automatisch)')}
 >
 <Plus size={12} />
 {t('workSchedule.new_phase', 'Neue Phase')}
 </button>
 </div>

 {phaseError && (
 <div className="mb-2 px-3 py-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
 <AlertTriangle size={14} className="mt-0.5 shrink-0" />
 <span className="flex-1">{phaseError}</span>
 <button onClick={() => setPhaseError(null)} className="text-red-400 hover:text-red-700"><X size={12} /></button>
 </div>
 )}

 {history.length === 0 ? (
 <div className="text-[11px] text-[var(--text-tertiary)] py-4 text-center border border-dashed border-line/50 rounded-lg">
 {t('workSchedule.no_history', 'Keine Arbeitszeit hinterlegt — bitte neue Phase anlegen.')}
 </div>
 ) : (
 <div className="space-y-2 max-h-80 overflow-y-auto">
 {history.map((h, i) => {
 const isCurrent = !h.gueltig_bis;
 const nextPhase = history[i - 1]; // history is DESC → index-1 is the newer neighbour
 // Gap/Overlap detection: reine YYYY-MM-DD String-Vergleiche (lexikografisch korrekt, TZ-immun).
 // Nahtlos: addDaysStr(myEnd, 1) === nextStart
 let gapWarn = null;
 if (h.gueltig_bis && nextPhase?.gueltig_ab) {
 const myEnd = h.gueltig_bis.slice(0, 10);
 const nextStart = nextPhase.gueltig_ab.slice(0, 10);
 if (myEnd >= nextStart) {
 gapWarn = t('workSchedule.overlap_warn', 'Überlappung mit Folge-Phase');
 } else if (addDaysStr(myEnd, 1) < nextStart) {
 gapWarn = t('workSchedule.gap_warn', 'Lücke zur Folge-Phase');
 }
 }

 const editPhase = () => {
 // Slots pro Tag aggregieren (Morning+Afternoon können als separate Rows kommen).
 const slotsByDay = {};
 (h.tage || []).forEach(x => {
 if (!x.ist_arbeitstag) return;
 const d = x.wochentag;
 if (!slotsByDay[d]) slotsByDay[d] = { slots: [], pauseMin: 0, soll: 0 };
 slotsByDay[d].slots.push({ von: parseFloat(x.stunden_von) || 0, bis: parseFloat(x.stunden_bis) || 0 });
 slotsByDay[d].pauseMin += parseInt(x.mittagspause_min, 10) || 0;
 slotsByDay[d].soll += parseFloat(x.soll_stunden) || 0;
 });
 const rows = Array.from({ length: 7 }, (_, day) => {
 const agg = slotsByDay[day];
 if (agg && agg.slots.length > 0) {
 agg.slots.sort((a, b) => a.von - b.von);
 return {
 wochentag: day,
 ist_arbeitstag: true,
 von_zeit: agg.slots[0].von,
 bis_zeit: agg.slots[agg.slots.length - 1].bis,
 soll_stunden: Math.round(agg.soll * 100) / 100,
 mittagspause_min: agg.pauseMin,
 };
 }
 return emptyRow(day);
 });
 setEditRows(rows);
 setGueltigAb(h.gueltig_ab?.slice(0, 10) || '');
 setHasChanges(true);
 };

 return (
 <div key={i} className={`text-xs px-3 py-2 rounded-lg border ${isCurrent ? 'bg-blue-50/40 border-blue-200' : 'bg-[var(--bg-secondary)] border-line/50'}`}>
 <div className="flex items-center justify-between mb-1.5 gap-2">
 <div className="flex items-center gap-2">
 <span className="font-mono text-[var(--text-secondary)]">
 {h.gueltig_ab?.slice(0, 10)} – {h.gueltig_bis ? h.gueltig_bis.slice(0, 10) : <span className="text-[#338edc] font-semibold">{t('workSchedule.current') || 'aktuell'}</span>}
 </span>
 <span className="font-mono font-bold text-content text-sm">{h.stunden_pro_woche}h<span className="text-[9px] text-[var(--text-tertiary)] ml-0.5">/Woche</span></span>
 </div>
 <div className="flex items-center gap-1.5">
 {h.vertrag_nummer ? (
 <button
 type="button"
 onClick={() => scrollToVertrag(h.vertrag_nummer)}
 className="text-[9px] font-mono px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors inline-flex items-center gap-1"
 title={t('workSchedule.vertragLinkTooltip', 'Zum verknüpften Vertrag in der Verträge-Card scrollen')}
 >
 <span>{h.vertrag_nummer}</span>
 {h.vertrag_quelle === 'dv' && <span className="text-[8px] opacity-70">D</span>}
 </button>
 ) : (
 <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200" title={t('workSchedule.noVertragTooltip', 'Diese Phase ist mit keinem Vertrag (mv-FK oder dv-Range) verknüpft')}>
 {t('workSchedule.noVertragTag', 'Kein Vertrag verknüpft')}
 </span>
 )}
 {h.manuell_gepflegt ? (
 <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200" title={t('workSchedule.manuell_tooltip', 'Manuell gepflegt — in der App gesetzt')}>
 {t('workSchedule.badge_manual', 'Manuell gepflegt')}
 </span>
 ) : (
 <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary,#4B5563)] border border-[var(--border-default,#E5E7EB)]" title={t('workSchedule.odoo_tooltip', 'Historischer Stand — ursprünglich aus Odoo synchronisiert (seit v2.372.0 nicht mehr aktiv)')}>
 {t('workSchedule.badge_odoo', 'Odoo-Sync (historisch)')}
 </span>
 )}
 </div>
 </div>

 {gapWarn && (
 <div className="mb-1.5 px-2 py-1 rounded text-[10px] bg-amber-50 text-amber-800 border border-amber-200 inline-flex items-center gap-1">
 <AlertTriangle size={10} />
 {gapWarn}
 </div>
 )}

 <div className="flex items-center justify-between gap-2">
 <div className="flex gap-1 flex-wrap">
 {Array.from({ length: 7 }, (_, day) => {
 const tag = (h.tage || []).find(x => x.wochentag === day);
 const active = tag?.ist_arbeitstag;
 const sollH = active ? parseFloat(tag.soll_stunden) || 0 : 0;
 return (
 <span
 key={day}
 className={`text-[9px] px-1.5 py-0.5 rounded font-mono ${active
 ? 'bg-[var(--bg-primary)] text-[var(--text-primary)] border border-line'
 : 'bg-[var(--bg-primary)] text-[var(--text-muted)] border border-line/40 opacity-50'}`}
 title={active ? `${TAGE_DE[day]}: ${sollH}h` : `${TAGE_DE[day]}: frei`}
 >
 <span className="font-bold">{TAGE_DE[day]}</span>
 {active && <span className="ml-0.5">{sollH}h</span>}
 {!active && <span className="ml-0.5">–</span>}
 </span>
 );
 })}
 </div>
 <div className="flex items-center gap-1 shrink-0">
 <button
 onClick={editPhase}
 className="inline-flex items-center gap-1 text-[10px] text-[var(--text-secondary)] hover:text-[#338edc] px-2 py-1 rounded border border-line/50 hover:border-[#338edc] transition-colors"
 title={t('workSchedule.edit_phase', 'Phase in Edit-Form laden')}
 >
 <Pencil size={10} />
 {t('common.edit', 'Bearbeiten')}
 </button>
 <button
 onClick={() => { setPhaseError(null); setDateEditPhase({ gueltig_ab: h.gueltig_ab?.slice(0, 10), gueltig_bis: h.gueltig_bis?.slice(0, 10) || '' }); }}
 className="inline-flex items-center gap-1 text-[10px] text-[var(--text-secondary)] hover:text-[#338edc] px-2 py-1 rounded border border-line/50 hover:border-[#338edc] transition-colors"
 title={t('workSchedule.edit_phase_dates', 'Datum anpassen (admin)')}
 >
 <CalendarDays size={10} />
 {t('workSchedule.edit_phase_dates_short', 'Datum')}
 </button>
 <button
 onClick={() => { setPhaseError(null); setConfirmDelete({ gueltig_ab: h.gueltig_ab?.slice(0, 10), gueltig_bis: h.gueltig_bis?.slice(0, 10), stunden_pro_woche: h.stunden_pro_woche }); }}
 className="inline-flex items-center gap-1 text-[10px] text-red-600 hover:text-white hover:bg-red-500 px-2 py-1 rounded border border-red-200 hover:border-red-500 transition-colors"
 title={t('workSchedule.delete_phase', 'Phase löschen')}
 >
 <Trash2 size={10} />
 {t('common.delete', 'Löschen')}
 </button>
 </div>
 </div>
 </div>
 );
 })}
 </div>
 )}
 </div>
 )}

 <ConfirmDialog
 open={!!confirmDelete}
 variant="danger"
 title={t('workSchedule.delete_title')}
 message={confirmDelete
 ? t('workSchedule.delete_message', {
 ab: confirmDelete.gueltig_ab,
 bis: confirmDelete.gueltig_bis || t('workSchedule.current'),
 h: confirmDelete.stunden_pro_woche,
 })
 : ''}
 confirmLabel={t('common.delete', 'Löschen')}
 cancelLabel={t('common.cancel', 'Abbrechen')}
 onConfirm={handleDeletePhase}
 onCancel={() => setConfirmDelete(null)}
 />

 <ConfirmDialog
 open={confirmCascade}
 title={t('workSchedule.cascade_title', 'Modell-Änderung speichern?')}
 message={t('workSchedule.cascade_message', 'Falls sich die Wochenstunden vom aktiven Vertrag unterscheiden, wird automatisch ein neuer Vertrag erzeugt und der alte zum Stichtag geschlossen.')}
 confirmLabel={t('workSchedule.cascade_confirm', 'Speichern')}
 cancelLabel={t('common.cancel', 'Abbrechen')}
 onConfirm={() => { setConfirmCascade(false); handleSave(); }}
 onCancel={() => setConfirmCascade(false)}
 />

 {showNewPhase && (
 <NewPhaseModal
 t={t}
 TAGE_DE={TAGE_DE}
 previousPhase={history[0]}
 onCancel={() => setShowNewPhase(false)}
 onSave={handleNewPhaseSave}
 />
 )}

 {dateEditPhase && (
 <PhaseDateEditModal
 t={t}
 phase={dateEditPhase}
 onCancel={() => setDateEditPhase(null)}
 onSave={handlePhaseDateEdit}
 />
 )}
 </Card>
 );
}

// #1021 BR-14 — Modal: gueltig_ab/_bis einer bestehenden Phase anpassen.
function PhaseDateEditModal({ t, phase, onCancel, onSave }) {
 const [neuAb, setNeuAb] = useState(phase.gueltig_ab || '');
 const [neuBis, setNeuBis] = useState(phase.gueltig_bis || '');
 const [grund, setGrund] = useState('');
 const [saving, setSaving] = useState(false);
 const [err, setErr] = useState(null);

 const grundOk = grund.trim().length >= 10 && grund.trim().length <= 1000;
 const dateOk = /^\d{4}-\d{2}-\d{2}$/.test(neuAb) && (!neuBis || /^\d{4}-\d{2}-\d{2}$/.test(neuBis));
 const changed = neuAb !== (phase.gueltig_ab || '') || neuBis !== (phase.gueltig_bis || '');

 const handleSubmit = async () => {
 if (!grundOk || !dateOk || !changed) return;
 setSaving(true);
 setErr(null);
 const ok = await onSave({
 aktuelle_gueltig_ab: phase.gueltig_ab,
 neue_gueltig_ab: neuAb !== phase.gueltig_ab ? neuAb : null,
 neue_gueltig_bis: neuBis !== (phase.gueltig_bis || '') ? (neuBis || null) : null,
 grund: grund.trim(),
 });
 if (!ok) setSaving(false);
 };

 return (
 <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={onCancel}>
 <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] shadow-xl max-w-md w-full" onClick={e => e.stopPropagation()}>
 <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
 <h3 className="text-base font-semibold text-[var(--text-primary,#1A1A2E)]">
 {t('workSchedule.edit_phase_dates', 'Phasen-Datum anpassen')}
 </h3>
 <button onClick={onCancel} className="text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-primary,#1A1A2E)]"><X size={18} /></button>
 </div>
 <div className="p-6 space-y-4">
 <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
 <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
 <span>{t('workSchedule.phaseDateEdit_warn', 'Diese Operation ändert das Gültigkeitsfenster der bestehenden Phase und triggert eine Neuberechnung der tages_arbeitszeit + Zeitkonto. Audit-Trail wird geschrieben.')}</span>
 </div>
 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className="block text-xs font-medium text-[var(--text-secondary,#4B5563)] mb-1">{t('workSchedule.gueltig_ab', 'Gültig ab')} *</label>
 <input type="date" value={neuAb} onChange={e => setNeuAb(e.target.value)}
 className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]" />
 </div>
 <div>
 <label className="block text-xs font-medium text-[var(--text-secondary,#4B5563)] mb-1">{t('workSchedule.gueltig_bis', 'Gültig bis')}</label>
 <input type="date" value={neuBis} onChange={e => setNeuBis(e.target.value)}
 className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]" />
 <div className="text-[10px] text-[var(--text-tertiary,#6B7280)] mt-0.5">{t('workSchedule.gueltig_bis_hint', 'Leer = aktuelle Phase')}</div>
 </div>
 </div>
 <div>
 <label className="block text-xs font-medium text-[var(--text-secondary,#4B5563)] mb-1">
 {t('workSchedule.phaseDateEdit_reason', 'Grund (Pflicht, mind. 10 Zeichen)')} *
 </label>
 <textarea value={grund} onChange={e => setGrund(e.target.value)} rows={3} maxLength={1000}
 placeholder={t('workSchedule.phaseDateEdit_reason_placeholder', 'z.B. Phase rückwirkend bis Eintrittsdatum verlängert (zuvor unbedeckte Periode)')}
 className="w-full border border-[var(--border-default,#E5E7EB)] rounded-lg px-3 py-2 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary,#1A1A2E)]" />
 <div className="text-[10px] text-[var(--text-tertiary,#6B7280)] mt-0.5">{grund.length} / 1000</div>
 </div>
 {err && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{err}</div>}
 </div>
 <div className="flex justify-end gap-2 px-6 py-4 border-t border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)]">
 <button onClick={onCancel} disabled={saving} className="px-4 py-2 rounded-[10px] border border-[var(--border-card,#E5E7EB)] text-sm font-medium text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-card-hover,#FAFAFE)]">
 {t('common.cancel', 'Abbrechen')}
 </button>
 <button onClick={handleSubmit} disabled={saving || !grundOk || !dateOk || !changed}
 className="px-4 py-2 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold disabled:opacity-50 transition-colors">
 {saving ? '...' : t('common.save', 'Speichern')}
 </button>
 </div>
 </div>
 </div>
 );
}

// ═══ Modal: Neue Phase anlegen ═══
function NewPhaseModal({ t, TAGE_DE, previousPhase, onCancel, onSave }) {
 const [gueltigAb, setGueltigAb] = useState(new Date().toISOString().slice(0, 10));
 const [copyFromPrev, setCopyFromPrev] = useState(!!previousPhase);
 const [saving, setSaving] = useState(false);
 const [localError, setLocalError] = useState(null);

 const buildDefaultRow = (d) => ({ wochentag: d, ist_arbeitstag: d < 5, von_zeit: 6, bis_zeit: 14, soll_stunden: d < 5 ? 7.5 : 0, mittagspause_min: d < 5 ? 30 : 0, auto_pause_von: null, auto_pause_bis: null });

 const buildRowsFromPrev = () => {
 if (!previousPhase) return Array.from({ length: 7 }, (_, i) => buildDefaultRow(i));
 return Array.from({ length: 7 }, (_, day) => {
 const tag = (previousPhase.tage || []).find(x => x.wochentag === day);
 if (tag && tag.ist_arbeitstag) {
 return {
 wochentag: day,
 ist_arbeitstag: true,
 von_zeit: parseFloat(tag.stunden_von) || 6,
 bis_zeit: parseFloat(tag.stunden_bis) || 14,
 soll_stunden: parseFloat(tag.soll_stunden) || 0,
 mittagspause_min: parseInt(tag.mittagspause_min, 10) || 0,
 auto_pause_von: tag.auto_pause_von || null,
 auto_pause_bis: tag.auto_pause_bis || null,
 };
 }
 return buildDefaultRow(day);
 });
 };

 const timeDiffMin = (vonHHMM, bisHHMM) => {
 if (!vonHHMM || !bisHHMM) return null;
 const [vh, vm] = vonHHMM.split(':').map(Number);
 const [bh, bm] = bisHHMM.split(':').map(Number);
 const diff = (bh * 60 + bm) - (vh * 60 + vm);
 return diff > 0 ? diff : null;
 };

 const [rows, setRows] = useState(() => (copyFromPrev ? buildRowsFromPrev() : Array.from({ length: 7 }, (_, i) => buildDefaultRow(i))));

 useEffect(() => {
 setRows(copyFromPrev ? buildRowsFromPrev() : Array.from({ length: 7 }, (_, i) => buildDefaultRow(i)));
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [copyFromPrev]);

 const updateRow = (idx, field, value) => {
 setRows(prev => {
 const next = [...prev];
 next[idx] = { ...next[idx], [field]: value };
 if (field === 'ist_arbeitstag' && !value) {
 next[idx].soll_stunden = 0;
 next[idx].auto_pause_von = null;
 next[idx].auto_pause_bis = null;
 } else if (['von_zeit', 'bis_zeit', 'mittagspause_min', 'auto_pause_von', 'auto_pause_bis'].includes(field)) {
 const autoVon = field === 'auto_pause_von' ? value : next[idx].auto_pause_von;
 const autoBis = field === 'auto_pause_bis' ? value : next[idx].auto_pause_bis;
 const autoDauer = timeDiffMin(autoVon, autoBis);
 if (autoDauer != null) next[idx].mittagspause_min = autoDauer;
 const von = field === 'von_zeit' ? value : next[idx].von_zeit;
 const bis = field === 'bis_zeit' ? value : next[idx].bis_zeit;
 const pause = autoDauer != null ? autoDauer : (field === 'mittagspause_min' ? value : next[idx].mittagspause_min);
 const brutto = (bis > von) ? bis - von : 0;
 const netto = Math.max(0, brutto - (parseInt(pause, 10) || 0) / 60);
 next[idx].soll_stunden = Math.round(netto * 100) / 100;
 }
 return next;
 });
 };

 const decToTime = (dec) => {
 if (dec == null || dec === '') return '';
 const d = parseFloat(dec);
 if (isNaN(d)) return '';
 const h = Math.floor(d);
 const m = Math.round((d - h) * 60);
 return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
 };
 const timeToDec = (time) => {
 if (!time) return 0;
 const [h, m] = time.split(':').map(Number);
 return h + (m || 0) / 60;
 };

 const weeklySum = rows.reduce((s, r) => s + (r.ist_arbeitstag ? (r.soll_stunden || 0) : 0), 0);
 const prevEnd = previousPhase?.gueltig_bis?.slice(0, 10);
 const prevStart = previousPhase?.gueltig_ab?.slice(0, 10);
 const prevHasEnd = !!prevEnd;
 let continuityHint = null;
 if (previousPhase && gueltigAb) {
 if (gueltigAb <= prevStart) {
 continuityHint = { type: 'err', msg: t('workSchedule.new_phase_before_prev', `Gültig-ab muss nach ${prevStart} liegen.`) };
 } else if (prevHasEnd) {
 const expected = addDaysStr(prevEnd, 1);
 if (gueltigAb !== expected) {
 continuityHint = { type: 'warn', msg: gueltigAb < expected
 ? t('workSchedule.new_phase_overlap', `Überlappt mit Phase bis ${prevEnd}.`)
 : t('workSchedule.new_phase_gap', `Lücke von ${expected} bis ${gueltigAb}.`) };
 }
 } else {
 continuityHint = { type: 'info', msg: t('workSchedule.new_phase_closes_prev', `Vorherige Phase wird automatisch am ${addDaysStr(gueltigAb, -1)} beendet.`) };
 }
 }

 const handleSubmit = async () => {
 setLocalError(null);
 if (!gueltigAb) { setLocalError(t('workSchedule.new_phase_need_date', 'Gültig-ab-Datum fehlt.')); return; }
 if (continuityHint?.type === 'err') { setLocalError(continuityHint.msg); return; }
 setSaving(true);
 const ok = await onSave({ gueltig_ab: gueltigAb, tage: rows });
 setSaving(false);
 if (!ok) {
 // Parent wird phaseError setzen; Modal bleibt offen damit User korrigieren kann
 // Aber zeig's auch inline:
 setLocalError(t('workSchedule.new_phase_save_failed', 'Speichern fehlgeschlagen — siehe Fehlermeldung oben.'));
 }
 };

 return (
 <div className="fixed inset-0 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.35)', zIndex: 70 }} onClick={onCancel}>
 <div className="bg-white rounded-2xl w-full max-w-[760px] p-6 max-h-[90vh] overflow-y-auto" style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.1)' }} onClick={e => e.stopPropagation()}>
 <div className="flex items-center justify-between mb-4">
 <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
 {t('workSchedule.new_phase_title', 'Neue Phase anlegen')}
 </h3>
 <button onClick={onCancel} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"><X size={18} /></button>
 </div>

 <div className="grid grid-cols-2 gap-3 mb-3">
 <div>
 <label className="text-[10px] text-[var(--text-secondary)] mb-0.5 block font-semibold uppercase">{t('workSchedule.valid_from') || 'Gültig ab'}</label>
 <input
 type="date"
 value={gueltigAb}
 onChange={e => setGueltigAb(e.target.value)}
 className="w-full text-xs bg-[var(--bg-secondary)] border border-line rounded px-2 py-1.5 text-content font-mono"
 />
 </div>
 {previousPhase && (
 <div>
 <label className="text-[10px] text-[var(--text-secondary)] mb-0.5 block font-semibold uppercase">{t('workSchedule.copy_from_previous', 'Von vorheriger übernehmen')}</label>
 <label className="inline-flex items-center gap-2 text-xs mt-1">
 <input type="checkbox" checked={copyFromPrev} onChange={e => setCopyFromPrev(e.target.checked)} className="accent-primary" />
 <span className="text-[var(--text-secondary)]">{previousPhase.stunden_pro_woche}h/Woche ab {prevStart}</span>
 </label>
 </div>
 )}
 </div>

 {continuityHint && (
 <div className={`mb-3 px-3 py-2 text-[11px] rounded-lg border inline-flex items-start gap-2 w-full ${
 continuityHint.type === 'err' ? 'bg-red-50 text-red-700 border-red-200' :
 continuityHint.type === 'warn' ? 'bg-amber-50 text-amber-800 border-amber-200' :
 'bg-blue-50 text-blue-700 border-blue-200'
 }`}>
 <AlertTriangle size={12} className="mt-0.5 shrink-0" />
 <span>{continuityHint.msg}</span>
 </div>
 )}

 <table className="w-full text-xs mb-3">
 <thead>
 <tr className="text-[var(--text-secondary)]">
 <th className="text-left py-1 font-semibold w-10">{t('employees.detail.day_col')}</th>
 <th className="text-center py-1 font-semibold">{t('employees.detail.active_col') || 'Aktiv'}</th>
 <th className="text-center py-1 font-semibold">{t('employees.detail.from_col') || 'Von'}</th>
 <th className="text-center py-1 font-semibold">{t('employees.detail.to_col') || 'Bis'}</th>
 <th className="text-center py-1 font-semibold">{t('workSchedule.pause_col', 'Pause')}</th>
 <th className="text-center py-1 font-semibold" title={t('autoPause.column_tooltip', 'Auto-Pause: System stempelt die Mittagspause automatisch im Fenster (von – bis).')}>{t('autoPause.column', 'Auto-Pause')}</th>
 <th className="text-right py-1 font-semibold">{t('workSchedule.netto_col', 'Netto')}</th>
 </tr>
 </thead>
 <tbody>
 {rows.map((r, i) => {
 const autoPauseAktiv = !!(r.auto_pause_von && r.auto_pause_bis);
 return (
 <tr key={i} className={`border-t border-line/30 ${!r.ist_arbeitstag ? 'opacity-40' : ''}`}>
 <td className="py-1.5 font-bold text-content-secondary">{TAGE_DE[i]}</td>
 <td className="py-1.5 text-center">
 <input type="checkbox" checked={r.ist_arbeitstag} onChange={e => updateRow(i, 'ist_arbeitstag', e.target.checked)} className="rounded border-line accent-primary" />
 </td>
 <td className="py-1.5 text-center">
 <input type="time" step="900" value={decToTime(r.von_zeit)} disabled={!r.ist_arbeitstag}
 onChange={e => updateRow(i, 'von_zeit', timeToDec(e.target.value))}
 className="w-20 text-center text-xs font-mono bg-[var(--bg-secondary)] border border-line rounded px-1 py-0.5 disabled:opacity-30" />
 </td>
 <td className="py-1.5 text-center">
 <input type="time" step="900" value={decToTime(r.bis_zeit)} disabled={!r.ist_arbeitstag}
 onChange={e => updateRow(i, 'bis_zeit', timeToDec(e.target.value))}
 className="w-20 text-center text-xs font-mono bg-[var(--bg-secondary)] border border-line rounded px-1 py-0.5 disabled:opacity-30" />
 </td>
 <td className="py-1.5 text-center">
 <input type="number" step="5" min="0" max="120" value={r.mittagspause_min ?? 0} disabled={!r.ist_arbeitstag || autoPauseAktiv}
 title={autoPauseAktiv ? t('autoPause.synced_tooltip', 'Automatisch synchronisiert mit Auto-Pause-Fenster (bis – von).') : undefined}
 onFocus={e => e.target.select()}
 onChange={e => {
 const v = e.target.value.replace(/^0+(?=\d)/, '');
 updateRow(i, 'mittagspause_min', v === '' ? 0 : parseInt(v, 10) || 0);
 }}
 className="w-14 text-center text-xs font-mono bg-[var(--bg-secondary)] border border-line rounded px-1 py-0.5 disabled:opacity-30" />
 <span className="text-[10px] text-[var(--text-tertiary)] ml-1">min</span>
 </td>
 <td className="py-1.5 text-center">
 <div className="inline-flex items-center gap-1">
 <input type="time" step="300" value={r.auto_pause_von || ''} disabled={!r.ist_arbeitstag}
 title={t('autoPause.from_tooltip', 'Auto-Pause Start (HH:MM). Leer = keine Auto-Pause.')}
 onChange={e => updateRow(i, 'auto_pause_von', e.target.value || null)}
 className="w-20 text-center text-xs font-mono bg-[var(--bg-secondary)] border border-line rounded px-1 py-0.5 disabled:opacity-30" />
 <span className="text-[10px] text-[var(--text-tertiary)]">–</span>
 <input type="time" step="300" value={r.auto_pause_bis || ''} disabled={!r.ist_arbeitstag}
 title={t('autoPause.to_tooltip', 'Auto-Pause Ende (HH:MM). Leer = keine Auto-Pause.')}
 onChange={e => updateRow(i, 'auto_pause_bis', e.target.value || null)}
 className="w-20 text-center text-xs font-mono bg-[var(--bg-secondary)] border border-line rounded px-1 py-0.5 disabled:opacity-30" />
 </div>
 </td>
 <td className="py-1.5 text-right font-mono font-bold text-content">{r.ist_arbeitstag ? (Math.round(r.soll_stunden * 100) / 100) + 'h' : '–'}</td>
 </tr>
 );
 })}
 </tbody>
 </table>

 {rows.some(r => r.ist_arbeitstag && r.auto_pause_von && r.auto_pause_bis) && (
 <div className="mb-3 px-3 py-2 text-[11px] rounded-lg border bg-blue-50 text-blue-700 border-blue-200">
 <div className="font-semibold mb-0.5">{t('autoPause.hint_title', 'Auto-Pause aktiv')}</div>
 <div>{t('autoPause.hint_text', 'System stempelt die Mittagspause automatisch im hinterlegten Zeitfenster. Manueller Vorrang: Wenn der MA im Fenster selbst stempelt, wird die Auto-Pause übersprungen.')}</div>
 </div>
 )}

 <div className="flex items-center justify-between mb-3 pt-2 border-t border-line">
 <span className="text-xs text-[var(--text-secondary)]">{t('workSchedule.netto_weekly', 'Netto-Wochenstunden')}</span>
 <span className="text-sm font-mono font-extrabold text-content">{Math.round(weeklySum * 100) / 100}h</span>
 </div>

 {localError && (
 <div className="mb-3 px-3 py-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg">{localError}</div>
 )}

 <div className="flex justify-end gap-2">
 <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm font-medium border" style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-primary, #E5E7EB)' }}>
 {t('common.cancel', 'Abbrechen')}
 </button>
 <button
 onClick={handleSubmit}
 disabled={saving || continuityHint?.type === 'err'}
 className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50"
 style={{ backgroundColor: '#338edc' }}
 >
 {saving ? '…' : t('common.save', 'Speichern')}
 </button>
 </div>
 </div>
 </div>
 );
}

// ═══ KARTE: Wöchentliche Anwesenheit (erweitert mit SOLL/IST) ═══
function WeeklyAttendance({ id }) {
 const { t, locale } = useLanguage();
 const [data, setData] = useState(null);
 const [loading, setLoading] = useState(true);
 const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
 const weekdays = WEEKDAY_KEYS.map(k => t(`common.weekdays.${k}`));

 const loadWeek = useCallback(() => {
 const von = dateStr(weekStart);
 const bis = dateStr(addDays(weekStart, 6));
 setLoading(true);
 api(`/api/mitarbeiter/${id}/attendance?von=${von}&bis=${bis}`)
 .then(d => { if (!d.error) setData(d); })
 .finally(() => setLoading(false));
 }, [id, weekStart]);

 useEffect(() => { loadWeek(); }, [loadWeek]);

 const days = useMemo(() => {
 if (!data?.records) return [];
 const sollPerDay = data.soll_per_day || {};
 const byDay = {};
 for (let i = 0; i < 7; i++) {
 const d = addDays(weekStart, i);
 const ds = dateStr(d);
 byDay[ds] = { date: ds, weekday: weekdays[i], dow: i, records: [], total: 0, soll: sollPerDay[i] || 0, brutto: 0, pause: 0, isToday: dateStr(new Date()) === ds, tag_typ: null, is_present: false };
 }
 // #820 — Gruppiere per r.datum (nicht check_in.slice), damit Abwesenheitstage
 // ohne Stempel nicht verloren gehen und der UTC-Shift nicht mehr falsch matcht.
 for (const r of data.records) {
 const ds = r.datum || r.check_in?.slice(0, 10);
 if (ds && byDay[ds]) {
 byDay[ds].records.push(r);
 byDay[ds].total += r.worked_hours || 0;
 byDay[ds].brutto += (r.brutto_minuten || 0) / 60;
 byDay[ds].pause += (r.gesamt_pausen_min || 0) / 60;
 byDay[ds].tag_typ = r.tag_typ || byDay[ds].tag_typ;
 if (r.is_present) byDay[ds].is_present = true;
 if (r.soll_minuten != null) byDay[ds].soll_ist = Math.round((r.soll_minuten / 60) * 100) / 100;
 }
 }
 return Object.values(byDay);
 }, [data, weekStart, weekdays]);

 const todayStrMemo = dateStr(new Date());
 const weekTotal = days.reduce((s, d) => s + d.total, 0);
 const weekSoll = days.reduce((s, d) => s + d.soll, 0);
 // #862 — SOLL-bis-heute für sinnvollen Delta bei laufender Woche
 const weekSollBisHeute = days.reduce((s, d) => {
 if (d.date > todayStrMemo) return s;
 // Laufender Tag: nur SOLL zählen wenn bereits abgeschlossen (soll_ist gesetzt via TAZ)
 if (d.date === todayStrMemo && !d.records.some(r => r.check_out)) return s;
 return s + (d.soll_ist != null ? d.soll_ist : d.soll);
 }, 0);
 const isCurrentWeek = dateStr(startOfWeek(new Date())) === dateStr(weekStart);
 // Calculate KW number
 const kwDate = new Date(weekStart);
 const yearStart = new Date(kwDate.getFullYear(), 0, 1);
 const kw = Math.ceil(((kwDate - yearStart) / 86400000 + yearStart.getDay() + 1) / 7);

 return (
 <Card title={t('employees.detail.attendance_cw', { kw })} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>}>
 <div className="flex items-center gap-2 mb-4">
 <button onClick={() => setWeekStart(addDays(weekStart, -7))}
 className="w-7 h-7 flex items-center justify-center rounded-lg bg-surface-inset border border-line-hover text-content-tertiary hover:text-content hover:bg-surface-active transition-colors">
 <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 19l-7-7 7-7"/></svg>
 </button>
 <button onClick={() => setWeekStart(startOfWeek(new Date()))}
 className={`px-2 py-1 rounded text-[10px] font-semibold ${isCurrentWeek ? 'bg-emerald-500/20 text-emerald-400' : 'bg-surface-inset text-[var(--text-secondary)] hover:bg-surface-active'}`}>
 {t('employees.detail.this_week_btn')}
 </button>
 <button onClick={() => setWeekStart(addDays(weekStart, 7))}
 className="w-7 h-7 flex items-center justify-center rounded-lg bg-surface-inset border border-line-hover text-content-tertiary hover:text-content hover:bg-surface-active transition-colors">
 <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7"/></svg>
 </button>
 <span className="text-xs text-[var(--text-secondary)] ml-1">
 {fmtDate(weekStart, locale)} {'–'} {fmtDate(addDays(weekStart, 6), locale)}
 </span>
 </div>

 {loading ? (
 <div className="text-xs text-[var(--text-tertiary)] py-4 text-center">{t('employees.detail.loading_attendance')}</div>
 ) : (
 <>
 {/* Table header */}
 <div className="flex items-center gap-1 px-2 py-1 text-[9px] text-[var(--text-secondary)] uppercase tracking-wider font-bold mb-1">
 <span className="w-5"></span>
 <span className="w-14"></span>
 <span className="w-10 text-center">{t('employees.detail.from_col')}</span>
 <span className="w-3"></span>
 <span className="w-10 text-center">{t('employees.detail.to_col')}</span>
 <span className="flex-1"></span>
 <span className="w-10 text-right">{t('employees.detail.target_header')}</span>
 <span className="w-10 text-right">{t('employees.detail.actual_header')}</span>
 <span className="w-10 text-right">{t('employees.detail.diff_header')}</span>
 </div>
 <div className="space-y-0.5">
 {days.map(d => {
 const firstIn = d.records.length > 0 ? d.records[0].check_in : null;
 const lastOut = d.records.length > 0 ? d.records[d.records.length - 1].check_out : null;
 const isPresent = d.is_present || d.records.some(r => r.is_present);
 // #820 — "Laufend" wenn heute + check_in aber noch kein check_out (is_present)
 const isRunning = isPresent && firstIn && !lastOut;
 const isAbsence = d.tag_typ === 'abwesenheit_bezahlt' || d.tag_typ === 'feiertag';
 const diff = d.total - d.soll;
 const isFreeDay = d.soll === 0;
 const workedOnFree = isFreeDay && d.total > 0 && !isAbsence;
 let rowBg = '';
 if (d.isToday) rowBg = 'bg-emerald-500/5 border border-emerald-500/20';
 else if (workedOnFree) rowBg = 'bg-primary-500/5';
 else if (isAbsence) rowBg = 'bg-surface-inset/60';

 let diffClr = 'text-[var(--text-tertiary)]';
 if (workedOnFree) diffClr = 'text-primary-400';
 else if (d.total > 0 && diff >= 0) diffClr = 'text-emerald-400';
 else if (d.total > 0 && diff < 0) diffClr = 'text-red-400';

 // v2.365.1 (Teil C): Tooltip für bezahlte Nicht-Arbeitstage
 // #862: Brutto/Pause in Tooltip für Transparenz
 const rowTooltip = isAbsence
 ? `${d.tag_typ === 'feiertag' ? t('employees.detail.holiday', 'Feiertag') : t('employees.detail.absence', 'Abwesenheit')} — ${t('employees.detail.paid_day_tooltip', 'Bezahlter Tag. SOLL wird als erfüllt gewertet, daher Delta 0.')}`
 : (d.total > 0 && d.brutto > 0
 ? `${t('employees.detail.brutto', 'Brutto')}: ${d.brutto.toFixed(2).replace('.', ',')}h · ${t('employees.detail.pause', 'Pause')}: ${(d.pause * 60).toFixed(0)}min · ${t('employees.detail.netto', 'Netto')}: ${d.total.toFixed(2).replace('.', ',')}h`
 : undefined);
 return (
 <div key={d.date} title={rowTooltip} className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs ${rowBg} ${isAbsence ? 'cursor-help' : ''}`}>
 <span className={`w-5 font-bold ${d.isToday ? 'text-emerald-400' : isFreeDay ? 'text-[var(--text-tertiary)]' : 'text-[var(--text-secondary)]'}`}>{d.weekday}</span>
 <span className="text-[var(--text-tertiary)] w-14 font-mono">{d.date.slice(5)}</span>
 <span className="font-mono text-content-secondary w-10 text-center">{fmtTime(firstIn)}</span>
 <span className="text-[var(--text-tertiary)] w-3 text-center">{'→'}</span>
 <span className="font-mono text-content-secondary w-10 text-center">{isRunning ? '…' : fmtTime(lastOut)}</span>
 <div className="flex-1 h-1.5 bg-surface-inset rounded-full overflow-hidden mx-1">
 <div className={`h-full rounded-full ${isAbsence ? 'bg-[#338edc]/40' : workedOnFree ? 'bg-primary-500/60' : d.total >= d.soll && d.soll > 0 ? 'bg-emerald-500/60' : d.total > 0 ? 'bg-yellow-500/60' : 'bg-transparent'}`}
 style={{ width: `${isAbsence ? 100 : d.soll > 0 ? Math.min(100, (d.total / d.soll) * 100) : d.total > 0 ? 100 : 0}%` }} />
 </div>
 <span className="font-mono text-[var(--text-tertiary)] w-10 text-right text-[10px]">{d.soll > 0 ? `${d.soll}h` : t('employees.detail.free')}</span>
 <span className={`font-mono font-bold w-10 text-right ${isRunning ? 'text-amber-500' : isAbsence ? 'text-[#338edc]' : d.total > 0 ? 'text-content-secondary' : 'text-[var(--text-tertiary)]'}`}>
 {isRunning ? t('employees.detail.running', 'Läuft') :
 isAbsence ? fmtHours(d.total, locale) :
 d.total > 0 ? fmtHours(d.total, locale) : '–'}
 </span>
 <span className={`font-mono text-[10px] w-10 text-right ${diffClr}`}>
 {isRunning || isAbsence ? '' :
 d.total > 0 && d.soll > 0 ? `${diff >= 0 ? '+' : ''}${diff.toFixed(1)}` :
 isFreeDay && d.total > 0 ? t('employees.detail.extra') : ''}
 </span>
 {isPresent && <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse flex-shrink-0 ml-1" />}
 </div>
 );
 })}
 </div>
 {/* #862: SOLL-bis-heute für laufende Woche, voller Wochen-SOLL als Sub */}
 <div className="flex items-center justify-between mt-3 pt-3 border-t border-line">
 <span className="text-xs text-[var(--text-secondary)]">{t('employees.detail.week_label')}</span>
 <div className="flex flex-col items-end gap-0.5">
 <div className="flex items-center gap-3">
 <span className={`text-sm font-mono font-extrabold ${weekTotal > 0 ? 'text-content' : 'text-[var(--text-tertiary)]'}`}>
 {fmtHours(weekTotal, locale)}
 </span>
 <span className="text-[10px] text-[var(--text-tertiary)]">/ {weekSollBisHeute.toFixed(1).replace('.', ',')}h {t('employees.detail.target_bis_heute', 'SOLL bis heute')}</span>
 {weekTotal > 0 && weekSollBisHeute > 0 && (
 <span className={`text-[10px] font-mono font-bold ${(weekTotal - weekSollBisHeute) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
 ({weekTotal - weekSollBisHeute >= 0 ? '+' : ''}{(weekTotal - weekSollBisHeute).toFixed(1)}h)
 </span>
 )}
 </div>
 <div className="text-[9px] text-[var(--text-tertiary)]">
 {t('employees.detail.target_week_full', 'Wochen-SOLL ges.')}: {weekSoll}h
 </div>
 </div>
 </div>
 </>
 )}
 </Card>
 );
}

// ═══ KARTE: Monatsübersicht Mini-Kalender ═══
function MonthlyAttendance({ id }) {
 const { t, locale, tMonthShort } = useLanguage();
 const now = new Date();
 const [monat, setMonat] = useState(now.getMonth());
 const [jahr, setJahr] = useState(now.getFullYear());
 const [data, setData] = useState(null);
 const [loading, setLoading] = useState(true);
 const weekdays = WEEKDAY_KEYS.map(k => t(`common.weekdays.${k}`));

 useEffect(() => {
 const von = `${jahr}-${pad2(monat + 1)}-01`;
 const lastDay = new Date(jahr, monat + 1, 0);
 const bis = dateStr(lastDay);
 setLoading(true);
 api(`/api/mitarbeiter/${id}/attendance?von=${von}&bis=${bis}`)
 .then(d => { if (!d.error) setData(d); })
 .finally(() => setLoading(false));
 }, [id, monat, jahr]);

 // #862 — dayTaz für SOLL-bis-heute (aus r.soll_minuten wenn vorhanden)
 const { dayGrid, dayTazSoll, totalHours, totalDays, sollPerDay } = useMemo(() => {
 const grid = {};
 const tazSoll = {};
 const lastDay = new Date(jahr, monat + 1, 0).getDate();
 for (let d = 1; d <= lastDay; d++) grid[d] = 0;
 if (data?.records) {
 for (const r of data.records) {
 const day = parseInt((r.datum || r.check_in)?.slice(8, 10), 10);
 if (day && grid[day] !== undefined) grid[day] += r.worked_hours || 0;
 if (day && r.soll_minuten != null) tazSoll[day] = r.soll_minuten / 60;
 }
 }
 const totalH = Object.values(grid).reduce((s, h) => s + h, 0);
 const totalD = Object.values(grid).filter(h => h > 0).length;
 return { dayGrid: grid, dayTazSoll: tazSoll, totalHours: totalH, totalDays: totalD, sollPerDay: data?.soll_per_day || {} };
 }, [data, monat, jahr]);

 const firstDayOfWeek = new Date(jahr, monat, 1).getDay();
 const calOffset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
 const daysInMonth = new Date(jahr, monat + 1, 0).getDate();

 const monthSoll = useMemo(() => {
 let total = 0;
 for (let d = 1; d <= daysInMonth; d++) {
 const dt = new Date(jahr, monat, d);
 const dow = dt.getDay();
 const mdow = dow === 0 ? 6 : dow - 1;
 total += sollPerDay[mdow] || 0;
 }
 return Math.round(total * 100) / 100;
 }, [sollPerDay, daysInMonth, jahr, monat]);

 // #862 — SOLL bis heute: Summe der TAZ soll_minuten bis heute (für sinnvolle Delta-Vergleich)
 const monthSollBisHeute = useMemo(() => {
 const today = new Date();
 const isCurrentMonth = today.getFullYear() === jahr && today.getMonth() === monat;
 const endDay = isCurrentMonth ? today.getDate() : daysInMonth;
 let total = 0;
 for (let d = 1; d <= endDay; d++) {
 // TAZ-SOLL bevorzugen, sonst sollPerDay als Fallback
 if (dayTazSoll[d] != null) {
 total += dayTazSoll[d];
 } else {
 const dt = new Date(jahr, monat, d);
 const dow = dt.getDay();
 const mdow = dow === 0 ? 6 : dow - 1;
 total += sollPerDay[mdow] || 0;
 }
 }
 return Math.round(total * 100) / 100;
 }, [dayTazSoll, sollPerDay, daysInMonth, jahr, monat]);

 return (
 <Card title={t('employees.detail.attendance_month')} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>}>
 <div className="flex items-center gap-2 mb-3">
 <button onClick={() => { if (monat === 0) { setMonat(11); setJahr(j => j - 1); } else setMonat(m => m - 1); }}
 className="w-6 h-6 flex items-center justify-center rounded bg-surface-inset text-[var(--text-tertiary)] hover:text-content text-xs">&lt;</button>
 <span className="text-xs text-content-secondary font-medium">{tMonthShort(monat)} {jahr}</span>
 <button onClick={() => { if (monat === 11) { setMonat(0); setJahr(j => j + 1); } else setMonat(m => m + 1); }}
 className="w-6 h-6 flex items-center justify-center rounded bg-surface-inset text-[var(--text-tertiary)] hover:text-content text-xs">&gt;</button>
 </div>

 {loading ? (
 <div className="text-xs text-[var(--text-tertiary)] py-4 text-center">{t('employees.detail.loading_short')}</div>
 ) : (
 <>
 <div className="grid grid-cols-7 gap-1 mb-1">
 {weekdays.map(w => <div key={w} className="text-[9px] text-[var(--text-secondary)] text-center font-bold">{w}</div>)}
 </div>
 <div className="grid grid-cols-7 gap-1">
 {Array.from({ length: calOffset }, (_, i) => <div key={`e${i}`} />)}
 {Array.from({ length: daysInMonth }, (_, i) => {
 const day = i + 1;
 const d = new Date(jahr, monat, day);
 const dow = d.getDay();
 const mdow = dow === 0 ? 6 : dow - 1;
 const daySoll = sollPerDay[mdow] || 0;
 const isFreeDay = daySoll === 0;
 const hours = dayGrid[day] || 0;
 const isTodayCell = dateStr(d) === dateStr(new Date());
 let bg = 'bg-surface-inset text-[var(--text-tertiary)]';
 if (isFreeDay && hours === 0) bg = 'bg-[rgba(107,114,128,0.2)] text-[var(--text-muted)]';
 else if (isFreeDay && hours > 0) bg = 'bg-[#338edc]/30 text-[#338edc] font-bold';
 else if (hours >= daySoll && daySoll > 0) bg = 'bg-emerald-500/35 text-emerald-600 dark:text-emerald-400';
 else if (hours >= daySoll * 0.75) bg = 'bg-amber-500/35 text-amber-600 dark:text-amber-400';
 else if (hours > 0) bg = 'bg-red-500/35 text-red-600 dark:text-red-400';

 return (
 <div key={day} className={`h-7 flex items-center justify-center rounded text-[10px] font-bold ${bg} ${isTodayCell ? 'ring-1 ring-emerald-500' : ''}`}
 title={`${day}.${monat + 1}: ${hours > 0 ? hours.toFixed(1) + 'h' : '–'} / ${daySoll}h ${t('employees.detail.target_suffix')}`}>
 {day}
 </div>
 );
 })}
 </div>

 {/* #862: SOLL-bis-heute primär, Monat-SOLL sekundär */}
 <div className="flex items-center justify-between mt-3 pt-3 border-t border-line">
 <div className="text-[10px] text-[var(--text-tertiary)]">{t('employees.detail.days_worked', { n: totalDays })}</div>
 <div className="flex flex-col items-end gap-0.5">
 <div className="flex items-center gap-2">
 <span className={`text-sm font-mono font-extrabold ${totalHours > 0 ? 'text-content' : 'text-[var(--text-tertiary)]'}`}>
 {totalHours.toFixed(1)}h
 </span>
 {monthSollBisHeute > 0 && (
 <span className="text-[10px] text-[var(--text-tertiary)]">
 / {monthSollBisHeute.toFixed(1).replace('.', ',')}h {t('employees.detail.target_bis_heute', 'SOLL bis heute')}
 </span>
 )}
 {totalHours > 0 && monthSollBisHeute > 0 && (
 <span className={`text-[10px] font-mono font-bold ${(totalHours - monthSollBisHeute) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
 ({totalHours - monthSollBisHeute >= 0 ? '+' : ''}{(totalHours - monthSollBisHeute).toFixed(1)}h)
 </span>
 )}
 </div>
 {monthSoll > 0 && (
 <div className="text-[9px] text-[var(--text-tertiary)]">
 {t('employees.detail.target_month_full', 'Monats-SOLL ges.')}: {monthSoll}h
 </div>
 )}
 </div>
 </div>

 <div className="flex items-center gap-3 mt-2 text-[9px] text-[var(--text-tertiary)]">
 <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-emerald-500/35" /> {t('employees.detail.gte_target')}</span>
 <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-amber-500/35" /> {t('employees.detail.gte_75')}</span>
 <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-red-500/35" /> {t('employees.detail.lt_75')}</span>
 <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-[#338edc]/30" /> {t('employees.detail.extra_legend')}</span>
 <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-[rgba(107,114,128,0.2)]" /> {t('employees.detail.free_legend')}</span>
 </div>
 </>
 )}
 </Card>
 );
}

// ═══ KARTE: Abwesenheiten (v2.358.0 / #820 — Neu: nur dieser MA, Urlaub + Progress + Nächste) ═══
function LeavesCard({ id }) {
 const { t, locale } = useLanguage();
 const [payload, setPayload] = useState(null);
 const [loading, setLoading] = useState(true);
 const currentYear = new Date().getFullYear();

 useEffect(() => {
 api(`/api/abwesenheiten/ma/${id}/statistiken?jahr=${currentYear}`)
 .then(d => { if (d.success) setPayload(d); })
 .finally(() => setLoading(false));
 }, [id]);

 const stats = payload?.data || [];
 const urlaub = payload?.urlaub || { anspruch: 0, genommen: 0, rest_vorjahr: 0, verfuegbar: 0 };
 const naechste = payload?.naechste_abwesenheit || null;
 const andereTypen = stats.filter(s => s.art_key !== 'urlaub');

 const anspruchTotal = (parseFloat(urlaub.anspruch) || 0) + (parseFloat(urlaub.rest_vorjahr) || 0);
 const genommen = parseFloat(urlaub.genommen) || 0;
 const pct = anspruchTotal > 0 ? Math.min(100, Math.round((genommen / anspruchTotal) * 100)) : 0;

 const formatRange = (von, bis) => {
 if (!von) return '';
 if (!bis || bis === von) return fmtDate(new Date(von + 'T00:00:00'), locale);
 return `${fmtDate(new Date(von + 'T00:00:00'), locale)} – ${fmtDate(new Date(bis + 'T00:00:00'), locale)}`;
 };

 return (
 <Card title={t('employees.detail.leaves_title', { year: currentYear })} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>}>
 {loading ? (
 <div className="text-xs text-[var(--text-tertiary)] py-4 text-center">{t('employees.detail.loading_leaves')}</div>
 ) : (
 <>
 {/* Urlaub mit Fortschrittsbalken */}
 <div className="mb-3">
 <div className="flex items-center justify-between mb-1.5">
 <div className="flex items-center gap-1.5">
 <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#22C55E' }} />
 <span className="text-xs font-medium text-[var(--text-primary)]">{t('employees.detail.urlaub_label', 'Urlaub')}</span>
 </div>
 <div className="flex items-baseline gap-1">
 <span className="text-sm font-mono font-bold text-[var(--text-primary)]">{genommen.toFixed(1)}</span>
 <span className="text-[10px] text-[var(--text-tertiary)]">/ {anspruchTotal.toFixed(0)} {t('employees.detail.days_short', 'Tage')}</span>
 </div>
 </div>
 <div className="h-2 bg-[var(--bg-inset,#F8F9FC)] rounded-full overflow-hidden border border-line">
 <div className="h-full bg-emerald-500 rounded-full transition-all"
 style={{ width: `${pct}%` }} />
 </div>
 <div className="flex items-center justify-between mt-1">
 <span className="text-[10px] text-[var(--text-tertiary)]">
 {pct}% {t('employees.detail.urlaub_genommen', 'genommen')}
 </span>
 {urlaub.verfuegbar > 0 && (
 <span className="text-[10px] text-[var(--text-tertiary)]">
 {urlaub.verfuegbar.toFixed(1)} {t('employees.detail.urlaub_verfuegbar', 'verfügbar')}
 </span>
 )}
 </div>
 </div>

 {/* Andere Abwesenheitstypen */}
 {andereTypen.length > 0 && (
 <div className="space-y-1.5 mb-3">
 {andereTypen.map(s => {
 const tage = parseFloat(s.tage_gesamt) || 0;
 return (
 <div key={s.art_key} className="flex items-center justify-between text-xs">
 <div className="flex items-center gap-1.5">
 <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.farbe || '#6B7280' }} />
 <span className="text-[var(--text-secondary)]">{locale.startsWith('de') ? s.label_de : (s.label_en || s.label_de)}</span>
 </div>
 <span className="font-mono text-[var(--text-primary)]">
 {tage.toFixed(1)} {tage === 1 ? t('employees.detail.day_short', 'Tag') : t('employees.detail.days_short', 'Tage')}
 </span>
 </div>
 );
 })}
 </div>
 )}

 {/* Nächste Abwesenheit */}
 {naechste && (
 <div className="mt-3 pt-3 border-t border-line">
 <div className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wider mb-1">
 {t('employees.detail.next_absence', 'Nächste geplante Abwesenheit')}
 </div>
 <div className="flex items-center gap-1.5 text-xs">
 <span className="w-2 h-2 rounded-full" style={{ backgroundColor: naechste.farbe || '#6B7280' }} />
 <span className="text-[var(--text-primary)] font-medium">
 {locale.startsWith('de') ? naechste.label_de : (naechste.label_en || naechste.label_de)}
 </span>
 <span className="text-[var(--text-secondary)]">
 {formatRange(naechste.von_datum, naechste.bis_datum)}
 </span>
 <span className="text-[10px] text-[var(--text-tertiary)]">
 ({parseFloat(naechste.tage).toFixed(0)} {parseFloat(naechste.tage) === 1 ? t('employees.detail.day_short', 'Tag') : t('employees.detail.days_short', 'Tage')})
 </span>
 </div>
 </div>
 )}

 {stats.length === 0 && !naechste && (
 <div className="text-xs text-[var(--text-tertiary)] py-4 text-center">{t('employees.detail.no_leaves', { year: currentYear })}</div>
 )}
 </>
 )}
 <div className="mt-3 pt-2 border-t border-line/30">
 <Link to={`/abwesenheiten/kalender?ma=${id}`} className="text-[10px] text-[#338edc] hover:text-[#2b7bc4] transition-colors">
 {t('employees.detail.all_absences_link', 'Abwesenheiten anzeigen')} &rarr;
 </Link>
 </div>
 </Card>
 );
}

// ═══ MODAL: Abwesenheit erfassen (v2.239.0: ALL types, max-days validation; #967: stundenweise UX) ═══
function AbwesenheitModal({ id, open, onClose, gleitzeitAktiv }) {
 const { t, locale } = useLanguage();
 const [arten, setArten] = useState([]);
 const [form, setForm] = useState({ art_key: '', von_datum: '', bis_datum: '', von_zeit: '', bis_zeit: '', tage: 1, stunden: '', grund: '' });
 const [ganztaegig, setGanztaegig] = useState(true);
 const [saving, setSaving] = useState(false);
 const [error, setError] = useState(null);

 useEffect(() => {
 if (open) {
 api('/api/abwesenheitsarten').then(d => {
 // v2.239.0: Show ALL active types, not just quelle='abwesenheiten'
 // #1701: Gleitzeittag nur für Gleitzeit-MA anbieten (Backend-Guard gespiegelt)
 // #1864 Folgeseite 3: Arten fremder Quelle (Zeitausgleich) nur, wenn sie stundenweise
 //   erfassbar sind — ganztägig bleibt der Antragsweg (Backend-Gate gespiegelt).
 if (d.data) setArten(d.data.filter(a => (a.quelle === 'abwesenheiten' || a.erlaubt_stundenweise) && (a.key !== 'gleitzeittag' || gleitzeitAktiv)));
 });
 setForm({ art_key: '', von_datum: new Date().toISOString().slice(0, 10), bis_datum: '', von_zeit: '', bis_zeit: '', tage: 1, stunden: '', grund: '' });
 setGanztaegig(true);
 setError(null);
 }
 }, [open, gleitzeitAktiv]);

 const selectedArt = arten.find(a => a.key === form.art_key);
 // #1864 Folgeseite 3: fremde Quelle (Zeitausgleich) hier NUR stundenweise —
 //   „Ganztägig" ist dann kein Schalter, sondern der Antragsweg.
 const nurStundenweise = !!(selectedArt && selectedArt.quelle !== 'abwesenheiten');
 // #967: Default für stundenweise-Arten (z.B. Arztbesuch) auf "stundenweise" — Christina BR-15
 useEffect(() => {
 if (selectedArt?.erlaubt_stundenweise) setGanztaegig(false);
 else setGanztaegig(true);
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [selectedArt?.key]);

 // #967: Live-Dauer-Anzeige aus von_zeit/bis_zeit
 const dauerH = (() => {
 if (!form.von_zeit || !form.bis_zeit) return null;
 const [vh, vm] = form.von_zeit.split(':').map(Number);
 const [bh, bm] = form.bis_zeit.split(':').map(Number);
 if ([vh, vm, bh, bm].some(n => Number.isNaN(n))) return null;
 const min = (bh * 60 + bm) - (vh * 60 + vm);
 if (min <= 0) return null;
 return Math.round((min / 60) * 100) / 100;
 })();

 const stundenweiseAktiv = !!(selectedArt?.erlaubt_stundenweise && (!ganztaegig || nurStundenweise));

 const handleSave = async () => {
 if (!form.art_key || !form.von_datum) { setError(t('absence.fill_required')); return; }
 // #967: Stundenweise-Mode: Pflicht-Validierung der Zeit-Felder
 if (stundenweiseAktiv) {
 if (!form.von_zeit || !form.bis_zeit) {
 setError(t('absence.time_required', 'Bitte Von- und Bis-Uhrzeit eingeben'));
 return;
 }
 if (dauerH == null || dauerH <= 0) {
 setError(t('absence.time_invalid_range', 'Bis-Uhrzeit muss nach Von-Uhrzeit liegen'));
 return;
 }
 } else {
 // Ganztägig: Tage-Validierung
 if (!form.tage || parseFloat(form.tage) <= 0) {
 setError(t('absence.days_required', 'Bitte Anzahl Tage eingeben'));
 return;
 }
 if (selectedArt?.max_tage_pro_jahr && parseFloat(form.tage) > selectedArt.max_tage_pro_jahr) {
 setError(t('absence.max_days_exceeded', { max: selectedArt.max_tage_pro_jahr }) || `Max. ${selectedArt.max_tage_pro_jahr} Tage/Jahr`);
 return;
 }
 }
 setSaving(true);
 setError(null);
 try {
 // #967: Body je nach Mode — stundenweise sendet von_zeit/bis_zeit + tage:null;
 // ganztägig sendet tage + von_zeit/bis_zeit:null. Backend rechnet stunden auto.
 const body = stundenweiseAktiv
 ? { mitarbeiter_id: parseInt(id), art_key: form.art_key, von_datum: form.von_datum, bis_datum: form.bis_datum || form.von_datum, von_zeit: form.von_zeit, bis_zeit: form.bis_zeit, tage: null, stunden: null, grund: form.grund }
 : { mitarbeiter_id: parseInt(id), art_key: form.art_key, von_datum: form.von_datum, bis_datum: form.bis_datum || null, von_zeit: null, bis_zeit: null, tage: parseFloat(form.tage), stunden: null, grund: form.grund };
 const res = await fetch('/api/abwesenheiten', {
 method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body),
 });
 const data = await res.json();
 if (data.success) { onClose(true); } else { setError(data.error || 'Fehler'); }
 } finally { setSaving(false); }
 };

 if (!open) return null;

 // Group types by kategorie
 const grouped = {};
 for (const a of arten) {
 const kat = a.kategorie || 'bezahlt';
 if (!grouped[kat]) grouped[kat] = [];
 grouped[kat].push(a);
 }
 const katLabels = { gesetzlich: t('absence.cat_legal') || 'Gesetzlich', bezahlt: t('absence.cat_paid') || 'Bezahlt', unbezahlt: t('absence.cat_unpaid') || 'Unbezahlt', sonderurlaub: t('absence.cat_special') || 'Sonderurlaub' };

 return (
 <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => onClose(false)}>
 <div className="bg-surface-card border border-line rounded-xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
 <h3 className="text-sm font-bold text-content mb-4">{t('absence.record_title')}</h3>

 {/* Art — grouped by kategorie */}
 <div className="mb-3">
 <label className="text-xs text-[var(--text-secondary)] mb-1 block">{t('absence.type')}</label>
 <select value={form.art_key} onChange={e => setForm(p => ({ ...p, art_key: e.target.value }))}
 className="w-full text-xs bg-[var(--bg-secondary)] border border-line rounded-lg px-3 py-2 text-content">
 <option value="">{t('absence.select_type')}</option>
 {Object.entries(grouped).map(([kat, items]) => (
 <optgroup key={kat} label={katLabels[kat] || kat}>
 {items.map(a => (
 <option key={a.key} value={a.key}>
 {a.kuerzel} — {locale.startsWith('de') ? a.label_de : (a.label_en || a.label_de)}
 {a.max_tage_pro_jahr ? ` (max ${a.max_tage_pro_jahr}d)` : ''}
 </option>
 ))}
 </optgroup>
 ))}
 </select>
 </div>

 {/* Selected type info badge */}
 {selectedArt && (
 <div className="mb-3 flex items-center gap-2">
 <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: selectedArt.farbe }} />
 <span className="text-[10px] text-[var(--text-secondary)]">
 {selectedArt.bezahlt ? t('absence.paid') || 'Bezahlt' : t('absence.unpaid') || 'Unbezahlt'}
 {selectedArt.max_tage_pro_jahr && ` · max. ${selectedArt.max_tage_pro_jahr} ${t('absence.days_per_year') || 'Tage/Jahr'}`}
 {selectedArt.automatisch_genehmigt && ` · ${t('absence.auto_approved') || 'Automatisch genehmigt'}`}
 </span>
 </div>
 )}

 {/* Datum Von/Bis */}
 <div className="grid grid-cols-2 gap-3 mb-3">
 <div>
 <label className="text-xs text-[var(--text-secondary)] mb-1 block">{t('absence.from')}</label>
 <input type="date" value={form.von_datum} onChange={e => setForm(p => ({ ...p, von_datum: e.target.value }))}
 className="w-full text-xs bg-[var(--bg-secondary)] border border-line rounded-lg px-3 py-2 text-content" />
 </div>
 <div>
 <label className="text-xs text-[var(--text-secondary)] mb-1 block">{t('absence.to')}</label>
 <input type="date" value={form.bis_datum} onChange={e => setForm(p => ({ ...p, bis_datum: e.target.value }))}
 className="w-full text-xs bg-[var(--bg-secondary)] border border-line rounded-lg px-3 py-2 text-content" />
 </div>
 </div>

 {/* #967 — Stundenweise (z.B. Arztbesuch): Ganztägig-Toggle + Time-Felder + Live-Dauer */}
 {selectedArt?.erlaubt_stundenweise && (
 <div className="border border-line rounded-lg p-3 mb-3 bg-[var(--bg-inset,#F8F9FC)]">
 {nurStundenweise ? (
 <p className="text-[10px] text-[var(--text-secondary)]" data-testid="abw-nur-stundenweise-hinweis">
 {t('absence.za_hourly_only_hint', 'Ganztägiger Zeitausgleich wird als Antrag erfasst — hier nur stundenweise mit Von-/Bis-Uhrzeit')}
 </p>
 ) : (
 <label className="flex items-center gap-2 cursor-pointer">
 <input type="checkbox" checked={ganztaegig} onChange={e => setGanztaegig(e.target.checked)}
 className="accent-[#338edc] w-4 h-4" />
 <span className="text-xs font-medium text-content">{t('absence.full_day', 'Ganztägig')}</span>
 <span className="text-[10px] text-[var(--text-tertiary)]">— {t('absence.full_day_hint', 'Zeit-Erfassung deaktiviert')}</span>
 </label>
 )}
 {stundenweiseAktiv && (
 <div className="grid grid-cols-2 gap-3 mt-3">
 <div>
 <label className="text-xs text-[var(--text-secondary)] mb-1 block">{t('absence.time_from')}</label>
 <input type="time" value={form.von_zeit} onChange={e => setForm(p => ({ ...p, von_zeit: e.target.value }))}
 className="w-full text-xs bg-[var(--bg-secondary)] border border-line rounded-lg px-3 py-2 text-content" />
 </div>
 <div>
 <label className="text-xs text-[var(--text-secondary)] mb-1 block">{t('absence.time_to')}</label>
 <input type="time" value={form.bis_zeit} onChange={e => setForm(p => ({ ...p, bis_zeit: e.target.value }))}
 className="w-full text-xs bg-[var(--bg-secondary)] border border-line rounded-lg px-3 py-2 text-content" />
 </div>
 </div>
 )}
 {stundenweiseAktiv && dauerH != null && dauerH > 0 && (
 <p className="text-[10px] text-[var(--text-secondary)] mt-2">
 {t('absence.duration_label', 'Dauer')}: {dauerH.toFixed(2).replace('.', ',')} h — {t('absence.counts_as_present', 'zählt als Anwesenheit, bezahlt')}
 </p>
 )}
 </div>
 )}

 {/* Tage — nur wenn nicht stundenweise */}
 {!stundenweiseAktiv && (
 <div className="mb-3">
 <label className="text-xs text-[var(--text-secondary)] mb-1 block">{t('absence.days')}</label>
 <input type="number" step="0.5" min="0.5" value={form.tage} onChange={e => setForm(p => ({ ...p, tage: e.target.value }))}
 className="w-24 text-xs bg-[var(--bg-secondary)] border border-line rounded-lg px-3 py-2 text-content font-mono" />
 </div>
 )}

 {/* Grund */}
 <div className="mb-4">
 <label className="text-xs text-[var(--text-secondary)] mb-1 block">{t('absence.reason')}</label>
 <textarea value={form.grund} onChange={e => setForm(p => ({ ...p, grund: e.target.value }))} rows={2}
 className="w-full text-xs bg-[var(--bg-secondary)] border border-line rounded-lg px-3 py-2 text-content resize-none" />
 </div>

 {error && <div className="text-xs text-red-500 mb-3">{error}</div>}

 <div className="flex gap-2">
 <button onClick={() => onClose(false)} className="flex-1 px-3 py-2 text-xs border border-line rounded-lg text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]">
 {t('common.cancel')}
 </button>
 <button onClick={handleSave} disabled={saving}
 className="flex-1 px-3 py-2 text-xs bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50">
 {saving ? '...' : t('common.save')}
 </button>
 </div>
 </div>
 </div>
 );
}

// ═══ KARTE: Wave Performance ═══
function PerformanceCard({ id }) {
 const { t, locale } = useLanguage();
 const [data, setData] = useState(null);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 const now = new Date();
 const von = new Date(now.getTime() - 30 * 86400000).toISOString().slice(0, 10);
 const bis = now.toISOString().slice(0, 10);
 api(`/api/performance/mitarbeiter/${id}?von=${von}&bis=${bis}`)
 .then(d => { if (!d.error) setData(d); })
 .finally(() => setLoading(false));
 }, [id]);

 const s = data?.summary;
 const fmtDur = (m) => {
 if (!m) return '–';
 const totalMin = Math.round(m);
 if (totalMin < 60) return `${totalMin}min`;
 const hrs = Math.floor(totalMin / 60);
 const mins = totalMin % 60;
 return mins > 0 ? `${hrs}h ${mins}min` : `${hrs}h`;
 };

 return (
 <Card title={t('employees.detail.performance_title')} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>}>
 {loading ? (
 <div className="text-xs text-[var(--text-tertiary)] py-4 text-center">{t('employees.detail.loading_short')}</div>
 ) : !s || s.waves === 0 ? (
 <EmptyState
 variant="compact"
 icon={<Activity className="w-5 h-5" strokeWidth={1.5} />}
 title={t('employees.detail.no_wave_data', 'Noch keine Wave-Daten')}
 description={t('employees.detail.no_wave_data_hint', 'Sobald die/der MA Waves abarbeitet, erscheinen hier KPIs der letzten 30 Tage.')}
 />
 ) : (
 <>
 <div className="grid grid-cols-3 gap-3 mb-3">
 <div className="text-center">
 <div className="text-lg font-mono font-extrabold text-content">{s.waves}</div>
 <div className="text-[9px] text-[var(--text-secondary)] uppercase">{t('employees.detail.waves')}</div>
 </div>
 <div className="text-center">
 <div className="text-lg font-mono font-extrabold text-content">{(s.articles || 0).toLocaleString(locale)}</div>
 <div className="text-[9px] text-[var(--text-secondary)] uppercase">{t('employees.detail.articles')}</div>
 </div>
 <div className="text-center">
 <div className="text-lg font-mono font-extrabold text-content">{s.articles_per_hour || 0}</div>
 <div className="text-[9px] text-[var(--text-secondary)] uppercase">{t('employees.detail.art_per_h')}</div>
 </div>
 </div>

 <div className="space-y-1.5 text-xs">
 <div className="flex justify-between">
 <span className="text-[var(--text-secondary)]">{t('employees.detail.total_picks')}</span>
 <span className="font-mono text-content-secondary">{(s.picks || 0).toLocaleString(locale)}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-[var(--text-secondary)]">{t('employees.detail.total_skus')}</span>
 <span className="font-mono text-content-secondary">{(s.skus || 0).toLocaleString(locale)}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-[var(--text-secondary)]">{t('employees.detail.avg_duration_wave')}</span>
 <span className="font-mono text-content-secondary">{fmtDur(s.avg_duration)}</span>
 </div>
 </div>

 {data.merchants && data.merchants.length > 0 && (
 <div className="mt-3 pt-3 border-t border-line">
 <div className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider mb-1.5">{t('employees.detail.top_customers')}</div>
 {data.merchants.slice(0, 5).map((m, i) => (
 <div key={i} className="flex items-center justify-between text-xs py-0.5">
 <span className="text-content-secondary truncate mr-2">{m.name}</span>
 <span className="font-mono text-[var(--text-tertiary)] shrink-0">{(m.articles || 0).toLocaleString(locale)} Art.</span>
 </div>
 ))}
 </div>
 )}

 {data.daily && data.daily.length >= 2 && (
 <div className="mt-3 pt-3 border-t border-line">
 <div className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider mb-1.5">{t('employees.detail.articles_per_day')}</div>
 <div className="flex items-end gap-px h-10">
 {data.daily.map((d, i) => {
 const maxA = Math.max(...data.daily.map(x => x.articles || 0), 1);
 const h = Math.max(2, ((d.articles || 0) / maxA) * 40);
 return (
 <div key={i} className="flex-1 rounded-t bg-emerald-500/40" style={{ height: `${h}px` }}
 title={`${d.datum}: ${d.articles} ${t('employees.detail.articles')}`} />
 );
 })}
 </div>
 </div>
 )}
 </>
 )}
 </Card>
 );
}

// ═══ KARTE: Effektive Arbeitszeit ═══
function EffektiveArbeitszeitCard({ id, maName }) {
 const { t } = useLanguage();
 const [data, setData] = useState(null);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 const now = new Date();
 const von = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-01`;
 const bis = dateStr(now);
 api(`/api/mitarbeiter/${id}/effektive-arbeitszeit?von=${von}&bis=${bis}`)
 .then(d => { if (!d.error) setData(d); else setData({ error: d.error }); })
 .finally(() => setLoading(false));
 }, [id]);

 const isShared = /^(outbound|inbound|lager)_/i.test(maName || '');

 const pctColor = (pct) => {
 if (pct >= 80) return '#34d399';
 if (pct >= 60) return '#eab308';
 if (pct >= 40) return '#f97316';
 return '#ef4444';
 };

 const interpretation = (pct) => {
 if (pct >= 80) return { label: t('employees.detail.very_high'), color: 'bg-emerald-500/15 text-emerald-400' };
 if (pct >= 60) return { label: t('employees.detail.normal'), color: 'bg-yellow-500/15 text-yellow-400' };
 if (pct >= 40) return { label: t('employees.detail.below_average'), color: 'bg-orange-500/15 text-orange-400' };
 return { label: t('employees.detail.low'), color: 'bg-red-500/15 text-red-400' };
 };

 // Group days by KW for weekly trend
 const weeklyTrend = useMemo(() => {
 if (!data?.details_pro_tag?.length) return [];
 const weeks = {};
 for (const d of data.details_pro_tag) {
 const dt = new Date(d.datum + 'T12:00:00');
 const yearStart = new Date(dt.getFullYear(), 0, 1);
 const kw = Math.ceil(((dt - yearStart) / 86400000 + yearStart.getDay() + 1) / 7);
 if (!weeks[kw]) weeks[kw] = { kw, anwesenheit: 0, produktiv: 0 };
 weeks[kw].anwesenheit += d.anwesenheit || 0;
 weeks[kw].produktiv += d.produktiv || 0;
 }
 return Object.values(weeks)
 .map(w => ({ ...w, pct: w.anwesenheit > 0 ? Math.round((w.produktiv / w.anwesenheit) * 1000) / 10 : 0 }))
 .sort((a, b) => a.kw - b.kw);
 }, [data]);

 return (
 <Card title={t('employees.detail.effective_title')} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>}>
 {loading ? (
 <div className="text-xs text-[var(--text-tertiary)] py-4 text-center">{t('employees.detail.loading_productivity')}</div>
 ) : isShared ? (
 <div className="text-xs text-[var(--text-tertiary)] py-4 text-center">{t('employees.detail.shared_account')}</div>
 ) : data?.error ? (
 <div className="text-xs text-[var(--text-tertiary)] py-4 text-center">{data.error}</div>
 ) : !data || data.anwesenheit_minuten === 0 ? (
 <div className="py-4">
 <div className="text-xs text-amber-400 text-center mb-1">{t('employees.detail.no_attendance_data')}</div>
 <div className="text-[10px] text-[var(--text-tertiary)] text-center">{t('employees.detail.no_stempel_data')}</div>
 </div>
 ) : (
 <>
 {/* Big percentage */}
 <div className="flex items-center gap-3 mb-4">
 <div className="text-3xl font-mono font-extrabold" style={{ color: pctColor(data.effektiv_prozent) }}>
 {data.effektiv_prozent.toFixed(1)}%
 </div>
 <div className="flex-1">
 <div className="h-3 bg-surface-inset rounded-full overflow-hidden mb-1">
 <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, data.effektiv_prozent)}%`, backgroundColor: pctColor(data.effektiv_prozent) }} />
 </div>
 <div className="flex items-center justify-between">
 <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${interpretation(data.effektiv_prozent).color}`}>
 {interpretation(data.effektiv_prozent).label}
 </span>
 <span className="text-[9px] text-[var(--text-tertiary)]">{t('employees.detail.healthy_range')}</span>
 </div>
 </div>
 </div>

 {/* Breakdown */}
 <div className="space-y-1.5 mb-3">
 {(() => {
 const anw = data.anwesenheit_minuten;
 const pickPct = anw > 0 ? Math.round((data.pick_minuten / anw) * 1000) / 10 : 0;
 const packPct = anw > 0 ? Math.round((data.pack_minuten / anw) * 1000) / 10 : 0;
 const sonstigeMin = anw - data.produktiv_minuten;
 const sonstigePct = anw > 0 ? Math.round((sonstigeMin / anw) * 1000) / 10 : 0;
 const fmtMin = (m) => {
 const h = Math.floor(m / 60);
 const min = Math.round(m % 60);
 return h > 0 ? `${h}h ${min}min` : `${min}min`;
 };
 return (
 <>
 <div className="flex items-center justify-between text-xs">
 <div className="flex items-center gap-2">
 <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />
 <span className="text-content-secondary">Pick</span>
 </div>
 <span className="font-mono text-[var(--text-secondary)]">{fmtMin(data.pick_minuten)} ({pickPct}%)</span>
 </div>
 <div className="flex items-center justify-between text-xs">
 <div className="flex items-center gap-2">
 <span className="w-2.5 h-2.5 rounded-sm bg-pink-500" />
 <span className="text-content-secondary">Pack</span>
 </div>
 <span className="font-mono text-[var(--text-secondary)]">{fmtMin(data.pack_minuten)} ({packPct}%)</span>
 </div>
 <div className="flex items-center justify-between text-xs">
 <div className="flex items-center gap-2">
 <span className="w-2.5 h-2.5 rounded-sm bg-[var(--text-tertiary,#6B7280)]" />
 <span className="text-content-secondary">{t('employees.detail.other_label')}</span>
 </div>
 <span className="font-mono text-[var(--text-secondary)]">{fmtMin(Math.max(0, sonstigeMin))} ({sonstigePct}%)</span>
 </div>
 {/* Stacked bar */}
 <div className="h-2 bg-surface-inset rounded-full overflow-hidden flex mt-1">
 <div className="h-full bg-emerald-500" style={{ width: `${pickPct}%` }} />
 <div className="h-full bg-pink-500" style={{ width: `${packPct}%` }} />
 </div>
 </>
 );
 })()}
 </div>

 {/* Totals */}
 <div className="flex items-center justify-between text-[10px] text-[var(--text-tertiary)] border-t border-line pt-2 mb-3">
 <span>{t('employees.detail.attendance_time')}: {Math.round(data.anwesenheit_minuten / 60)}h {Math.round(data.anwesenheit_minuten % 60)}min</span>
 <span>{t('employees.detail.productive_time')}: {Math.round(data.produktiv_minuten / 60)}h {Math.round(data.produktiv_minuten % 60)}min</span>
 </div>

 {/* Weekly trend */}
 {weeklyTrend.length >= 2 && (
 <div className="border-t border-line pt-3">
 <div className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider mb-2">{t('employees.detail.weekly_trend')}</div>
 <div className="flex items-end gap-1 h-10">
 {weeklyTrend.map((w, i) => {
 const h = Math.max(3, (w.pct / 100) * 40);
 return (
 <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
 <div className="w-full rounded-t" style={{ height: `${h}px`, backgroundColor: pctColor(w.pct) }}
 title={`KW${w.kw}: ${w.pct}%`} />
 <span className="text-[8px] text-[var(--text-tertiary)]">{w.kw}</span>
 </div>
 );
 })}
 </div>
 </div>
 )}
 </>
 )}
 </Card>
 );
}

// ═══ APP-ZUGANG CARD ═══
function AppZugangCard({ id }) {
 const { t, locale } = useLanguage();
 const [data, setData] = useState(null);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 fetch(`/api/mitarbeiter/${id}/app-zugang`, { credentials: 'include' })
 .then(r => r.json())
 .then(d => setData(d))
 .catch(() => {})
 .finally(() => setLoading(false));
 }, [id]);

 if (loading) return (
 <Card title={t('employees.detail.app_access')} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>}>
 <div className="flex justify-center py-4"><div className="animate-spin h-5 w-5 border-2 border-[#338edc] border-t-transparent rounded-full" /></div>
 </Card>
 );

 const u = data?.user;
 const invite = data?.pending_invite;

 return (
 <Card title={t('employees.detail.app_access')} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>}>
 {u ? (
 <div className="space-y-1">
 <div className="flex items-center gap-3 mb-3">
 <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#338edc] to-purple-600 flex items-center justify-center flex-shrink-0">
 <span className="text-[10px] font-bold text-white">{(u.vorname?.[0] || '') + (u.nachname?.[0] || '')}</span>
 </div>
 <div>
 <div className="text-sm font-medium text-content">{u.vorname} {u.nachname}</div>
 <div className="text-[10px] text-[var(--text-secondary)]">{u.email}</div>
 </div>
 </div>
 <InfoRow label={t('employees.detail.role')} value={
 <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${
 { admin: 'text-red-400 bg-red-500/10', manager: 'text-primary-400 bg-primary-500/10', mitarbeiter: 'text-emerald-400 bg-emerald-500/10' }[u.rolle] || 'text-[var(--text-tertiary,#6B7280)] bg-[rgba(107,114,128,0.1)]'
 }`}>{u.rolle}</span>
 } />
 <InfoRow label={t('employees.detail.status')} value={
 <span className={`inline-flex items-center gap-1 text-xs ${u.aktiv ? 'text-emerald-400' : 'text-red-400'}`}>
 <span className={`w-1.5 h-1.5 rounded-full ${u.aktiv ? 'bg-emerald-400' : 'bg-red-400'}`} />
 {u.aktiv ? t('employees.detail.active') : t('employees.detail.deactivated')}
 </span>
 } />
 <InfoRow label={t('employees.detail.last_login')} value={u.letzter_login ? new Date(u.letzter_login).toLocaleString(locale, { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : t('employees.detail.never')} />
 </div>
 ) : invite ? (
 <div>
 <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-500/10 text-yellow-400 text-[10px] font-medium mb-3">
 <span className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
 {t('employees.detail.invite_pending')}
 </div>
 <InfoRow label={t('employees.detail.email_label')} value={invite.email} />
 <InfoRow label={t('employees.detail.role')} value={invite.rolle} />
 <InfoRow label={t('employees.detail.expires')} value={new Date(invite.laeuft_ab_am).toLocaleDateString(locale)} />
 </div>
 ) : (
 <EmptyState
 variant="compact"
 icon={<UserX className="w-5 h-5" strokeWidth={1.5} />}
 title={t('employees.detail.no_app_access', 'Kein App-Zugang')}
 description={t('employees.detail.no_app_access_hint', 'Diese/r MA hat noch keinen User-Account für die WeShip-App.')}
 ctaLabel={t('employees.detail.link_in_user_mgmt', 'In Benutzerverwaltung anlegen')}
 ctaHref="/admin/users"
 />
 )}
 </Card>
 );
}

// ═══ STAMMDATEN TAB ═══
function MaskedField({ label, value, mono = false }) {
 const [visible, setVisible] = useState(false);
 const { t } = useLanguage();
 if (!value) return <InfoRow label={label} value="–" mono={mono} />;

 const masked = mono && value.length > 4
 ? '****' + value.slice(-4)
 : value;

 return (
 <div className="flex justify-between items-center py-1.5 border-b border-line/50 last:border-0">
 <span className="text-xs text-[var(--text-secondary)]">{label}</span>
 <div className="flex items-center gap-1.5">
 <span className={`text-sm text-content-secondary ${mono ? 'font-mono' : ''}`}>
 {visible ? value : masked}
 </span>
 <button
 onClick={() => setVisible(!visible)}
 className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] p-0.5"
 title={visible ? t('employees.detail.stammdaten_hide') : t('employees.detail.stammdaten_show')}
 >
 {visible ? (
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
 ) : (
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
 )}
 </button>
 </div>
 </div>
 );
}

// #874 — Shared Save-Context: EditableField meldet failed saves zurück,
// StammdatenTab zeigt Banner + Retry-Button + beforeunload-Warnung.
const StammdatenSaveCtx = createContext(null);

// #882 (v2.379.0) — FE-Regex (synchron mit Backend in mitarbeiter.js).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[\+\d\s\-\(\)\/]{5,30}$/;

// IBAN Mod-97 (ISO 13616) — doppelter Check FE/BE: FE gibt instant Feedback.
function ibanMod97(iban) {
 const clean = String(iban).replace(/\s/g, '').toUpperCase();
 if (!/^[A-Z]{2}\d{2}[A-Z0-9]{4,30}$/.test(clean)) return false;
 const rearranged = clean.slice(4) + clean.slice(0, 4);
 let numeric = '';
 for (const ch of rearranged) numeric += /[A-Z]/.test(ch) ? (ch.charCodeAt(0) - 55).toString() : ch;
 let remainder = 0;
 for (const digit of numeric) remainder = (remainder * 10 + parseInt(digit, 10)) % 97;
 return remainder === 1;
}

// Client-side Validation vor dem Save — verhindert unnötigen Backend-Roundtrip.
// Liefert null wenn OK, sonst Error-String (i18n-fähig über t-Key-Resolver).
function validateField(type, field, rawVal, t) {
 if (rawVal == null || rawVal === '') return null; // leer = OK
 const v = String(rawVal).trim();
 if (type === 'email' && !EMAIL_RE.test(v)) return t('employees.detail.val_email', 'E-Mail-Format ungültig');
 if (type === 'tel' && !PHONE_RE.test(v)) return t('employees.detail.val_phone', 'Telefon-Format ungültig (5–30 Zeichen)');
 if (field === 'iban') {
 const clean = v.replace(/\s/g, '').toUpperCase();
 if (!/^[A-Z]{2}\d{2}[A-Z0-9]{4,30}$/.test(clean)) return t('employees.detail.val_iban_format', 'IBAN-Format ungültig');
 if (!ibanMod97(clean)) return t('employees.detail.val_iban_checksum', 'IBAN-Prüfsumme ungültig');
 }
 return null;
}

function parseFieldValue(type, v) {
 if (type === 'number') return v === '' ? null : parseFloat(String(v).replace(',', '.'));
 if (type === 'email' || type === 'tel') return v === '' ? null : String(v).trim();
 return v === '' ? null : (v ?? null);
}

function ProbemonatBadge({ eintritt, austritt, poolAccount }) {
 const { t } = useLanguage();
 if (poolAccount || !eintritt || austritt) return null;
 const eintrittStr = String(eintritt).slice(0, 10);
 const eintrittDate = new Date(eintrittStr + 'T00:00:00');
 if (isNaN(eintrittDate.getTime())) return null;
 const ende = new Date(eintrittDate);
 ende.setMonth(ende.getMonth() + 1);
 const today = new Date();
 today.setHours(0, 0, 0, 0);
 const tageBis = Math.floor((ende.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
 if (tageBis < -7) return null;
 let color = 'green';
 let text;
 if (tageBis < 0) {
  color = 'red';
  text = t('probemonat.abgelaufen', 'Probemonat abgelaufen');
 } else if (tageBis === 0) {
  color = 'red';
  text = t('probemonat.heute', 'Probemonat endet heute');
 } else if (tageBis < 3) {
  color = 'red';
  text = t('probemonat.noch_tage', 'Probemonat noch {{n}} Tage').replace('{{n}}', tageBis);
 } else if (tageBis <= 7) {
  color = 'yellow';
  text = t('probemonat.noch_tage', 'Probemonat noch {{n}} Tage').replace('{{n}}', tageBis);
 } else {
  color = 'green';
  text = t('probemonat.noch_tage', 'Probemonat noch {{n}} Tage').replace('{{n}}', tageBis);
 }
 const bgMap = { green: 'bg-emerald-50 border-emerald-200 text-emerald-700', yellow: 'bg-amber-50 border-amber-200 text-amber-700', red: 'bg-rose-50 border-rose-200 text-rose-700' };
 return (
  <div className="flex items-center justify-end py-1.5 border-b border-line/40">
   <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg border text-[11px] font-medium ${bgMap[color]}`}>
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
     <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
    {text}
   </span>
  </div>
 );
}

// #1705 — Urlaubssaldo zum Austritt (Berechnung auf Read, keine Mutation).
// Rendert in der Austrittsdaten-Card, sobald ein Austrittsdatum gesetzt ist.
function AustrittUrlaubsSaldo({ maId, austrittDatum }) {
 const { t, locale } = useLanguage();
 const [data, setData] = useState(null);
 const [error, setError] = useState(false);

 useEffect(() => {
 if (!maId || !austrittDatum) { setData(null); return; }
 let cancelled = false;
 setData(null); setError(false);
 fetch(`/api/mitarbeiter/${maId}/urlaub-austritt`, { credentials: 'include' })
 .then((r) => r.json())
 .then((d) => { if (!cancelled) { if (d && !d.error && d.austritt_datum) setData(d); else setError(true); } })
 .catch(() => { if (!cancelled) setError(true); });
 return () => { cancelled = true; };
 }, [maId, austrittDatum]);

 if (!austrittDatum) return null;
 const fmtTage = (v) => `${Number(v).toLocaleString(locale === 'de' ? 'de-AT' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${t('austritt.saldo_tage_unit', 'Tage')}`;

 const Row = ({ label, value, strong }) => (
 <div className="flex justify-between items-center py-1.5 border-b border-line/50">
 <span className="text-xs text-[var(--text-secondary)]">{label}</span>
 <span className={`text-sm font-mono ${strong ? 'font-semibold text-[var(--text-primary)]' : 'text-content-secondary'}`}>{value}</span>
 </div>
 );

 return (
 <div className="mt-3 pt-3 border-t border-line/50">
 <div className="text-xs font-medium text-[var(--text-secondary)] mb-1">
 {t('austritt.saldo_title', 'Urlaubssaldo zum Austritt')}
 </div>
 <div className="text-[11px] text-[var(--text-tertiary)] mb-2">
 {t('austritt.saldo_hint', 'Automatisch berechnet bis zum Austrittsdatum (individuelles Urlaubsjahr, monatlicher Aufbau) — Grundlage für die Urlaubsabrechnung. Keine Buchung, reine Anzeige.')}
 </div>
 {error && <div className="text-xs text-rose-600">{t('austritt.saldo_error', 'Saldo konnte nicht berechnet werden.')}</div>}
 {!error && !data && <div className="text-xs text-[var(--text-muted)]">{t('common.loading', 'Lädt…')}</div>}
 {data && (
 <>
 {data.startsaldo != null && (
 <Row label={`${t('austritt.saldo_startsaldo', 'Startsaldo')} (${fmtDate(data.startsaldo_datum, locale)})`} value={fmtTage(data.startsaldo)} />
 )}
 {data.startsaldo == null && Number(data.uebertrag || 0) !== 0 && (
 <Row label={t('austritt.saldo_uebertrag', 'Übertrag/Buchungen')} value={`+ ${fmtTage(data.uebertrag)}`} />
 )}
 <Row label={t('austritt.saldo_aufgebaut', 'Aufgebaut bis Austritt')} value={`+ ${fmtTage(data.aufgebaut_bis_austritt)}`} />
 <Row label={t('austritt.saldo_genommen', 'Genommen (bis heute)')} value={`− ${fmtTage(data.genommen_bis_heute)}`} />
 <Row label={t('austritt.saldo_geplant', 'Geplant (bis Austritt)')} value={`− ${fmtTage(data.geplant_bis_austritt)}`} />
 <Row label={`${t('austritt.saldo_result', 'Saldo zum Austritt')} (${fmtDate(data.austritt_datum, locale)})`} value={fmtTage(data.saldo)} strong />
 </>
 )}
 </div>
 );
}

// #1274 — FlowAI-Orientierungstags (Chips + Autocomplete). Additiv, eigenes Feld.
function FlowAITagsField({ ma, setMa, canEdit }) {
 const { t } = useLanguage();
 const tags = Array.isArray(ma.flowai_tags) ? ma.flowai_tags : [];
 const [input, setInput] = useState('');
 const [suggestions, setSuggestions] = useState([]);
 const [saving, setSaving] = useState(false);
 const [errorMsg, setErrorMsg] = useState(null);

 useEffect(() => {
 fetch('/api/mitarbeiter/flowai-tags', { credentials: 'include' })
 .then((r) => r.json())
 .then((d) => { if (Array.isArray(d.tags)) setSuggestions(d.tags); })
 .catch(() => {});
 }, []);

 const persist = (nextTags) => {
 setSaving(true);
 setErrorMsg(null);
 fetch(`/api/mitarbeiter/${ma.id}/stammdaten`, {
 method: 'PUT', headers: { 'Content-Type': 'application/json' },
 credentials: 'include', body: JSON.stringify({ flowai_tags: nextTags }),
 }).then(async (r) => {
 const d = await r.json().catch(() => ({ error: `HTTP ${r.status}` }));
 if (r.ok && !d.error) {
 const saved = Array.isArray(d.flowai_tags) ? d.flowai_tags : nextTags;
 setMa((prev) => ({ ...prev, flowai_tags: saved }));
 setSuggestions((prev) => [...new Set([...prev, ...saved])].sort());
 } else {
 setErrorMsg(d.error || `HTTP ${r.status}`);
 }
 }).catch((err) => setErrorMsg(String(err?.message || err || 'Netzwerkfehler')))
 .finally(() => setSaving(false));
 };

 const addTag = (raw) => {
 const tag = String(raw || '').trim();
 if (!tag) return;
 if (tags.some((x) => x.toLowerCase() === tag.toLowerCase())) { setInput(''); return; }
 persist([...tags, tag]);
 setInput('');
 };

 const removeTag = (tag) => persist(tags.filter((x) => x !== tag));

 const handleKeyDown = (e) => {
 if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(input); }
 if (e.key === 'Backspace' && !input && tags.length) removeTag(tags[tags.length - 1]);
 };

 const listId = `flowai-tags-suggest-${ma.id}`;
 const available = suggestions.filter((s) => !tags.some((x) => x.toLowerCase() === s.toLowerCase()));

 return (
 <div className="py-1.5">
 <div className="text-xs text-[var(--text-secondary)] mb-2">
 {t('employees.detail.flowaiTagsHint', 'Diese Tags geben FlowAI Orientierung (z. B. Rolle, Zuständigkeit, Standort). FlowAI nutzt sie später, um Aufgaben richtig zuzuordnen.')}
 </div>
 <div className="flex flex-wrap gap-1.5 mb-2">
 {tags.length === 0 && (
 <span className="text-sm text-[var(--text-muted)]">{t('employees.detail.flowaiTagsEmpty', 'Keine Tags')}</span>
 )}
 {tags.map((tag) => (
 <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)] text-[var(--text-primary)]">
 {tag}
 {canEdit && (
 <button type="button" onClick={() => removeTag(tag)} disabled={saving}
 className="text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-50" aria-label={`${tag} entfernen`}>×</button>
 )}
 </span>
 ))}
 </div>
 {canEdit && (
 <>
 <input
 type="text" value={input} list={listId}
 onChange={(e) => setInput(e.target.value)}
 onKeyDown={handleKeyDown}
 onBlur={() => addTag(input)}
 disabled={saving}
 placeholder={t('employees.detail.flowaiTagsPlaceholder', 'Tag eingeben + Enter…')}
 className="w-full text-sm px-2 py-1 rounded-lg bg-white border border-[var(--border-default,#E5E7EB)] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc] disabled:opacity-50"
 />
 <datalist id={listId}>
 {available.map((s) => <option key={s} value={s} />)}
 </datalist>
 </>
 )}
 {errorMsg && <div className="text-xs text-red-700 mt-1">{errorMsg}</div>}
 </div>
 );
}

function EditableField({ label, field, value, ma, setMa, canEdit, type = 'text', options = null, placeholder = null, masked = false, mono = false, hint = null, readOnly = false, readOnlyNote = null, rows = 4 }) {
 const { t } = useLanguage();
 const saveCtx = useContext(StammdatenSaveCtx);
 const [editing, setEditing] = useState(false);
 const [editVal, setEditVal] = useState('');
 const [saving, setSaving] = useState(false);
 const [saved, setSaved] = useState(false);
 const [errorMsg, setErrorMsg] = useState(null);
 const [maskVisible, setMaskVisible] = useState(false);

 const save = (val) => {
 const v = val !== undefined ? val : editVal;
 const origVal = type === 'date' ? (value ? String(value).slice(0, 10) : '') : String(value ?? '');
 if (v === origVal) { setEditing(false); return; }
 // #882: FE-Validation vor Backend-Call
 const feErr = validateField(type, field, v, t);
 if (feErr) {
 setErrorMsg(feErr);
 saveCtx?.markPending(field, parseFieldValue(type, v), feErr);
 return;
 }
 setSaving(true);
 setErrorMsg(null);
 const parsed = parseFieldValue(type, v);
 const payload = { [field]: parsed };
 fetch(`/api/mitarbeiter/${ma.id}/stammdaten`, {
 method: 'PUT', headers: { 'Content-Type': 'application/json' },
 credentials: 'include', body: JSON.stringify(payload),
 }).then(async r => {
 const d = await r.json().catch(() => ({ error: `HTTP ${r.status}` }));
 if (r.ok && !d.error) {
 setMa(prev => {
 const next = { ...prev, [field]: d[field] };
 // #873 — name wird backend-seitig aus vorname+nachname rekomponiert
 if ((field === 'vorname' || field === 'nachname') && d.name != null) next.name = d.name;
 return next;
 });
 setEditing(false);
 setSaved(true);
 setTimeout(() => setSaved(false), 1500);
 saveCtx?.clearPending(field);
 } else {
 const msg = d.error || `HTTP ${r.status}`;
 setErrorMsg(msg);
 saveCtx?.markPending(field, parsed, msg);
 // editing bleibt offen, damit der User sieht was er eingegeben hat
 }
 }).catch(err => {
 const msg = String(err?.message || err || 'Netzwerkfehler');
 setErrorMsg(msg);
 saveCtx?.markPending(field, parsed, msg);
 }).finally(() => setSaving(false));
 };

 const handleKeyDown = (e) => {
 if (e.key === 'Enter') { e.preventDefault(); save(); }
 if (e.key === 'Escape') { e.preventDefault(); setEditing(false); }
 };

 const handleBlur = () => {
 if (!saving) save();
 };

 if (editing && canEdit && !readOnly) {
 const inputCls = `bg-[var(--bg-input,#fff)] border ${errorMsg ? 'border-red-500' : 'border-[var(--border-input,#E5E7EB)] focus:border-[#338edc]'} rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none w-full ${mono ? 'font-mono' : ''}`;
 // #882: Textarea sprengt die 60%-Spalte, kriegt volle Breite.
 const isTextarea = type === 'textarea';
 const containerCls = isTextarea ? 'flex flex-col gap-1.5' : 'flex justify-between items-center';
 const fieldWrapCls = isTextarea ? 'w-full' : 'flex-1 max-w-[60%]';
 const htmlInputType = type === 'email' ? 'email' : type === 'tel' ? 'tel' : 'text';
 return (
 <div className="py-1.5 border-b border-line/50 last:border-0">
 <div className={containerCls}>
 <span className="text-xs text-[var(--text-secondary)] shrink-0 mr-3">{label}</span>
 <div className={fieldWrapCls}>
 {options ? (
 <select value={editVal} onChange={e => { setEditVal(e.target.value); save(e.target.value); }}
 onBlur={() => setEditing(false)} onKeyDown={handleKeyDown} autoFocus
 className={inputCls}>
 <option value="">–</option>
 {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
 </select>
 ) : type === 'date' ? (
 <input type="date" value={editVal} onChange={e => setEditVal(e.target.value)}
 onBlur={handleBlur} onKeyDown={handleKeyDown} autoFocus className={inputCls} />
 ) : isTextarea ? (
 <textarea value={editVal} onChange={e => setEditVal(e.target.value)}
 onBlur={handleBlur}
 onKeyDown={e => { if (e.key === 'Escape') { e.preventDefault(); setEditing(false); } }}
 autoFocus rows={rows} placeholder={placeholder || ''}
 className={`${inputCls} resize-y min-h-[80px]`} />
 ) : (
 <input type={htmlInputType} value={editVal} onChange={e => setEditVal(e.target.value)}
 onBlur={handleBlur} onKeyDown={handleKeyDown} autoFocus
 placeholder={placeholder || ''}
 onFocus={e => e.target.select()} className={inputCls} />
 )}
 </div>
 </div>
 {hint && !errorMsg && (
 <div className="text-[11px] text-[var(--text-tertiary)] mt-1">{hint}</div>
 )}
 {errorMsg && (
 <div className="text-[11px] text-red-600 mt-1">⚠ {errorMsg}</div>
 )}
 </div>
 );
 }

 const displayVal = options
 ? (options.find(o => o.value === value)?.label || value || null)
 : type === 'date' ? (value ? fmtDate(value) : null) : (value != null && value !== '' ? value : null);
 const isEmpty = displayVal == null || displayVal === '–';
 const startEditing = () => {
 if (readOnly || !canEdit) return;
 setEditVal(type === 'date' ? (value ? String(value).slice(0, 10) : '') : String(value ?? ''));
 setEditing(true);
 };
 const hasPending = !!saveCtx?.pending?.[field];
 const isInteractive = canEdit && !readOnly;

 // #881: Mask mono values (e.g. SVNR) when not explicitly revealed
 const valStr = displayVal != null ? String(displayVal) : '';
 const shownVal = masked && !maskVisible && valStr.length > 4
 ? '****' + valStr.slice(-4)
 : valStr;

 return (
 <div
 className={`flex justify-between items-center py-1.5 border-b border-line/50 last:border-0 transition-all rounded-sm ${saved ? 'bg-green-50 dark:bg-green-900/10' : ''} ${hasPending ? 'bg-red-50/60 dark:bg-red-900/10' : ''} ${isInteractive ? 'cursor-pointer hover:bg-[var(--bg-card-hover,var(--bg-secondary))]' : ''}`}
 data-field={field}
 onClick={isInteractive ? startEditing : undefined}
 title={readOnly && readOnlyNote ? readOnlyNote : undefined}
 >
 <span className="text-xs text-[var(--text-secondary)] flex items-center gap-1.5">
 {label}
 {readOnly && readOnlyNote && (
 <span className="text-[9px] font-bold uppercase tracking-wider text-amber-600 border border-amber-300 rounded px-1 py-[1px]">DEPRECATED</span>
 )}
 </span>
 <div className="flex items-center gap-1.5">
 {saved && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
 {hasPending && <span className="text-[10px] font-medium text-red-600" title={saveCtx.pending[field].error}>⚠ Nicht gespeichert</span>}
 {isEmpty && isInteractive ? (
 <span className="text-sm text-[var(--text-muted)] italic">{t('employees.detail.click_to_enter')}</span>
 ) : (
 <span className={`text-sm text-content-secondary ${mono ? 'font-mono' : ''}`}>
 {type === 'number' && value != null ? fmtNum(value, 2) : (masked ? (shownVal || '–') : (displayVal || '–'))}
 </span>
 )}
 {masked && !isEmpty && (
 <button
 type="button"
 onClick={e => { e.stopPropagation(); setMaskVisible(v => !v); }}
 className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] p-0.5"
 title={maskVisible ? 'Verbergen' : 'Anzeigen'}
 >
 {maskVisible ? (
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
 ) : (
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
 )}
 </button>
 )}
 </div>
 </div>
 );
}

// ═══════════════════════════════════════════════════════════════
// DokumenteTab — Einheitliches Dokumenten-Center
// ═══════════════════════════════════════════════════════════════

const DOC_CATEGORIES = [
 { key: 'vertraege', icon: '📋', labelKey: 'mitarbeiter.dokumente.kat_vertraege' },
 { key: 'lohnzettel', icon: '💰', labelKey: 'mitarbeiter.dokumente.kat.lohnzettel' },
 { key: 'arztbestaetigung', icon: '🏥', labelKey: 'mitarbeiter.dokumente.kat_arztbestaetigung' },
 { key: 'arbeitserlaubnis', icon: '📑', labelKey: 'mitarbeiter.dokumente.kat_arbeitserlaubnis' },
 { key: 'sonstige', icon: '📄', labelKey: 'mitarbeiter.dokumente.kat_sonstige' },
];

function DokumenteTab({ maId, ma }) {
 const { t, locale } = useLanguage();
 const [activeCat, setActiveCat] = useState('vertraege');
 const [docs, setDocs] = useState([]);
 const [arztnachweise, setArztnachweise] = useState([]); // #1878: Portal-Uploads aus urlaub_antraege (read-only)
 const [vertraege, setVertraege] = useState([]);
 const [digitalVertraege, setDigitalVertraege] = useState([]);
 const [loading, setLoading] = useState(true);
 const [uploading, setUploading] = useState(false);

 const load = useCallback(() => {
 setLoading(true);
 Promise.all([
 api(`/api/mitarbeiter/${maId}/dokumente`),
 api(`/api/dienstvertraege/ma/${maId}`),
 fetch(`/api/vertraege?mitarbeiter_id=${maId}`, { credentials: 'include' }).then(r => r.json()).catch(() => ({ vertraege: [] })),
 // #1878: 403 für Nicht-HR darf den Tab nicht leeren → leere Liste statt Fehler
 fetch(`/api/mitarbeiter/${maId}/arztnachweise`, { credentials: 'include' }).then(r => r.ok ? r.json() : { arztnachweise: [] }).catch(() => ({ arztnachweise: [] })),
 ]).then(([dokRes, dvRes, digRes, anRes]) => {
 if (dokRes.dokumente) setDocs(dokRes.dokumente);
 if (dvRes.vertraege) setVertraege(dvRes.vertraege);
 setDigitalVertraege(digRes.vertraege || []);
 setArztnachweise(Array.isArray(anRes?.arztnachweise) ? anRes.arztnachweise : []);
 }).finally(() => setLoading(false));
 }, [maId]);
 useEffect(load, [load]);

 const handleUpload = async (e, kategorie) => {
 const file = e.target.files?.[0];
 if (!file) return;
 setUploading(true);
 const fd = new FormData();
 fd.append('file', file);
 fd.append('titel', file.name);
 fd.append('kategorie', kategorie || 'sonstiges');
 try {
 const res = await fetch(`/api/mitarbeiter/${maId}/dokumente`, { method: 'POST', body: fd, credentials: 'include' });
 if (res.ok) load();
 } finally { setUploading(false); }
 };

 const handleDelete = async (dokId) => {
 await fetch(`/api/mitarbeiter/${maId}/dokumente/${dokId}`, { method: 'DELETE', credentials: 'include' });
 load();
 };

 const handleDownload = (dokId, filename) => {
 const a = document.createElement('a');
 a.href = `/api/mitarbeiter/${maId}/dokumente/${dokId}`;
 a.download = filename || 'download';
 a.click();
 };

 const fmtSz = (bytes) => {
 if (!bytes) return '';
 if (bytes < 1024) return `${bytes} B`;
 if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
 return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
 };

 const fmtDt = (d) => d ? new Date(d).toLocaleDateString('de-AT') : '–';

 // Categorize docs
 const lohnzettelDocs = docs.filter(d => d.kategorie === 'lohnzettel');
 const arztDocs = docs.filter(d => ['bestaetigung', 'krankmeldung'].includes(d.kategorie));
 const sonstigeDocs = docs.filter(d => !['lohnzettel', 'bestaetigung', 'krankmeldung', 'arbeitsvertrag', 'zusatzvereinbarung'].includes(d.kategorie));
 const hasArbeitserlaubnis = !!ma?.arbeitserlaubnis_datei;

 const counts = {
 vertraege: vertraege.length + digitalVertraege.length,
 lohnzettel: lohnzettelDocs.length,
 arztbestaetigung: arztDocs.length + arztnachweise.length,
 arbeitserlaubnis: hasArbeitserlaubnis ? 1 : 0,
 sonstige: sonstigeDocs.length,
 };

 const statusBadge = (status) => {
 const map = {
 'Laufend': 'bg-green-100 text-green-700',
 'Entwurf': 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary,#6B7280)]',
 'Abgeschlossen': 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary,#6B7280)]',
 'Storniert': 'bg-red-100 text-red-500',
 'entwurf': 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary,#6B7280)]',
 'gesendet': 'bg-amber-100 text-amber-700',
 'unterschrieben': 'bg-green-100 text-green-700',
 };
 return map[status] || 'bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary,#6B7280)]';
 };

 const DocRow = ({ title, date, size, quelle, onDownload, onDelete }) => (
 <div className="px-5 py-3 flex items-center gap-4 hover:bg-[var(--bg-inset,#F8F9FC)] transition">
 <div className="flex-1 min-w-0">
 <div className="text-sm font-medium text-[var(--text-primary)] truncate">{title}</div>
 <div className="text-xs text-[var(--text-tertiary)] flex gap-3 mt-0.5">
 {date && <span>{fmtDt(date)}</span>}
 {size && <span>{fmtSz(size)}</span>}
 {quelle && quelle !== 'upload' && (
 <span className="px-1.5 py-0.5 bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default)] rounded text-[10px] font-medium">{quelle}</span>
 )}
 </div>
 </div>
 {onDownload && (
 <button onClick={onDownload} className="text-[#338edc] hover:text-[#2b7bc4]" title={t('mitarbeiter.dokumente.herunterladen')}>
 <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
 </button>
 )}
 {onDelete && (
 <button onClick={onDelete} className="text-[var(--text-tertiary)] hover:text-red-500" title={t('mitarbeiter.dokumente.loeschen')}>
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
 </button>
 )}
 </div>
 );

 const UploadBtn = ({ kategorie }) => (
 <label className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#338edc] text-white text-sm font-medium cursor-pointer hover:bg-[#2b7bc4] transition ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
 {uploading ? '...' : t('mitarbeiter.dokumente.hochladen')}
 <input type="file" className="hidden" onChange={e => handleUpload(e, kategorie)} disabled={uploading} />
 </label>
 );

 const renderContent = () => {
 if (loading) return <div className="text-center py-12 text-[var(--text-tertiary)]">...</div>;

 switch (activeCat) {
 case 'vertraege':
 return (
 <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl">
 {vertraege.length === 0 && digitalVertraege.length === 0 ? (
 <div className="text-center py-8 text-[var(--text-tertiary)] text-sm">{t('mitarbeiter.dokumente.keine_vertraege', 'Keine Verträge vorhanden')}</div>
 ) : (
 <div className="divide-y divide-[var(--border-default)]">
 {vertraege.map(v => (
 <div key={`mv-${v.id}`} className="px-5 py-3 flex items-center gap-4 hover:bg-[var(--bg-inset,#F8F9FC)] transition">
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2">
 <span className="text-sm font-medium text-[var(--text-primary)] truncate">{v.bezeichnung || t('employees.detail.contract')}</span>
 <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusBadge(v.status)}`}>{v.status}</span>
 </div>
 <div className="text-xs text-[var(--text-tertiary)] mt-0.5 flex gap-3">
 <span>{fmtDt(v.start_datum)}{v.end_datum ? ` – ${fmtDt(v.end_datum)}` : ` – ${t('employees.detail.ongoing', 'laufend')}`}</span>
 {v.stunden_pro_woche && <span>{v.stunden_pro_woche}h/{t('employees.detail.week_short', 'Wo')}</span>}
 {v.brutto_monatlich && <span className="font-mono">{Number(v.brutto_monatlich).toLocaleString('de-DE')} EUR</span>}
 </div>
 </div>
 {v.pdf_pfad && (
 <a href={`/api/dienstvertraege/${v.id}/pdf`} target="_blank" rel="noreferrer"
 className="text-[#338edc] hover:text-[#2b7bc4] text-xs font-medium">PDF</a>
 )}
 </div>
 ))}
 {digitalVertraege.map(dv => (
 <div key={`dv-${dv.id}`} className="px-5 py-3 flex items-center gap-4 hover:bg-[var(--bg-inset,#F8F9FC)] transition">
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2">
 <span className="text-sm font-medium text-[var(--text-primary)] truncate">{dv.template_name || 'Digitaler Vertrag'}</span>
 <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusBadge(dv.status)}`}>
 {dv.status === 'unterschrieben' ? t('vertrag.unterschrieben', 'Unterschrieben') : dv.status === 'gesendet' ? t('employees.detail.dv_waiting_signature', 'Warte auf Unterschrift') : t('vertrag.entwurf', 'Entwurf')}
 </span>
 </div>
 <div className="text-xs text-[var(--text-tertiary)] mt-0.5 flex gap-3">
 <span>{fmtDt(dv.created_at)}</span>
 {dv.unterschrift_am && <span>{t('mitarbeiter.dokumente.unterschrieben_am', 'Unterschrieben am')} {fmtDt(dv.unterschrift_am)}</span>}
 </div>
 </div>
 {dv.pdf_pfad && (
 <a href={`/api/vertraege/${dv.id}/pdf`} target="_blank" rel="noreferrer"
 className="text-[#338edc] hover:text-[#2b7bc4] text-xs font-medium">PDF</a>
 )}
 </div>
 ))}
 </div>
 )}
 </div>
 );

 case 'lohnzettel':
 return (
 <div className="space-y-4">
 <UploadBtn kategorie="lohnzettel" />
 <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl">
 {lohnzettelDocs.length === 0 ? (
 <div className="text-center py-8 text-[var(--text-tertiary)] text-sm">{t('mitarbeiter.dokumente.keine')}</div>
 ) : (
 <div className="divide-y divide-[var(--border-default)]">
 {lohnzettelDocs.map(doc => (
 <DocRow key={doc.id} title={doc.titel || doc.datei_name || doc.dateiname} date={doc.gueltig_ab || doc.created_at}
 size={doc.datei_groesse} quelle={doc.quelle}
 onDownload={() => handleDownload(doc.id, doc.datei_name || doc.dateiname)}
 onDelete={() => handleDelete(doc.id)} />
 ))}
 </div>
 )}
 </div>
 </div>
 );

 case 'arztbestaetigung':
 return (
 <div className="space-y-4">
 <UploadBtn kategorie="krankmeldung" />
 {/* #1878: vom Mitarbeiter im Portal hochgeladene Krankmeldungen — gelesen aus urlaub_antraege,
     nichts migriert. Kein Löschen (die Datei gehört zum Antrag), Download authentifiziert + audit_log. */}
 {arztnachweise.length > 0 && (
 <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl" data-testid="akte-arztnachweise">
 <div className="px-5 py-2 text-xs font-semibold text-[var(--text-tertiary)] border-b border-[var(--border-default,#E5E7EB)]">
 {t('mitarbeiter.dokumente.arztnachweis_abschnitt', 'Vom Mitarbeiter hochgeladen (Portal)')}
 </div>
 <div className="divide-y divide-[var(--border-default)]">
 {arztnachweise.map(an => {
 const zeitraum = an.bis_datum
 ? `${fmtDt(an.von_datum)} – ${fmtDt(an.bis_datum)}`
 : `${t('mitarbeiter.dokumente.arztnachweis_seit', 'seit')} ${fmtDt(an.von_datum)}`;
 const statusLabel = t(`urlaub.status.${an.status}`, an.status);
 return (
 <div key={`an-${an.antrag_id}`} className="px-5 py-3 flex items-center gap-4 hover:bg-[var(--bg-inset,#F8F9FC)] transition" data-testid={`arztnachweis-${an.antrag_id}`}>
 <div className="flex-1 min-w-0">
 <div className="text-sm font-medium text-[var(--text-primary)] truncate">
 {t('mitarbeiter.dokumente.kat.krankmeldung', 'Krankmeldung')} {zeitraum}
 </div>
 <div className="text-xs text-[var(--text-tertiary)] flex flex-wrap gap-3 mt-0.5">
 <span>{t('mitarbeiter.dokumente.arztnachweis_hochgeladen', 'hochgeladen')} {fmtDt(an.hochgeladen_am)}</span>
 {an.datei_groesse ? <span>{fmtSz(an.datei_groesse)}</span> : null}
 <span className="px-1.5 py-0.5 bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default)] rounded text-[10px] font-medium">{t('mitarbeiter.dokumente.kat.krankmeldung', 'Krankmeldung')}</span>
 <span className="px-1.5 py-0.5 bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default)] rounded text-[10px] font-medium">{statusLabel}</span>
 {!an.datei_vorhanden && <span className="text-red-600">{t('mitarbeiter.dokumente.arztnachweis_datei_fehlt', 'Datei fehlt auf dem Server')}</span>}
 <Link to={`/abwesenheiten/antraege?antrag=${an.antrag_id}&src=urlaub_antrag`} className="text-[#338edc] hover:underline">
 {t('mitarbeiter.dokumente.arztnachweis_zum_antrag', 'Zum Antrag')} →
 </Link>
 </div>
 </div>
 {an.datei_vorhanden && (
 <a href={`/api/mitarbeiter/${maId}/arztnachweise/${an.antrag_id}?inline=1`} target="_blank" rel="noreferrer"
 className="text-[#338edc] hover:text-[#2b7bc4]" title={t('mitarbeiter.dokumente.herunterladen')} data-testid={`arztnachweis-download-${an.antrag_id}`}>
 <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
 </a>
 )}
 </div>
 );
 })}
 </div>
 </div>
 )}
 <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl">
 {arztDocs.length === 0 ? (
 <div className="text-center py-8 text-[var(--text-tertiary)] text-sm">{t('mitarbeiter.dokumente.keine')}</div>
 ) : (
 <div className="divide-y divide-[var(--border-default)]">
 {arztDocs.map(doc => (
 <DocRow key={doc.id} title={doc.titel || doc.datei_name || doc.dateiname} date={doc.created_at}
 size={doc.datei_groesse} quelle={doc.quelle}
 onDownload={() => handleDownload(doc.id, doc.datei_name || doc.dateiname)}
 onDelete={() => handleDelete(doc.id)} />
 ))}
 </div>
 )}
 </div>
 </div>
 );

 case 'arbeitserlaubnis':
 return (
 <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl">
 {hasArbeitserlaubnis ? (
 <div className="px-5 py-4 flex items-center gap-4">
 <div className="flex-1 min-w-0">
 <div className="text-sm font-medium text-[var(--text-primary)]">{ma.arbeitserlaubnis_datei?.split('/').pop()}</div>
 {ma.arbeitserlaubnis_gueltig_bis && (
 <div className="text-xs text-[var(--text-tertiary)] mt-0.5">
 {t('employees.detail.arbeitserlaubnis_gueltig_bis', 'Gültig bis')}: {fmtDt(ma.arbeitserlaubnis_gueltig_bis)}
 </div>
 )}
 </div>
 <a href={`/api/mitarbeiter/${maId}/arbeitserlaubnis`} target="_blank" rel="noreferrer"
 className="text-[#338edc] hover:text-[#2b7bc4]">
 <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
 </a>
 </div>
 ) : (
 <div className="text-center py-8 text-[var(--text-tertiary)] text-sm">{t('mitarbeiter.dokumente.keine_arbeitserlaubnis', 'Keine Arbeitserlaubnis hinterlegt')}</div>
 )}
 </div>
 );

 case 'sonstige':
 return (
 <div className="space-y-4">
 <div className="flex items-center gap-3">
 <select id="upload-kat-select" defaultValue="sonstiges"
 className="border border-[var(--border-default)] rounded-lg px-3 py-1.5 text-sm bg-[var(--bg-card,#fff)] text-[var(--text-primary)]">
 {['zeugnis', 'ausweis', 'personalfragebogen', 'bescheid', 'sonstiges'].map(k => (
 <option key={k} value={k}>{t(`mitarbeiter.dokumente.kat.${k}`)}</option>
 ))}
 </select>
 <label className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#338edc] text-white text-sm font-medium cursor-pointer hover:bg-[#2b7bc4] transition ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
 {uploading ? '...' : t('mitarbeiter.dokumente.hochladen')}
 <input type="file" className="hidden" onChange={e => {
 const kat = document.getElementById('upload-kat-select')?.value || 'sonstiges';
 handleUpload(e, kat);
 }} disabled={uploading} />
 </label>
 </div>
 <div className="border border-[var(--border-default,#E5E7EB)] rounded-xl">
 {sonstigeDocs.length === 0 ? (
 <div className="text-center py-8 text-[var(--text-tertiary)] text-sm">{t('mitarbeiter.dokumente.keine')}</div>
 ) : (
 <div className="divide-y divide-[var(--border-default)]">
 {sonstigeDocs.map(doc => (
 <DocRow key={doc.id} title={doc.titel || doc.datei_name || doc.dateiname} date={doc.created_at}
 size={doc.datei_groesse} quelle={doc.quelle}
 onDownload={() => handleDownload(doc.id, doc.datei_name || doc.dateiname)}
 onDelete={() => handleDelete(doc.id)} />
 ))}
 </div>
 )}
 </div>
 </div>
 );

 default: return null;
 }
 };

 return (
 <div className="space-y-6">
 {/* Category Tab Bar */}
 <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)] inline-flex gap-1 flex-wrap">
 {DOC_CATEGORIES.map(cat => (
 <button key={cat.key} onClick={() => setActiveCat(cat.key)}
 className={`px-4 py-2 rounded-[8px] text-sm font-medium transition-all flex items-center gap-1.5 ${
 activeCat === cat.key
 ? 'bg-white shadow-sm text-[var(--text-primary)] font-semibold dark:bg-[var(--bg-card)]'
 : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
 }`}>
 {t(cat.labelKey)}
 {counts[cat.key] > 0 && (
 <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeCat === cat.key ? 'bg-[#338edc] text-white' : 'bg-[var(--bg-inset)] text-[var(--text-tertiary)]'}`}>
 {counts[cat.key]}
 </span>
 )}
 </button>
 ))}
 </div>

 {/* Content */}
 {renderContent()}
 </div>
 );
}

// #881: Readonly-Row für den berechneten Stundensatz mit Null-Grund-Tooltip
function StundensatzRow({ info, t }) {
 const satz = info?.satz;
 const reason = info?.reason;
 const reasonTooltip = reason === 'missing_lohnkosten'
 ? t('employees.detail.stundensatz_reason_missing', 'Jahres-Lohnkosten nicht gepflegt')
 : reason === 'no_active_phase'
 ? t('employees.detail.stundensatz_reason_no_phase', 'Keine aktive Arbeitszeitmodell-Phase (gueltig_bis IS NULL)')
 : reason === 'zero_wochenstunden'
 ? t('employees.detail.stundensatz_reason_zero_hours', 'Wochenstunden = 0 in aktiver Phase')
 : null;
 const jlk = info?.jahres_lohnkosten;
 const ws = info?.wochenstunden;
 const jahresstd = info?.jahres_arbeitsstunden;
 const formulaTooltip = satz != null
 ? `${jlk} € / (${jahresstd}h × ${ws}h/40h) = ${satz} €/h`
 : reasonTooltip;
 return (
 <div className="flex justify-between items-center py-1.5 border-b border-line/50 last:border-0" title={formulaTooltip || undefined}>
 <span className="text-xs text-[var(--text-secondary)]">
 {t('employees.detail.stundensatz_calculated', 'Stundensatz (berechnet)')}
 </span>
 <div className="flex items-center gap-1.5">
 {satz != null ? (
 <span className="text-sm font-mono font-semibold text-content">{fmtNum(satz, 2)} €/h</span>
 ) : (
 <span className="text-sm text-[var(--text-muted)] italic" title={reasonTooltip || undefined}>
 — {reasonTooltip ? `(${reasonTooltip})` : ''}
 </span>
 )}
 </div>
 </div>
 );
}

// #899 — Lohnkosten-Historisierung: Phasen-Tabelle mit gueltig_ab/_bis + pro-rata Auto-Berechnung.
// administrator darf historische Phasen editieren (Grund-Pflicht), ops_manager nur neue anlegen.
// DELETE nur administrator. Aktive Phase = gueltig_bis IS NULL.
function LohnkostenHistorieCard({ maId, userRole }) {
 const { t } = useLanguage();
 const canCreate = ['administrator', 'ops_manager'].includes(userRole);
 const isAdmin = userRole === 'administrator';
 const [phasen, setPhasen] = useState([]);
 const [activeId, setActiveId] = useState(null);
 const [loading, setLoading] = useState(true);
 const [err, setErr] = useState(null);
 const [createOpen, setCreateOpen] = useState(false);
 const [editPhase, setEditPhase] = useState(null); // phase row being edited
 const [deleteId, setDeleteId] = useState(null);

 const reload = useCallback(() => {
 setLoading(true);
 fetch(`/api/mitarbeiter/${maId}/lohnkosten`, { credentials: 'include' })
 .then(r => r.json())
 .then(d => {
 if (d?.success) {
 setPhasen(d.phasen || []);
 setActiveId(d.aktive_phase?.id ?? null);
 setErr(null);
 } else {
 setErr(d?.error || 'Fehler beim Laden');
 }
 })
 .catch(e => setErr(String(e)))
 .finally(() => setLoading(false));
 }, [maId]);

 useEffect(() => { reload(); }, [reload]);

 const today = new Date().toISOString().slice(0, 10);
 const fmtEUR = (n) => n == null ? '—' : `€ ${Number(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
 const fmtD = (s) => s ? String(s).slice(0, 10).split('-').reverse().join('.') : '—';

 return (
 <Card
 title={t('lohnkosten.historie_title', 'Lohnkosten-Historie')}
 icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v20M5 9h10a3 3 0 010 6H7a3 3 0 000 6h11"/></svg>}
 >
 <div className="text-[11px] text-[var(--text-tertiary)] mb-3">
 {t('lohnkosten.historie_hint', 'Jahres-Lohnkosten in Phasen. Aktive Phase = keine Obergrenze. Monats-Werte werden pro-rata auf Werktage berechnet.')}
 </div>

 {err && (
 <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">{err}</div>
 )}

 {loading ? (
 <div className="text-sm text-[var(--text-tertiary)]">…</div>
 ) : phasen.length === 0 ? (
 <div className="text-sm text-[var(--text-tertiary)] italic py-3">
 {t('lohnkosten.historie_leer', 'Noch keine Lohnkosten-Phase angelegt.')}
 </div>
 ) : (
 <div className="overflow-x-auto">
 <table className="w-full text-sm">
 <thead>
 <tr className="text-left text-xs text-[var(--text-tertiary)] border-b border-[var(--border-table,#F3F4F6)]">
 <th className="py-2 pr-3">{t('lohnkosten.col_zeitraum', 'Zeitraum')}</th>
 <th className="py-2 pr-3">{t('lohnkosten.col_betrag', 'Jahres-Lohnkosten')}</th>
 <th className="py-2 pr-3">{t('lohnkosten.col_rolle', 'Rolle/Abteilung')}</th>
 <th className="py-2 pr-3">{t('lohnkosten.col_notizen', 'Notizen')}</th>
 <th className="py-2 w-16"></th>
 </tr>
 </thead>
 <tbody>
 {phasen.map(p => {
 const isActive = p.id === activeId;
 const isHistorisch = p.gueltig_bis != null && p.gueltig_bis < today;
 return (
 <tr key={p.id} className="border-b border-[var(--border-table,#F3F4F6)] last:border-0">
 <td className="py-2 pr-3 font-mono text-xs">
 {fmtD(p.gueltig_ab)} – {p.gueltig_bis ? fmtD(p.gueltig_bis) : (
 <span className="text-[#338edc] font-semibold">{t('lohnkosten.offen', 'offen')}</span>
 )}
 {isActive && (
 <span className="ml-2 text-[10px] bg-[#338edc] text-white px-1.5 py-0.5 rounded">
 {t('lohnkosten.aktiv', 'aktiv')}
 </span>
 )}
 </td>
 <td className="py-2 pr-3 font-mono text-content font-semibold">{fmtEUR(p.jahres_lohnkosten)}</td>
 <td className="py-2 pr-3 text-[var(--text-secondary)] text-xs">
 {p.lager_rolle_snapshot || '—'}{p.berufsbezeichnung_snapshot ? ` / ${p.berufsbezeichnung_snapshot}` : ''}
 </td>
 <td className="py-2 pr-3 text-[var(--text-tertiary)] text-xs max-w-[240px] truncate" title={p.notizen || ''}>
 {p.notizen || '—'}
 </td>
 <td className="py-2 text-right">
 {canCreate && (!isHistorisch || isAdmin) && (
 <button
 onClick={() => setEditPhase(p)}
 className="text-[var(--text-tertiary)] hover:text-[#338edc] p-1"
 title={t('lohnkosten.edit', 'Bearbeiten')}
 >
 <Pencil size={14} />
 </button>
 )}
 {isAdmin && (
 <button
 onClick={() => setDeleteId(p.id)}
 className="text-[var(--text-tertiary)] hover:text-red-500 p-1 ml-1"
 title={t('lohnkosten.delete', 'Löschen')}
 >
 <Trash2 size={14} />
 </button>
 )}
 </td>
 </tr>
 );
 })}
 </tbody>
 </table>
 </div>
 )}

 {canCreate && (
 <button
 onClick={() => setCreateOpen(true)}
 className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#338edc] hover:text-[#2570b5]"
 >
 <Plus size={14} /> {t('lohnkosten.neue_phase', 'Neue Phase anlegen')}
 </button>
 )}

 {createOpen && (
 <LohnkostenPhaseModal
 maId={maId}
 mode="create"
 existingPhasen={phasen}
 onClose={() => setCreateOpen(false)}
 onSaved={() => { setCreateOpen(false); reload(); }}
 />
 )}
 {editPhase && (
 <LohnkostenPhaseModal
 maId={maId}
 mode="edit"
 phase={editPhase}
 isAdmin={isAdmin}
 onClose={() => setEditPhase(null)}
 onSaved={() => { setEditPhase(null); reload(); }}
 />
 )}
 {deleteId && (
 <ConfirmDialog
 isOpen={true}
 title={t('lohnkosten.delete_title', 'Lohnkosten-Phase löschen?')}
 message={t('lohnkosten.delete_msg', 'Die Phase wird endgültig gelöscht. Falls es die letzte Phase war, wird die vorherige Phase wieder geöffnet. Cache wird neu synchronisiert.')}
 confirmLabel={t('common.delete', 'Löschen')}
 onCancel={() => setDeleteId(null)}
 onConfirm={async () => {
 const r = await fetch(`/api/mitarbeiter/${maId}/lohnkosten/${deleteId}`, { method: 'DELETE', credentials: 'include' });
 const j = await r.json();
 setDeleteId(null);
 if (j.success) reload();
 else setErr(j.error || 'Fehler beim Löschen');
 }}
 />
 )}
 </Card>
 );
}

function LohnkostenPhaseModal({ maId, mode, phase, existingPhasen, isAdmin, onClose, onSaved }) {
 const { t } = useLanguage();
 const isEdit = mode === 'edit';
 const today = new Date().toISOString().slice(0, 10);
 const istHistorisch = isEdit && phase?.gueltig_bis != null && phase.gueltig_bis < today;
 const [gueltigAb, setGueltigAb] = useState(isEdit ? (phase?.gueltig_ab || '') : today);
 const [betrag, setBetrag] = useState(isEdit ? (phase?.jahres_lohnkosten ?? '') : '');
 const [rolle, setRolle] = useState(isEdit ? (phase?.lager_rolle_snapshot || '') : '');
 const [beruf, setBeruf] = useState(isEdit ? (phase?.berufsbezeichnung_snapshot || '') : '');
 const [notizen, setNotizen] = useState(isEdit ? (phase?.notizen || '') : '');
 const [grund, setGrund] = useState('');
 const [saving, setSaving] = useState(false);
 const [err, setErr] = useState(null);

 const save = async () => {
 if (saving) return; // #1224: Doppelklick-/Re-Entrancy-Schutz
 setErr(null);
 if (!isEdit && (!gueltigAb || !betrag)) {
 setErr(t('lohnkosten.err_required', 'gueltig_ab und jahres_lohnkosten sind Pflicht.'));
 return;
 }
 if (istHistorisch && grund.trim().length < 3) {
 setErr(t('lohnkosten.err_grund', 'Begründung (min. 3 Zeichen) für historische Phase erforderlich.'));
 return;
 }
 setSaving(true);
 try {
 const body = isEdit
 ? { jahres_lohnkosten: betrag === '' ? undefined : Number(betrag), lager_rolle_snapshot: rolle || null, berufsbezeichnung_snapshot: beruf || null, notizen: notizen || null, ...(istHistorisch ? { grund } : {}) }
 : { gueltig_ab: gueltigAb, jahres_lohnkosten: Number(betrag), lager_rolle_snapshot: rolle || null, berufsbezeichnung_snapshot: beruf || null, notizen: notizen || null };
 const url = isEdit ? `/api/mitarbeiter/${maId}/lohnkosten/${phase.id}` : `/api/mitarbeiter/${maId}/lohnkosten`;
 const r = await fetch(url, { method: isEdit ? 'PUT' : 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
 const j = await r.json();
 if (!r.ok || !j.success) throw new Error(j.error || 'Fehler');
 onSaved();
 } catch (e) {
 setErr(String(e.message || e));
 } finally {
 setSaving(false);
 }
 };

 // Preview: prev offene Phase würde bis gueltig_ab - 1 geschlossen
 const vorigePhase = !isEdit && existingPhasen ? existingPhasen.find(p => p.gueltig_bis == null) : null;

 return (
 <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
 <div className="bg-surface-card border border-line rounded-card max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
 <div className="flex items-center justify-between px-5 py-3 border-b border-line">
 <h3 className="text-sm font-bold text-content">
 {isEdit ? t('lohnkosten.phase_bearbeiten', 'Lohnkosten-Phase bearbeiten') : t('lohnkosten.neue_phase', 'Neue Phase anlegen')}
 </h3>
 <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"><X size={16} /></button>
 </div>
 <div className="p-5 space-y-3">
 {!isEdit && (
 <div>
 <label className="block text-xs text-[var(--text-secondary)] mb-1">{t('lohnkosten.gueltig_ab', 'Gültig ab')}</label>
 <input type="date" value={gueltigAb} onChange={e => setGueltigAb(e.target.value)}
 className="w-full bg-[var(--bg-input,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-lg px-2.5 py-1.5 text-sm font-mono focus:outline-none focus:border-[#338edc]" />
 {vorigePhase && gueltigAb && (
 <div className="text-[11px] text-[var(--text-tertiary)] mt-1">
 {t('lohnkosten.prev_close_hint', 'Vorherige offene Phase wird am {date} geschlossen.').replace('{date}', addDaysStr(gueltigAb, -1)?.split('-').reverse().join('.') || '—')}
 </div>
 )}
 </div>
 )}
 {isEdit && (
 <div className="text-xs text-[var(--text-tertiary)]">
 {t('lohnkosten.gueltig_ab', 'Gültig ab')}: <span className="font-mono font-semibold">{String(phase.gueltig_ab).slice(0, 10).split('-').reverse().join('.')}</span>
 {phase.gueltig_bis && <> – {String(phase.gueltig_bis).slice(0, 10).split('-').reverse().join('.')}</>}
 {istHistorisch && <span className="ml-2 text-amber-600">({t('lohnkosten.historisch', 'historisch')})</span>}
 </div>
 )}
 <div>
 <label className="block text-xs text-[var(--text-secondary)] mb-1">{t('lohnkosten.jahres_lohnkosten', 'Jahres-Lohnkosten (€)')}</label>
 <input type="number" step="0.01" min="0" value={betrag} onChange={e => setBetrag(e.target.value)}
 placeholder="36000.00"
 className="w-full bg-[var(--bg-input,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-lg px-2.5 py-1.5 text-sm font-mono focus:outline-none focus:border-[#338edc]" />
 </div>
 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className="block text-xs text-[var(--text-secondary)] mb-1">{t('lohnkosten.rolle_snapshot', 'Rolle (Snapshot)')}</label>
 <input type="text" value={rolle} onChange={e => setRolle(e.target.value)}
 placeholder="picker / packer / pick_pack"
 className="w-full bg-[var(--bg-input,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-[#338edc]" />
 </div>
 <div>
 <label className="block text-xs text-[var(--text-secondary)] mb-1">{t('lohnkosten.beruf_snapshot', 'Berufsbezeichnung (Snapshot)')}</label>
 <input type="text" value={beruf} onChange={e => setBeruf(e.target.value)}
 className="w-full bg-[var(--bg-input,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-[#338edc]" />
 </div>
 </div>
 <div>
 <label className="block text-xs text-[var(--text-secondary)] mb-1">{t('lohnkosten.notizen', 'Notizen')}</label>
 <textarea value={notizen} onChange={e => setNotizen(e.target.value)} rows={2}
 className="w-full bg-[var(--bg-input,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-[#338edc]" />
 </div>
 {istHistorisch && (
 <div>
 <label className="block text-xs text-[var(--text-secondary)] mb-1">
 {t('lohnkosten.grund_historisch', 'Begründung (Pflicht bei historischer Phase)')}
 </label>
 <input type="text" value={grund} onChange={e => setGrund(e.target.value)}
 placeholder={t('lohnkosten.grund_placeholder', 'z.B. Korrektur Nachzahlung, Tippfehler, ...')}
 className="w-full bg-[var(--bg-input,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-[#338edc]" />
 <div className="text-[11px] text-amber-600 mt-1">
 {t('lohnkosten.grund_note', 'Edit wird mit Zeitstempel und User-ID in den Notizen dokumentiert.')}
 </div>
 </div>
 )}
 {err && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
 </div>
 <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-line">
 <button onClick={onClose} disabled={saving}
 className="px-3 py-1.5 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-50">
 {t('common.cancel', 'Abbrechen')}
 </button>
 <button onClick={save} disabled={saving}
 className="px-4 py-1.5 text-sm font-semibold text-white bg-[#338edc] hover:bg-[#2570b5] rounded-lg disabled:opacity-50">
 {saving ? '…' : t('common.save', 'Speichern')}
 </button>
 </div>
 </div>
 </div>
 );
}

function StammdatenTab({ ma, setMa, canEdit, userRole, canSetAustritt = false, onOpenAustrittModal = null, section = 'all' }) {
 const showPersoenlich = section === 'all' || section === 'persoenlich';
 const showAnstellung = section === 'all' || section === 'anstellung';
 const { t, locale } = useLanguage();
 // #874 — Pending-Saves-Tracking: Fields die beim Auto-Save fehlgeschlagen sind
 const [pendingSaves, setPendingSaves] = useState({}); // { field: { value, error } }
 const [retryState, setRetryState] = useState({ saving: false, msg: null });

 const markPending = useCallback((field, value, error) => {
 setPendingSaves(prev => ({ ...prev, [field]: { value, error } }));
 }, []);
 const clearPending = useCallback((field) => {
 setPendingSaves(prev => {
 if (!(field in prev)) return prev;
 const next = { ...prev };
 delete next[field];
 return next;
 });
 }, []);

 const pendingCount = Object.keys(pendingSaves).length;

 // beforeunload-Warnung bei unsaved changes
 useEffect(() => {
 if (pendingCount === 0) return;
 const handler = (e) => {
 e.preventDefault();
 e.returnValue = '';
 return '';
 };
 window.addEventListener('beforeunload', handler);
 return () => window.removeEventListener('beforeunload', handler);
 }, [pendingCount]);

 const retryAll = async () => {
 if (pendingCount === 0 || retryState.saving) return;
 setRetryState({ saving: true, msg: null });
 const payload = {};
 for (const [field, { value }] of Object.entries(pendingSaves)) payload[field] = value;
 try {
 const res = await fetch(`/api/mitarbeiter/${ma.id}/stammdaten`, {
 method: 'PUT', headers: { 'Content-Type': 'application/json' },
 credentials: 'include', body: JSON.stringify(payload),
 });
 const d = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
 if (res.ok && !d.error) {
 setMa(prev => ({ ...prev, ...d }));
 setPendingSaves({});
 setRetryState({ saving: false, msg: { type: 'ok', text: t('employees.detail.savedAll', 'Alle Änderungen gespeichert ✓') } });
 setTimeout(() => setRetryState(s => ({ ...s, msg: null })), 2500);
 } else {
 const msg = d.error || `HTTP ${res.status}`;
 setPendingSaves(prev => {
 const next = {};
 for (const [f, obj] of Object.entries(prev)) next[f] = { ...obj, error: msg };
 return next;
 });
 setRetryState({ saving: false, msg: { type: 'err', text: msg } });
 }
 } catch (err) {
 setRetryState({ saving: false, msg: { type: 'err', text: String(err?.message || err) } });
 }
 };

 const saveCtxValue = useMemo(
 () => ({ pending: pendingSaves, markPending, clearPending }),
 [pendingSaves, markPending, clearPending]
 );

 const [pinMsg, setPinMsg] = useState(null);
 const [pinGenerating, setPinGenerating] = useState(false);
 const [kinder, setKinder] = useState([]);
 const [kinderLoading, setKinderLoading] = useState(true);
 const [newKindName, setNewKindName] = useState('');
 const [newKindDate, setNewKindDate] = useState('');
 const [addingKind, setAddingKind] = useState(false);
 const [aeUploading, setAeUploading] = useState(false);
 const [abteilungen, setAbteilungen] = useState([]);
 const [kvEinstufungen, setKvEinstufungen] = useState([]);
 const [kvEinstufungV2Options, setKvEinstufungV2Options] = useState([]); // #1012
 const [urlaubSaldo, setUrlaubSaldo] = useState(null);
 const [standorte, setStandorte] = useState([]);
 // #881: berechneter Stundensatz inkl. Null-Grund
 const [stundensatzInfo, setStundensatzInfo] = useState(null);
 // #895: Effektive Urlaubsvorgriff-Obergrenze (aus Helper)
 const [vorgriffInfo, setVorgriffInfo] = useState(null);

 // Load kinder + abteilungen + kv_einstufungen + urlaubSaldo
 useEffect(() => {
 fetch(`/api/mitarbeiter/${ma.id}/kinder`, { credentials: 'include' })
 .then(r => r.json())
 .then(d => { if (d.kinder) setKinder(d.kinder); })
 .finally(() => setKinderLoading(false));
 fetch('/api/abteilungen', { credentials: 'include' })
 .then(r => r.json()).then(d => { if (Array.isArray(d)) setAbteilungen(d); });
 fetch('/api/kv-einstufungen', { credentials: 'include' })
 .then(r => r.json()).then(d => { if (Array.isArray(d)) setKvEinstufungen(d); });
 fetch('/api/kv-einstufung/options', { credentials: 'include' })
 .then(r => r.json()).then(d => { if (Array.isArray(d?.options)) setKvEinstufungV2Options(d.options); });
 fetch('/api/standorte', { credentials: 'include' })
 .then(r => r.json()).then(d => { if (Array.isArray(d)) setStandorte(d); });
 api(`/api/mitarbeiter/${ma.id}/urlaub?jahr=${new Date().getFullYear()}`)
 .then(d => { if (!d.error) setUrlaubSaldo(d); });
 }, [ma.id]);

 // #881: Stundensatz neu laden, wenn sich jahres_lohnkosten ändert
 useEffect(() => {
 fetch(`/api/mitarbeiter/${ma.id}/stundensatz`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : null)
 .then(d => { if (d && !d.error) setStundensatzInfo(d); })
 .catch(() => {});
 }, [ma.id, ma.jahres_lohnkosten]);

 // #895: Vorgriff-Info neu laden, wenn sich die relevanten Felder ändern
 useEffect(() => {
 fetch(`/api/mitarbeiter/${ma.id}/vorgriff-info`, { credentials: 'include' })
 .then(r => r.ok ? r.json() : null)
 .then(d => { if (d && !d.error) setVorgriffInfo(d); })
 .catch(() => {});
 }, [ma.id, ma.ist_fuehrungsebene, ma.urlaub_vorgriff_max_tage]);

 const generatePin = async () => {
 setPinGenerating(true);
 setPinMsg(null);
 try {
 const res = await fetch(`/api/mitarbeiter/${ma.id}/generate-pin`, { method: 'POST', credentials: 'include' });
 const d = await res.json();
 if (d.success) {
 setMa(prev => ({ ...prev, stempel_pin: d.stempel_pin }));
 setPinMsg({ type: 'success', text: t('employees.detail.pin_generated', 'PIN generiert') });
 } else {
 setPinMsg({ type: 'error', text: d.error || 'Fehler' });
 }
 } catch { setPinMsg({ type: 'error', text: 'Fehler' }); }
 finally { setPinGenerating(false); setTimeout(() => setPinMsg(null), 3000); }
 };

 const addKind = async () => {
 if (!newKindName.trim()) return;
 setAddingKind(true);
 try {
 const res = await fetch(`/api/mitarbeiter/${ma.id}/kinder`, {
 method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
 body: JSON.stringify({ name: newKindName.trim(), geburtsdatum: newKindDate || null }),
 });
 const d = await res.json();
 if (d.id) { setKinder(prev => [...prev, d]); setNewKindName(''); setNewKindDate(''); }
 } finally { setAddingKind(false); }
 };

 const deleteKind = async (kindId) => {
 await fetch(`/api/mitarbeiter/${ma.id}/kinder/${kindId}`, { method: 'DELETE', credentials: 'include' });
 setKinder(prev => prev.filter(k => k.id !== kindId));
 };

 const uploadArbeitserlaubnis = async (e) => {
 const file = e.target.files?.[0];
 if (!file) return;
 setAeUploading(true);
 const fd = new FormData();
 fd.append('file', file);
 try {
 const res = await fetch(`/api/mitarbeiter/${ma.id}/arbeitserlaubnis`, { method: 'POST', body: fd, credentials: 'include' });
 const d = await res.json();
 if (d.success) setMa(prev => ({ ...prev, arbeitserlaubnis_datei: d.datei }));
 } finally { setAeUploading(false); }
 };

 const deleteArbeitserlaubnis = async () => {
 await fetch(`/api/mitarbeiter/${ma.id}/arbeitserlaubnis`, { method: 'DELETE', credentials: 'include' });
 setMa(prev => ({ ...prev, arbeitserlaubnis_datei: null }));
 };

 const anstellungsartOptions = [
 { value: 'angestellter', label: t('employees.detail.stammdaten_angestellter') },
 { value: 'arbeiter', label: t('employees.detail.stammdaten_arbeiter') },
 { value: 'lehrling', label: t('employees.detail.stammdaten_lehrling') },
 { value: 'aushilfe', label: t('employees.detail.stammdaten_aushilfe') },
 { value: 'geringfuegig', label: t('employees.detail.stammdaten_geringfuegig') },
 ];
 const tarifOptions = kvEinstufungen.length > 0
 ? kvEinstufungen.map(kv => ({ value: kv.code, label: `${kv.code} — ${kv.name}` }))
 : [{ value: 'B001', label: 'B001 — Arbeiter' }, { value: 'B002', label: 'B002 — Angestellte' }, { value: 'B010', label: 'B010 — Aushilfe/GF' }, { value: 'B044', label: 'B044 — Lehrling' }];
 const abteilungOptions = abteilungen.length > 0
 ? abteilungen.filter(a => a.aktiv).map(a => ({ value: String(a.id), label: `${a.name} (${a.bereich === 'buero' ? t('settings.departments.office', 'Büro') : t('settings.departments.warehouse', 'Lager')})` }))
 : [];
 const standortOptions = standorte.map(s => ({ value: String(s.id), label: s.name }));
 const pendlerOptions = [
 { value: 'keine', label: t('employees.detail.stammdaten_keine') },
 { value: 'klein', label: 'Klein' },
 { value: 'gross', label: t('employees.detail.stammdaten_gross', 'Groß') },
 ];
 // #881 — Land Whitelist (mit Backend synchron)
 const landOptions = [
 { value: 'AT', label: t('employees.detail.land_at', 'Österreich') },
 { value: 'DE', label: t('employees.detail.land_de', 'Deutschland') },
 { value: 'HU', label: t('employees.detail.land_hu', 'Ungarn') },
 { value: 'SI', label: t('employees.detail.land_si', 'Slowenien') },
 { value: 'HR', label: t('employees.detail.land_hr', 'Kroatien') },
 ];
 // #882 — Staatsbürgerschaft ISO-Whitelist (mit Backend synchron, 18 EU+Nachbarn + SONSTIGE)
 const staatsbuergerschaftOptions = [
 { value: 'AT', label: t('employees.detail.land_at', 'Österreich') },
 { value: 'DE', label: t('employees.detail.land_de', 'Deutschland') },
 { value: 'HU', label: t('employees.detail.land_hu', 'Ungarn') },
 { value: 'SI', label: t('employees.detail.land_si', 'Slowenien') },
 { value: 'HR', label: t('employees.detail.land_hr', 'Kroatien') },
 { value: 'RO', label: t('employees.detail.land_ro', 'Rumänien') },
 { value: 'PL', label: t('employees.detail.land_pl', 'Polen') },
 { value: 'BG', label: t('employees.detail.land_bg', 'Bulgarien') },
 { value: 'CZ', label: t('employees.detail.land_cz', 'Tschechien') },
 { value: 'SK', label: t('employees.detail.land_sk', 'Slowakei') },
 { value: 'IT', label: t('employees.detail.land_it', 'Italien') },
 { value: 'RS', label: t('employees.detail.land_rs', 'Serbien') },
 { value: 'BA', label: t('employees.detail.land_ba', 'Bosnien und Herzegowina') },
 { value: 'MK', label: t('employees.detail.land_mk', 'Nordmazedonien') },
 { value: 'AL', label: t('employees.detail.land_al', 'Albanien') },
 { value: 'TR', label: t('employees.detail.land_tr', 'Türkei') },
 { value: 'UA', label: t('employees.detail.land_ua', 'Ukraine') },
 { value: 'RU', label: t('employees.detail.land_ru', 'Russland') },
 { value: 'SONSTIGE', label: t('employees.detail.land_sonstige', 'Sonstige') },
 ];

 const SectionHeader = ({ children }) => (
 <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)] mt-5 mb-3 first:mt-0">{children}</div>
 );

 return (
 <StammdatenSaveCtx.Provider value={saveCtxValue}>
 <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
 {/* ───── Linke Spalte (60%) — Hauptinhalt ───── */}
 <div className="lg:col-span-3 space-y-5">
 {/* Card 1: Persönliche Daten */}
 {showPersoenlich && (
 <Card title={t('employees.detail.stammdaten_persoenlich')} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>}>
 <EditableField label={t('employees.detail.stammdaten_vorname', 'Vorname')} field="vorname" value={ma.vorname} ma={ma} setMa={setMa} canEdit={canEdit} />
 <EditableField label={t('employees.detail.stammdaten_nachname', 'Nachname')} field="nachname" value={ma.nachname} ma={ma} setMa={setMa} canEdit={canEdit} />
 {ma.personalnummer && <InfoRow label={t('employees.personalnummer', 'Personalnummer')} value={ma.personalnummer} mono />}
 <EditableField label={t('employees.detail.stammdaten_rzl_nr')} field="rzl_personalnummer" value={ma.rzl_personalnummer} ma={ma} setMa={setMa} canEdit={canEdit} type="number" />
 <EditableField label={t('employees.detail.stammdaten_svnr')} field="svnr" value={ma.svnr} ma={ma} setMa={setMa} canEdit={canEdit} masked mono placeholder="2804291094" hint={t('employees.detail.svnr_hint', '10 Ziffern (z.B. 2804291094), Bindestrich optional')} />
 <EditableField label={t('employees.detail.stammdaten_iban')} field="iban" value={ma.iban} ma={ma} setMa={setMa} canEdit={canEdit} masked mono placeholder="AT12 3456 7890 1234 5678" hint={t('employees.detail.iban_hint', 'ISO 13616 — Leerzeichen werden entfernt, Prüfsumme wird validiert')} />
 <EditableField label={t('employees.detail.birthday', 'Geburtstag')} field="birthday" value={ma.birthday} ma={ma} setMa={setMa} canEdit={canEdit} type="date" />
 <EditableField label={t('employees.detail.staatsbuergerschaft', 'Staatsbürgerschaft')} field="staatsbuergerschaft" value={ma.staatsbuergerschaft} ma={ma} setMa={setMa} canEdit={canEdit} options={staatsbuergerschaftOptions} />
 <EditableField label={t('employees.detail.stammdaten_strasse')} field="strasse" value={ma.strasse} ma={ma} setMa={setMa} canEdit={canEdit} />
 <EditableField label={t('employees.detail.stammdaten_plz')} field="plz" value={ma.plz} ma={ma} setMa={setMa} canEdit={canEdit} />
 <EditableField label={t('employees.detail.stammdaten_ort')} field="ort" value={ma.ort} ma={ma} setMa={setMa} canEdit={canEdit} />
 <EditableField label={t('employees.detail.stammdaten_land')} field="land" value={ma.land} ma={ma} setMa={setMa} canEdit={canEdit} options={landOptions} />
 <EditableField label={t('employees.detail.stammdaten_standort', 'Standort')} field="standort_id" value={ma.standort_id != null ? String(ma.standort_id) : ''} ma={ma} setMa={setMa} canEdit={canEdit} options={standortOptions} />

 {/* Stempel-PIN with Generate button */}
 <div className="flex justify-between items-center py-1.5 border-b border-line/50 last:border-0">
 <span className="text-xs text-[var(--text-secondary)]">{t('employees.detail.stammdaten_stempel_pin', 'Stempel-PIN')}</span>
 <div className="flex items-center gap-2">
 <span className="text-sm font-mono text-[var(--text-primary)]">{ma.stempel_pin || '–'}</span>
 {canEdit && (
 <button onClick={generatePin} disabled={pinGenerating}
 className="text-xs px-2 py-0.5 rounded-lg bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-50">
 {pinGenerating ? '...' : t('employees.detail.pin_generate', 'Generieren')}
 </button>
 )}
 </div>
 </div>
 {pinMsg && (
 <div className={`text-xs px-2 py-1 rounded mt-1 ${pinMsg.type === 'success' ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'}`}>
 {pinMsg.text}
 </div>
 )}

 {/* Portal-Stempeln Toggle */}
 <div className="flex justify-between items-center py-1.5 border-b border-line/50">
 <span className="text-xs text-[var(--text-secondary)]">{t('employees.detail.portalStampAllowed', 'Portal-Stempeln')}</span>
 {canEdit ? (
 <ToggleSwitch
 checked={!!ma.stempel_portal_erlaubt}
 onChange={(newVal) => {
 fetch(`/api/mitarbeiter/${ma.id}/stammdaten`, {
 method: 'PUT', headers: { 'Content-Type': 'application/json' },
 credentials: 'include', body: JSON.stringify({ stempel_portal_erlaubt: newVal }),
 }).then(r => r.json()).then(d => { if (!d.error) setMa(prev => ({ ...prev, stempel_portal_erlaubt: newVal })); });
 }}
 />
 ) : (
 <span className="text-sm text-[var(--text-primary)]">{ma.stempel_portal_erlaubt ? t('common.yes', 'Ja') : t('common.no', 'Nein')}</span>
 )}
 </div>

 {/* #989 — Zeiterfassung aktiv Toggle */}
 <div className="flex justify-between items-center py-1.5 border-b border-line/50" title={t('employees.detail.zeiterfassungAktivTooltip', 'Wenn aktiviert, sieht der MA das Modul "Zeiterfassung" in der Sidebar und kann Cases-Timer + manuelle Einträge erfassen.')}>
 <span className="text-xs text-[var(--text-secondary)]">{t('employees.detail.zeiterfassungAktiv', 'Zeiterfassung aktiv')}</span>
 {canEdit ? (
 <ToggleSwitch
 checked={!!ma.zeiterfassung_aktiv}
 onChange={(newVal) => {
 fetch(`/api/mitarbeiter/${ma.id}/stammdaten`, {
 method: 'PUT', headers: { 'Content-Type': 'application/json' },
 credentials: 'include', body: JSON.stringify({ zeiterfassung_aktiv: newVal }),
 }).then(r => r.json()).then(d => { if (!d.error) setMa(prev => ({ ...prev, zeiterfassung_aktiv: newVal })); });
 }}
 />
 ) : (
 <span className="text-sm text-[var(--text-primary)]">{ma.zeiterfassung_aktiv ? t('common.yes', 'Ja') : t('common.no', 'Nein')}</span>
 )}
 </div>

 {/* Früher Arbeitsbeginn erlaubt (vor 06:00) */}
 <div className="flex justify-between items-center py-1.5 border-b border-line/50" title={t('employees.detail.fruehBeginnTooltip', 'Wenn aktiviert, ignoriert das System die 06:00-Regel für diese/n Mitarbeiter/in')}>
 <span className="text-xs text-[var(--text-secondary)]">{t('employees.detail.fruehBeginnErlaubt', 'Früher Arbeitsbeginn erlaubt (vor 06:00)')}</span>
 {canEdit ? (
 <ToggleSwitch
 checked={!!ma.fruehester_beginn_ignorieren}
 onChange={(newVal) => {
 fetch(`/api/mitarbeiter/${ma.id}/stammdaten`, {
 method: 'PUT', headers: { 'Content-Type': 'application/json' },
 credentials: 'include', body: JSON.stringify({ fruehester_beginn_ignorieren: newVal }),
 }).then(r => r.json()).then(d => { if (!d.error) setMa(prev => ({ ...prev, fruehester_beginn_ignorieren: newVal })); });
 }}
 />
 ) : (
 <span className="text-sm text-[var(--text-primary)]">{ma.fruehester_beginn_ignorieren ? t('common.yes', 'Ja') : t('common.no', 'Nein')}</span>
 )}
 </div>

 {/* #848 — Arbeit außerhalb Arbeitszeitmodell (Sa/So/Feiertag) */}
 <div className="flex justify-between items-center py-1.5 border-b border-line/50" title={t('employees.detail.ausserhalbModellTooltip', 'Wenn aktiviert, darf diese/r MA an Sa/So/Feiertagen oder außerhalb des Arbeitszeitmodells stempeln. HR wird bei jedem Event benachrichtigt.')}>
 <span className="text-xs text-[var(--text-secondary)]">{t('employees.detail.ausserhalbModellErlaubt', 'Arbeit außerhalb Arbeitszeitmodell erlaubt (Sa/So/Feiertag)')}</span>
 {canEdit ? (
 <ToggleSwitch
 checked={!!ma.darf_ausserhalb_arbeitszeitmodell_arbeiten}
 onChange={(newVal) => {
 fetch(`/api/mitarbeiter/${ma.id}/stammdaten`, {
 method: 'PUT', headers: { 'Content-Type': 'application/json' },
 credentials: 'include', body: JSON.stringify({ darf_ausserhalb_arbeitszeitmodell_arbeiten: newVal }),
 }).then(r => r.json()).then(d => { if (!d.error) setMa(prev => ({ ...prev, darf_ausserhalb_arbeitszeitmodell_arbeiten: newVal })); });
 }}
 />
 ) : (
 <span className="text-sm text-[var(--text-primary)]">{ma.darf_ausserhalb_arbeitszeitmodell_arbeiten ? t('common.yes', 'Ja') : t('common.no', 'Nein')}</span>
 )}
 </div>

 {/* #1813 — Standortpflicht beim Stempeln: Lager am Standort, Büro auch aus dem Homeoffice */}
 <div className="flex justify-between items-center py-1.5 border-b border-line/50" title={t('employees.detail.standortPflichtTooltip', 'Standard folgt der globalen Einstellung. Pflicht: Stempeln nur am hinterlegten Standort (GPS wird geprüft). Frei: ortsunabhängig, z. B. Homeoffice — es wird keine Position gespeichert.')}>
 <span className="text-xs text-[var(--text-secondary)]">{t('employees.detail.standortPflicht', 'Standortpflicht beim Stempeln')}</span>
 {canEdit ? (
 <select
 value={ma.standort_pflicht_modus || 'standard'}
 onChange={(e) => {
 const newVal = e.target.value;
 fetch(`/api/mitarbeiter/${ma.id}/stammdaten`, {
 method: 'PUT', headers: { 'Content-Type': 'application/json' },
 credentials: 'include', body: JSON.stringify({ standort_pflicht_modus: newVal }),
 }).then(r => r.json()).then(d => { if (!d.error) setMa(prev => ({ ...prev, standort_pflicht_modus: newVal })); });
 }}
 className="text-xs bg-[var(--bg-secondary)] border border-line rounded px-2 py-1 text-content"
 >
 <option value="standard">{t('employees.detail.standortPflichtStandard', 'Standard (globale Vorgabe)')}</option>
 <option value="pflicht">{t('employees.detail.standortPflichtPflicht', 'Pflicht (nur am Standort)')}</option>
 <option value="frei">{t('employees.detail.standortPflichtFrei', 'Frei (ortsunabhängig)')}</option>
 </select>
 ) : (
 <span className="text-sm text-[var(--text-primary)]">
 {ma.standort_pflicht_modus === 'pflicht'
 ? t('employees.detail.standortPflichtPflicht', 'Pflicht (nur am Standort)')
 : ma.standort_pflicht_modus === 'frei'
 ? t('employees.detail.standortPflichtFrei', 'Frei (ortsunabhängig)')
 : t('employees.detail.standortPflichtStandard', 'Standard (globale Vorgabe)')}
 </span>
 )}
 </div>

 {/* #1772 — Sichtbarkeit der Urlaubs-Kennzahlen im MA-Portal (Default AN) */}
 <SectionHeader>{t('employees.detail.portalAnzeige.section', 'Anzeige im Mitarbeiter-Portal')}</SectionHeader>
 {[
 { field: 'portal_zeige_planbar', label: t('employees.detail.portalAnzeige.planbar', "Card „Zum Planen verfügbar\u201c anzeigen") },
 { field: 'portal_zeige_saldo', label: t('employees.detail.portalAnzeige.saldo', "Card „Saldo jetzt\u201c anzeigen") },
 { field: 'portal_zeige_verplant', label: t('employees.detail.portalAnzeige.verplant', "Card „Bereits verplant\u201c anzeigen") },
 ].map(({ field, label }) => (
 <div key={field} className="flex justify-between items-center py-1.5 border-b border-line/50" title={t('employees.detail.portalAnzeige.tooltip', 'Blendet diese Kennzahl-Card in den Abwesenheiten des Mitarbeiter-Portals aus. Berechnung und Antrags-Prüfung bleiben unverändert.')}>
 <span className="text-xs text-[var(--text-secondary)]">{label}</span>
 {canEdit ? (
 <ToggleSwitch
 checked={ma[field] !== false}
 onChange={(newVal) => {
 fetch(`/api/mitarbeiter/${ma.id}/stammdaten`, {
 method: 'PUT', headers: { 'Content-Type': 'application/json' },
 credentials: 'include', body: JSON.stringify({ [field]: newVal }),
 }).then(r => r.json()).then(d => { if (!d.error) setMa(prev => ({ ...prev, [field]: newVal })); });
 }}
 />
 ) : (
 <span className="text-sm text-[var(--text-primary)]">{ma[field] !== false ? t('common.yes', 'Ja') : t('common.no', 'Nein')}</span>
 )}
 </div>
 ))}

 {/* #1808 — Light-Modus der MA-App (Default AUS). Anders als die #1772-Flags ist
     die Konvention hier `=== true`: fehlt der Wert, bleibt die volle Ansicht. */}
 <div className="flex justify-between items-center py-1.5 border-b border-line/50" title={t('employees.detail.appLightModus.tooltip', 'Reduziert die MA-App: blendet Mehr-/Überstunden, Tagesübersicht, 6-Monats-Trend, die Kacheln Delta und Offener Bestand sowie die grafische Urlaubsaufteilung aus. Berechnungen, Antrags-Prüfung und der PDF-Monatsnachweis bleiben unverändert.')}>
 <span className="text-xs text-[var(--text-secondary)]">{t('employees.detail.appLightModus.label', 'Light-Modus in der MA-App')}</span>
 {canEdit ? (
 <ToggleSwitch
 checked={ma.app_light_modus === true}
 onChange={(newVal) => {
 fetch(`/api/mitarbeiter/${ma.id}/stammdaten`, {
 method: 'PUT', headers: { 'Content-Type': 'application/json' },
 credentials: 'include', body: JSON.stringify({ app_light_modus: newVal }),
 }).then(r => r.json()).then(d => { if (!d.error) setMa(prev => ({ ...prev, app_light_modus: newVal })); });
 }}
 />
 ) : (
 <span className="text-sm text-[var(--text-primary)]">{ma.app_light_modus === true ? t('common.yes', 'Ja') : t('common.no', 'Nein')}</span>
 )}
 </div>

 {/* #893 — Gleitzeit */}
 <SectionHeader>{t('gleitzeit.section_title', 'Gleitzeit')}</SectionHeader>
 <div className="flex justify-between items-center py-1.5 border-b border-line/50" title={t('gleitzeit.aktiv_tooltip', 'Bei Gleitzeit gibt es keine tägliche SOLL — Mehr-/Überstunden werden erst am Ende jeder Periode klassifiziert. Tages-Obergrenze: 12h.')}>
 <span className="text-xs text-[var(--text-secondary)]">{t('gleitzeit.aktiv', 'Gleitzeit aktiv')}</span>
 {canEdit ? (
 <ToggleSwitch
 checked={!!ma.gleitzeit_aktiv}
 onChange={(newVal) => {
 if (newVal && !ma.gleitzeit_start_datum) {
 const today = new Date().toISOString().slice(0, 10);
 fetch(`/api/mitarbeiter/${ma.id}/stammdaten`, {
 method: 'PUT', headers: { 'Content-Type': 'application/json' },
 credentials: 'include', body: JSON.stringify({ gleitzeit_aktiv: true, gleitzeit_start_datum: today }),
 }).then(r => r.json()).then(d => { if (!d.error) setMa(prev => ({ ...prev, gleitzeit_aktiv: true, gleitzeit_start_datum: today })); });
 } else {
 fetch(`/api/mitarbeiter/${ma.id}/stammdaten`, {
 method: 'PUT', headers: { 'Content-Type': 'application/json' },
 credentials: 'include', body: JSON.stringify({ gleitzeit_aktiv: newVal }),
 }).then(r => r.json()).then(d => { if (!d.error) setMa(prev => ({ ...prev, gleitzeit_aktiv: newVal })); });
 }
 }}
 />
 ) : (
 <span className="text-sm text-[var(--text-primary)]">{ma.gleitzeit_aktiv ? t('common.yes', 'Ja') : t('common.no', 'Nein')}</span>
 )}
 </div>
 {ma.gleitzeit_aktiv && (
 <>
 <EditableField
 label={t('gleitzeit.periode_tage', 'Perioden-Länge (Tage)')}
 field="gleitzeit_periode_tage"
 value={ma.gleitzeit_periode_tage}
 ma={ma} setMa={setMa} canEdit={canEdit} type="number"
 placeholder="90"
 />
 <EditableField
 label={t('gleitzeit.start_datum', 'Gleitzeit-Start-Datum')}
 field="gleitzeit_start_datum"
 value={ma.gleitzeit_start_datum}
 ma={ma} setMa={setMa} canEdit={canEdit} type="date"
 />
 </>
 )}

 <SectionHeader>{t('employees.detail.contact_info')}</SectionHeader>
 <EditableField label={t('employees.detail.stammdaten_work_email')} field="work_email" value={ma.work_email} ma={ma} setMa={setMa} canEdit={canEdit} type="email" placeholder="max@beispiel.at" />
 <EditableField label={t('employees.detail.stammdaten_work_phone')} field="work_phone" value={ma.work_phone} ma={ma} setMa={setMa} canEdit={canEdit} type="tel" placeholder="+43 660 123 4567" />
 <EditableField label={t('employees.detail.stammdaten_private_mobile', 'Privates Mobiltelefon')} field="mobile_phone" value={ma.mobile_phone} ma={ma} setMa={setMa} canEdit={canEdit} type="tel" placeholder="+43 660 123 4567" />
 <EditableField label={t('employees.detail.stammdaten_private_email', 'Private E-Mail')} field="private_email" value={ma.private_email} ma={ma} setMa={setMa} canEdit={canEdit} type="email" placeholder="max@beispiel.at" />

 {/* #879 — Pause-Reminder-SMS Opt-in */}
 <div
 className="flex justify-between items-center py-1.5 border-b border-line/50"
 title={t('pauseSms.toggle_tooltip', 'Erinnerungs-SMS vor Mittagspause (nur bei fixen Auto-Pausen). Erfordert private Mobilnummer + globalen Master-Toggle in Einstellungen → Stempeluhr.')}
 >
 <span className="text-xs text-[var(--text-secondary)]">{t('pauseSms.ma_toggle', 'Pause-Reminder-SMS')}</span>
 {canEdit ? (
 <ToggleSwitch
 checked={!!ma.pause_sms_aktiv}
 disabled={!ma.mobile_phone}
 onChange={(newVal) => {
 if (newVal && !ma.mobile_phone) return;
 fetch(`/api/mitarbeiter/${ma.id}/stammdaten`, {
 method: 'PUT', headers: { 'Content-Type': 'application/json' },
 credentials: 'include', body: JSON.stringify({ pause_sms_aktiv: newVal }),
 }).then(r => r.json()).then(d => { if (!d.error) setMa(prev => ({ ...prev, pause_sms_aktiv: newVal })); });
 }}
 />
 ) : (
 <span className="text-sm text-[var(--text-primary)]">{ma.pause_sms_aktiv ? t('common.yes', 'Ja') : t('common.no', 'Nein')}</span>
 )}
 </div>

 {/* #918 — Prozess-Notifications Opt-out */}
 <div
 className="flex justify-between items-center py-1.5 border-b border-line/50"
 title={t('prozessNotify.toggle_tooltip', 'Multi-Channel-Benachrichtigungen über Prozess-Updates. Aus = MA wird bei keinem Prozess-Versand mehr angeschrieben.')}
 >
 <span className="text-xs text-[var(--text-secondary)]">{t('prozessNotify.ma_toggle', 'Prozess-Benachrichtigungen')}</span>
 {canEdit ? (
 <ToggleSwitch
 checked={ma.process_notifications_enabled !== false}
 onChange={(newVal) => {
 fetch(`/api/mitarbeiter/${ma.id}/process-notifications-enabled`, {
 method: 'PUT', headers: { 'Content-Type': 'application/json' },
 credentials: 'include', body: JSON.stringify({ enabled: newVal }),
 }).then(r => r.json()).then(d => { if (!d.error) setMa(prev => ({ ...prev, process_notifications_enabled: newVal })); });
 }}
 />
 ) : (
 <span className="text-sm text-[var(--text-primary)]">{ma.process_notifications_enabled !== false ? t('common.yes', 'Ja') : t('common.no', 'Nein')}</span>
 )}
 </div>

 <SectionHeader>{t('employees.detail.emergency_contact')}</SectionHeader>
 <EditableField label={t('employees.detail.stammdaten_notfall_name')} field="notfallkontakt_name" value={ma.notfallkontakt_name} ma={ma} setMa={setMa} canEdit={canEdit} />
 <EditableField label={t('employees.detail.stammdaten_notfall_telefon')} field="notfallkontakt_telefon" value={ma.notfallkontakt_telefon} ma={ma} setMa={setMa} canEdit={canEdit} type="tel" placeholder="+43 660 123 4567" />
 <EditableField label={t('employees.detail.stammdaten_notfall_beziehung')} field="notfallkontakt_beziehung" value={ma.notfallkontakt_beziehung} ma={ma} setMa={setMa} canEdit={canEdit} />
 </Card>
 )}

 {/* Card: FlowAI-Tags (#1274) */}
 {showPersoenlich && (
 <Card title={t('employees.detail.flowaiTagsTitle', 'FlowAI-Tags')} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>}>
 <FlowAITagsField ma={ma} setMa={setMa} canEdit={canEdit} />
 <div className="mt-3 pt-3 border-t border-line/50">
 <EditableField
   label={t('employees.detail.flowaiHinweisLabel', 'FlowAI-Hinweis')}
   field="flowai_hinweis"
   value={ma.flowai_hinweis}
   ma={ma} setMa={setMa} canEdit={canEdit}
   type="textarea" rows={3}
   placeholder={t('employees.detail.flowaiHinweisPlaceholder', 'z. B. „Bei Carrier-/Preisfragen zuständig"')}
   hint={t('employees.detail.flowaiHinweisHint', 'Freitext-Zuständigkeit/Orientierung für FlowAI, z. B. „Bei Carrier-/Preisfragen zuständig". FlowAI nutzt diesen Hinweis später zur Zuordnung.')}
 />
 </div>
 </Card>
 )}

 {/* Card 2: Anstellung */}
 {showAnstellung && (
 <Card title={t('employees.detail.stammdaten_anstellung')} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>}>
 <EditableField label={t('employees.detail.stammdaten_anstellungsart')} field="anstellungsart" value={ma.anstellungsart} ma={ma} setMa={setMa} canEdit={canEdit} options={anstellungsartOptions} />
 <EditableField
   label={t('employees.detail.stammdaten_kv_einstufung_v2', 'KV-Einstufung (neu)')}
   field="kv_einstufung_v2"
   value={ma.kv_einstufung_v2}
   ma={ma}
   setMa={setMa}
   canEdit={canEdit}
   options={kvEinstufungV2Options.map(o => ({ value: o.code, label: locale === 'en' ? o.label_en : o.label_de }))}
 />
 <EditableField
   label={t('employees.detail.stammdaten_kv_einstufung_seit', 'Einstufung seit')}
   field="kv_einstufung_seit"
   value={ma.kv_einstufung_seit}
   ma={ma}
   setMa={setMa}
   canEdit={canEdit}
   type="date"
 />
 <EditableField
   label={t('employees.detail.stammdaten_verwendungsgruppenjahr', 'Verwendungsgruppenjahr')}
   field="verwendungsgruppenjahr"
   value={ma.verwendungsgruppenjahr}
   ma={ma}
   setMa={setMa}
   canEdit={canEdit}
   options={[
     { value: 'im_1_2_jahr', label: t('employees.detail.vgj_im_1_2_jahr', 'Im 1. und 2. Verwendungsgruppenjahr') },
     { value: 'nach_2_jahren', label: t('employees.detail.vgj_nach_2_jahren', 'nach 2 Verwendungsgruppenjahren') },
     { value: 'nach_4_jahren', label: t('employees.detail.vgj_nach_4_jahren', 'nach 4 Verwendungsgruppenjahren') },
     { value: 'nach_6_jahren', label: t('employees.detail.vgj_nach_6_jahren', 'nach 6 Verwendungsgruppenjahren') },
     { value: 'nach_8_jahren', label: t('employees.detail.vgj_nach_8_jahren', 'nach 8 Verwendungsgruppenjahren') },
     { value: 'nach_10_jahren', label: t('employees.detail.vgj_nach_10_jahren', 'nach 10 Verwendungsgruppenjahren') },
     { value: 'nach_12_jahren', label: t('employees.detail.vgj_nach_12_jahren', 'nach 12 Verwendungsgruppenjahren') },
     { value: 'nach_15_jahren', label: t('employees.detail.vgj_nach_15_jahren', 'nach 15 Verwendungsgruppenjahren') },
   ]}
 />
 <EditableField label={t('employees.detail.stammdaten_berufsbezeichnung')} field="berufsbezeichnung" value={ma.berufsbezeichnung} ma={ma} setMa={setMa} canEdit={canEdit} />
 <EditableField label={t('employees.detail.stammdaten_eintritt')} field="eintritt_datum" value={ma.eintritt_datum} ma={ma} setMa={setMa} canEdit={canEdit} type="date" />
 <ProbemonatBadge eintritt={ma.eintritt_datum} austritt={ma.austritt_datum} poolAccount={ma.ist_pool_account} />
 <div className="flex justify-between items-center py-1.5 border-b border-line/50">
 <span className="text-xs text-[var(--text-secondary)]">{t('employees.detail.stammdaten_austritt')}</span>
 <div className="flex items-center gap-2">
 <span className="text-sm text-content-secondary font-mono">
 {ma.austritt_datum ? fmtDate(ma.austritt_datum, locale) : '–'}
 </span>
 {canSetAustritt && !ma.ist_pool_account && onOpenAustrittModal && (
 <button
 type="button"
 onClick={onOpenAustrittModal}
 className="text-xs hover:underline"
 style={{ color: '#338edc' }}
 >
 {ma.austritt_datum
 ? t('austritt.field_edit_link', 'Bearbeiten')
 : t('austritt.toolbar_create', 'Austritt erfassen') + ' →'}
 </button>
 )}
 </div>
 </div>
 <EditableField label={t('employees.detail.stammdaten_abteilung', 'Abteilung')} field="abteilung_id" value={ma.abteilung_id ? String(ma.abteilung_id) : ''} ma={ma} setMa={setMa} canEdit={canEdit} options={abteilungOptions} />
 {/* wochenstunden_soll ist read-only seit v2.349.0 — wird aus Arbeitszeitmodell berechnet.
     #1208 Pre-Eintritts-Awareness: bei eintritt_datum > heute Datum im Untertitel
     mitführen statt pauschal "berechnet" zu schreiben. Wenn kein Wert vorhanden:
     transparenten Hinweis statt Default-Lüge. */}
 <div className="flex items-start justify-between py-2 border-b border-line/40 last:border-0 gap-3">
 <div className="text-xs text-[var(--text-secondary)] pt-0.5 min-w-0 flex-shrink-0">
 {t('employees.detail.stammdaten_wochenstunden')}
 </div>
 <div className="text-right">
 <div className="text-xs font-mono font-semibold text-content">
 {ma.wochenstunden_soll != null ? `${ma.wochenstunden_soll}h` : '–'}
 </div>
 <div className="text-[10px] text-[var(--text-tertiary)]">
 {ma.wochenstunden_soll == null
   ? t('workSchedule.no_model', 'Arbeitszeit-Modell noch nicht zugewiesen')
   : isVorEintritt(ma)
     ? t('workSchedule.from_model_future', { defaultValue: 'Aus Arbeitszeitmodell ab {{datum}}', datum: fmtDate(ma.eintritt_datum, locale) })
     : t('workSchedule.derived_from_model', 'Aus Arbeitszeitmodell berechnet')}
 </div>
 </div>
 </div>
 <EditableField label={t('employees.detail.stammdaten_brutto')} field="brutto_monatlich" value={ma.brutto_monatlich} ma={ma} setMa={setMa} canEdit={canEdit} type="number" />
 <EditableField
 label={t('employees.detail.stammdaten_stundensatz_intern')}
 field="stundensatz_intern"
 value={ma.stundensatz_intern}
 ma={ma} setMa={setMa} canEdit={canEdit} type="number"
 readOnly
 readOnlyNote={t('employees.detail.stundensatz_intern_deprecated', 'Deprecated seit v2.374.0 — ersetzt durch Jahres-Lohnkosten / aktuelle Wochenstunden')}
 />
 <EditableField label={t('employees.detail.stammdaten_jahres_lohnkosten', 'Jahres-Lohnkosten (€)')} field="jahres_lohnkosten" value={ma.jahres_lohnkosten} ma={ma} setMa={setMa} canEdit={canEdit} type="number" hint={t('employees.detail.jahres_lohnkosten_hint', 'Gesamt-DG-Kosten pro Jahr (brutto × ~14 × 1.32)')} />

 {/* #881 — berechneter Stundensatz (nicht editierbar, mit Null-Grund-Tooltip) */}
 <StundensatzRow info={stundensatzInfo} t={t} />

 {/* Sozialabgaben block removed */}
 </Card>
 )}

 {/* Card 2a (#896): Austrittsdaten — nur wenn austritt_datum gesetzt, oranger Border-Akzent */}
 {showAnstellung && ma.austritt_datum && (
 <Card
 title={t('austritt.section_title', 'Austrittsdaten')}
 icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>}
 className="border-l-4 !border-l-orange-500"
 >
 <div className="text-[11px] text-[var(--text-tertiary)] mb-2">
 {t('austritt.section_hint', 'Pflichtangaben zur Dokumentation des Austritts. Auflösungsart und Begründung sind erforderlich.')}
 </div>
 <div className="flex justify-between items-center py-1.5 border-b border-line/50">
 <span className="text-xs text-[var(--text-secondary)]">{t('employees.detail.stammdaten_austritt', 'Austrittsdatum')}</span>
 <div className="flex items-center gap-2">
 <span className="text-sm text-content-secondary font-mono">{fmtDate(ma.austritt_datum, locale)}</span>
 {canSetAustritt && !ma.ist_pool_account && onOpenAustrittModal && (
 <button
 type="button"
 onClick={onOpenAustrittModal}
 className="text-xs hover:underline"
 style={{ color: '#338edc' }}
 >
 {t('austritt.field_edit_link', 'Bearbeiten')}
 </button>
 )}
 </div>
 </div>
 <EditableField
 label={t('austritt.aufloesungsart_label', 'Auflösungsart')}
 field="austritt_aufloesungsart"
 value={ma.austritt_aufloesungsart}
 ma={ma} setMa={setMa} canEdit={canEdit}
 options={[
 { value: 'kuendigung_an', label: t('austritt.aufloesungsart.kuendigung_an', 'Kündigung durch Arbeitnehmer') },
 { value: 'kuendigung_ag', label: t('austritt.aufloesungsart.kuendigung_ag', 'Kündigung durch Arbeitgeber') },
 { value: 'einvernehmlich_an', label: t('austritt.aufloesungsart.einvernehmlich_an', 'Einvernehmliche Auflösung (AN-initiiert)') },
 { value: 'einvernehmlich_ag', label: t('austritt.aufloesungsart.einvernehmlich_ag', 'Einvernehmliche Auflösung (AG-initiiert)') },
 { value: 'probezeit_an', label: t('austritt.aufloesungsart.probezeit_an', 'Auflösung in Probezeit (AN)') },
 { value: 'probezeit_ag', label: t('austritt.aufloesungsart.probezeit_ag', 'Auflösung in Probezeit (AG)') },
 { value: 'entlassung', label: t('austritt.aufloesungsart.entlassung', 'Entlassung') },
 { value: 'vorzeitig', label: t('austritt.aufloesungsart.vorzeitig', 'Vorzeitiger Austritt') },
 { value: 'zeitablauf', label: t('austritt.aufloesungsart.zeitablauf', 'Zeitablauf (befristeter Vertrag)') },
 { value: 'pensionierung', label: t('austritt.aufloesungsart.pensionierung', 'Pensionierung') },
 { value: 'tod', label: t('austritt.aufloesungsart.tod', 'Tod') },
 ]}
 hint={!ma.austritt_aufloesungsart ? t('austritt.aufloesungsart_pflicht', 'Pflichtfeld — ohne Auswahl kann der Datensatz nicht gespeichert werden.') : null}
 />
 <EditableField
 label={t('austritt.begruendung_label', 'Begründung zum Austritt')}
 field="austritt_begruendung"
 value={ma.austritt_begruendung}
 ma={ma} setMa={setMa} canEdit={canEdit} type="textarea" rows={4}
 placeholder={t('austritt.begruendung_placeholder', 'z.B. Wechsel in Vollzeit-Position bei anderem Arbeitgeber, persönliche Gründe, etc.')}
 hint={(!ma.austritt_begruendung || String(ma.austritt_begruendung).trim().length < 10)
 ? t('austritt.begruendung_pflicht', 'Pflichtfeld — mindestens 10 Zeichen.')
 : null}
 />
 {/* #1705: Urlaubssaldo zum Austritt — Berechnung auf Read, keine Mutation */}
 {!ma.ist_pool_account && <AustrittUrlaubsSaldo maId={ma.id} austrittDatum={ma.austritt_datum} />}
 </Card>
 )}

 {/* Card 2c (#899): Lohnkosten-Historie — Phasen mit gueltig_ab/_bis, admin-only Delete */}
 {showAnstellung && <LohnkostenHistorieCard maId={ma.id} userRole={userRole} />}
 </div>

 {/* ───── Rechte Spalte (40%) — Sekundärinhalt ───── */}
 <div className="lg:col-span-2 space-y-5">
 {/* Card 3: Kinder */}
 {showPersoenlich && (
 <Card title={t('employees.detail.kinder_title', 'Kinder')} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="5"/><path d="M20 21a8 8 0 00-16 0"/></svg>}>
 {kinderLoading ? (
 <div className="text-sm text-[var(--text-tertiary)]">...</div>
 ) : (
 <>
 {kinder.length === 0 && (
 <div className="text-sm text-[var(--text-tertiary)] mb-3">{t('employees.detail.kinder_keine', 'Keine Kinder eingetragen')}</div>
 )}
 {kinder.map(k => (
 <div key={k.id} className="flex items-center justify-between py-1.5 border-b border-line/50 last:border-0">
 <div>
 <span className="text-sm text-[var(--text-primary)]">{k.name}</span>
 {k.geburtsdatum && <span className="text-xs text-[var(--text-tertiary)] ml-2">{fmtDate(k.geburtsdatum)}</span>}
 </div>
 {canEdit && (
 <button onClick={() => deleteKind(k.id)} className="text-[var(--text-tertiary)] hover:text-red-500 p-0.5">
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
 </button>
 )}
 </div>
 ))}
 {canEdit && (
 <div className="flex items-center gap-2 mt-3">
 <input value={newKindName} onChange={e => setNewKindName(e.target.value)} placeholder={t('employees.detail.kinder_name', 'Name')}
 className="flex-1 bg-[var(--bg-input,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-lg px-2.5 py-1.5 text-sm text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-[#338edc]" />
 <input type="date" value={newKindDate} onChange={e => setNewKindDate(e.target.value)}
 className="bg-[var(--bg-input,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-lg px-2.5 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 <button onClick={addKind} disabled={addingKind || !newKindName.trim()}
 className="px-3 py-1.5 rounded-lg bg-[#338edc] text-white text-sm font-medium hover:bg-[#2b7bc4] disabled:opacity-50">
 +
 </button>
 </div>
 )}
 </>
 )}
 </Card>
 )}

 {/* Card 4: Arbeitserlaubnis + Urlaub */}
 {showPersoenlich && (
 <Card title={t('employees.detail.urlaub_und_arbeitserlaubnis', 'Urlaub & Arbeitserlaubnis')} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>}>
 {/* #1159: Urlaubstage / Jahr — Auto-computed mit HR-Override-Toggle */}
 <UrlaubsAnspruchBlock ma={ma} setMa={setMa} canEdit={canEdit} t={t} />
 <SectionHeader>{t('employees.detail.urlaub_startsaldo_section', 'Startsaldo')}</SectionHeader>
 <EditableField label={t('employees.detail.urlaub_startsaldo', 'Startsaldo (Tage)')} field="urlaub_startsaldo" value={ma.urlaub_startsaldo} ma={ma} setMa={setMa} canEdit={canEdit} type="number" />
 <EditableField label={t('employees.detail.urlaub_startsaldo_datum', 'Startsaldo per')} field="urlaub_startsaldo_datum" value={ma.urlaub_startsaldo_datum} ma={ma} setMa={setMa} canEdit={canEdit} type="date" />

 {/* #895 — Urlaubsvorgriff-Steuerung */}
 <SectionHeader>{t('urlaub.vorgriff.section', 'Vorgriff (Minus-Urlaub)')}</SectionHeader>
 <div className="flex justify-between items-center py-1.5 border-b border-line/50" title={t('urlaub.vorgriff.fuehrungsebene_tooltip', 'Führungsebene-MAs bekommen die erhöhte Vorgriff-Obergrenze (Standard: 15 Tage statt 5).')}>
 <span className="text-xs text-[var(--text-secondary)]">{t('mitarbeiter.felder.ist_fuehrungsebene', 'Führungsebene')}</span>
 {canEdit ? (
 <ToggleSwitch
 checked={!!ma.ist_fuehrungsebene}
 onChange={(newVal) => {
 fetch(`/api/mitarbeiter/${ma.id}/stammdaten`, {
 method: 'PUT', headers: { 'Content-Type': 'application/json' },
 credentials: 'include', body: JSON.stringify({ ist_fuehrungsebene: newVal }),
 }).then(r => r.json()).then(d => { if (!d.error) setMa(prev => ({ ...prev, ist_fuehrungsebene: newVal })); });
 }}
 />
 ) : (
 <span className="text-sm text-[var(--text-primary)]">{ma.ist_fuehrungsebene ? t('common.yes', 'Ja') : t('common.no', 'Nein')}</span>
 )}
 </div>
 <EditableField
 label={t('mitarbeiter.felder.urlaub_vorgriff_max_tage', 'Vorgriff max. (Tage, MA-Override)')}
 field="urlaub_vorgriff_max_tage"
 value={ma.urlaub_vorgriff_max_tage}
 ma={ma} setMa={setMa} canEdit={canEdit} type="number"
 hint={t('urlaub.vorgriff.override_hint', 'Leer = Default aus Einstellungen. Explizite Zahl überschreibt auch Führungsebene-Default.')}
 />
 {vorgriffInfo && (
 <div className="flex justify-between items-center py-1.5 border-b border-line/50 bg-[var(--bg-inset,#F8F9FC)] px-2 rounded">
 <span className="text-xs text-[var(--text-secondary)]">{t('urlaub.vorgriff.effektiv', 'Effektive Obergrenze')}</span>
 <span className="text-sm text-[var(--text-primary)]">
 <span className="font-semibold">{vorgriffInfo.max_tage} {t('employees.detail.days_unit', 'Tage')}</span>
 <span className="ml-2 text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
 ({t(`urlaub.vorgriff.quelle_${vorgriffInfo.quelle}`, vorgriffInfo.quelle)})
 </span>
 </span>
 </div>
 )}

 {/* #979: Berechneter-Saldo-Block entfernt — Hero-KPI in VacationCard (Tab Zeit) */}

 <EditableField label={t('employees.detail.stammdaten_notizen')} field="notizen" value={ma.notizen} ma={ma} setMa={setMa} canEdit={canEdit} type="textarea" rows={5} placeholder={t('employees.detail.notizen_placeholder', 'Interne Notizen zum/zur Mitarbeiter/in …')} />

 <SectionHeader>{t('employees.detail.arbeitserlaubnis_title', 'Arbeitserlaubnis')}</SectionHeader>
 <EditableField label={t('employees.detail.arbeitserlaubnis_gueltig_bis', 'Gültig bis')} field="arbeitserlaubnis_gueltig_bis" value={ma.arbeitserlaubnis_gueltig_bis} ma={ma} setMa={setMa} canEdit={canEdit} type="date" />
 {ma.arbeitserlaubnis_datei ? (
 <div className="flex items-center justify-between py-1.5 border-b border-line/50">
 <div className="flex items-center gap-2">
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
 <span className="text-sm text-[var(--text-primary)]">{ma.arbeitserlaubnis_datei.split('/').pop()}</span>
 </div>
 <div className="flex items-center gap-2">
 <a href={`/api/mitarbeiter/${ma.id}/arbeitserlaubnis`} target="_blank" rel="noreferrer"
 className="text-[#338edc] hover:text-[#2b7bc4] text-sm">
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
 </a>
 {canEdit && (
 <button onClick={deleteArbeitserlaubnis} className="text-[var(--text-tertiary)] hover:text-red-500">
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
 </button>
 )}
 </div>
 </div>
 ) : canEdit ? (
 <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[var(--border-default,#E5E7EB)] text-sm text-[var(--text-secondary)] cursor-pointer hover:bg-[var(--bg-inset,#F8F9FC)]">
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
 {aeUploading ? '...' : t('employees.detail.arbeitserlaubnis_upload', 'Datei hochladen')}
 <input type="file" className="hidden" onChange={uploadArbeitserlaubnis} accept=".pdf,.jpg,.jpeg,.png" disabled={aeUploading} />
 </label>
 ) : (
 <div className="text-sm text-[var(--text-tertiary)]">–</div>
 )}
 </Card>
 )}

 {/* Card 2b: Lohnsteuer AT (#882) — AT-spezifische Lohn-Felder (rechte Spalte) */}
 {showAnstellung && (
 <Card title={t('employees.detail.lohnsteuer_at_title', 'Lohnsteuer AT')} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>}>
 <div className="text-[11px] text-[var(--text-tertiary)] mb-2">{t('employees.detail.lohnsteuer_at_hint', 'Relevante Felder für die Lohnverrechnung in Österreich — werden an die Steuerberatung übergeben.')}</div>
 <EditableField label={t('employees.detail.stammdaten_bv_kasse', 'BV-Kasse')} field="bv_kasse" value={ma.bv_kasse} ma={ma} setMa={setMa} canEdit={canEdit} hint={t('employees.detail.bv_kasse_hint', 'Betriebliche Vorsorgekasse (z.B. fair-finance)')} />
 <EditableField label={t('employees.detail.stammdaten_fabo_plus', 'FABO Plus (Anzahl Kinder)')} field="fabo_plus" value={ma.fabo_plus} ma={ma} setMa={setMa} canEdit={canEdit} type="number" hint={t('employees.detail.fabo_plus_hint', 'Familienbonus Plus — Anzahl anspruchsberechtigter Kinder')} />
 <EditableField label={t('employees.detail.stammdaten_pendlerpauschale', 'Pendlerpauschale')} field="pendlerpauschale" value={ma.pendlerpauschale} ma={ma} setMa={setMa} canEdit={canEdit} options={pendlerOptions} />
 <EditableField label={t('employees.detail.stammdaten_av_ae_abzug', 'AV/AE-Abzug')} field="av_ae_abzug" value={ma.av_ae_abzug} ma={ma} setMa={setMa} canEdit={canEdit} hint={t('employees.detail.av_ae_abzug_hint', 'Arbeitsvergütung/Alleinerzieher-Abzugsbetrag (Text/Code)')} />
 </Card>
 )}
 </div>

 {/* #979: KrankenstandCard aus Stammdaten entfernt — jetzt in Tab "Zeit & Abwesenheit" */}

 {/* #874 — Sticky Save-Bar bei unsaved changes */}
 {pendingCount > 0 && (
 <div className="lg:col-span-5 sticky bottom-4 z-20 rounded-xl border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800 px-4 py-3 flex items-center justify-between shadow-sm">
 <div className="flex items-center gap-2 text-sm">
 <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
 <span className="font-medium text-red-800 dark:text-red-200">
 {t('employees.detail.unsavedCount', '{{n}} ungespeicherte Änderung(en)').replace('{{n}}', String(pendingCount))}
 </span>
 <span className="text-xs text-red-700 dark:text-red-300">
 ({Object.keys(pendingSaves).join(', ')})
 </span>
 </div>
 <div className="flex items-center gap-2">
 {retryState.msg && (
 <span className={`text-xs ${retryState.msg.type === 'ok' ? 'text-green-700' : 'text-red-700'}`}>{retryState.msg.text}</span>
 )}
 <button onClick={retryAll} disabled={retryState.saving}
 className="px-4 py-1.5 rounded-lg bg-[#338edc] text-white text-sm font-medium hover:bg-[#2b7bc4] disabled:opacity-50">
 {retryState.saving
 ? t('common.saving', 'Speichert…')
 : t('employees.detail.saveAll', 'Alle speichern')}
 </button>
 </div>
 </div>
 )}
 </div>
 </StammdatenSaveCtx.Provider>
 );
}

// ─── Krankenstand Stats Card ──────────────────────────────────
function KrankenstandCard({ maId, t, canQuittung = false }) {
 const { locale } = useLanguage();
 const [data, setData] = useState(null);
 const [grundDraft, setGrundDraft] = useState({}); // antragId → Grund-Text
 const [busyId, setBusyId] = useState(null);

 const load = useCallback(() => {
 fetch(`/api/urlaub/krankenstaende/ma/${maId}`, { credentials: 'include' })
 .then(r => r.json())
 .then(d => { if (d.success) setData(d.data); })
 .catch(() => {});
 }, [maId]);
 useEffect(() => { load(); }, [load]);

 const quittung = async (antragId, erledigt) => {
 setBusyId(antragId);
 try {
 const res = await fetch(`/api/urlaub/krankenstaende/arztnachweis/${antragId}/quittung`, {
 method: 'POST',
 credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: JSON.stringify({ erledigt, grund: erledigt ? (grundDraft[antragId] || '') : '' }),
 });
 if (res.ok) {
 setGrundDraft((p) => { const n = { ...p }; delete n[antragId]; return n; });
 load();
 }
 } finally {
 setBusyId(null);
 }
 };

 if (!data) return null;
 const { aktuell, historie, ohne_arztnachweis, arztnachweis_faelle, mit_arztnachweis } = data;
 // #1745: keine parseInt-Trunkierung — halbe Kranktage (4,5) bleiben sichtbar, Format == Kopf-KPI
 const tage = Number(aktuell.tage) || 0;
 const faelle = parseInt(aktuell.faelle) || 0;
 const fehltCount = ohne_arztnachweis?.length || 0;
 const faelleListe = Array.isArray(arztnachweis_faelle) ? arztnachweis_faelle : [];
 const mitNachweis = Array.isArray(mit_arztnachweis) ? mit_arztnachweis : []; // #1878

 return (
 <Card title={t('krankenstand.statistik')} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>}>
 {/* Arztnachweis-Warnung — zählt nur NICHT-erledigte Fälle (#1611) */}
 {fehltCount > 0 && (
 <div className="mb-3 p-2 rounded-lg flex items-center gap-2 text-xs" style={{ background: '#FEF2F2', border: '1px solid #FECACA' }}>
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
 <span style={{ color: '#991B1B' }}>
 {t('krankenstand.arztnachweisFehlt', 'Arztnachweis fehlt')} ({fehltCount})
 </span>
 </div>
 )}

 {/* #1878: Fälle MIT hochgeladenem Nachweis — Link öffnet die Datei (nur HR-Rollen, audit_log) */}
 {mitNachweis.length > 0 && (
 <div className="mb-3 space-y-2" data-testid="ks-mit-arztnachweis">
 {mitNachweis.map((f) => (
 <div key={`mn-${f.id}`} className="p-2 rounded-lg border text-xs flex items-center justify-between gap-2" style={{ borderColor: '#BBF7D0', background: '#F0FDF4' }}>
 <span className="font-medium text-[var(--text-secondary)]">
 {fmtDate(f.von_datum)}{f.bis_datum ? ` – ${fmtDate(f.bis_datum)}` : ''}
 <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: '#DCFCE7', color: '#166534' }}>{t('krankenstand.arztnachweisVorhanden', 'Arztnachweis vorhanden')}</span>
 </span>
 {canQuittung ? (
 <a href={`/api/mitarbeiter/${maId}/arztnachweise/${f.id}?inline=1`} target="_blank" rel="noreferrer" className="text-[#338edc] hover:underline whitespace-nowrap" data-testid={`ks-arztnachweis-link-${f.id}`}>
 {t('krankenstand.arztnachweisOeffnen', 'Arztnachweis öffnen')} →
 </a>
 ) : null}
 </div>
 ))}
 </div>
 )}

 {/* #1611: Arztnachweis-Fälle pro Fall mit HR-Quittierung (rücknehmbar + Audit) */}
 {faelleListe.length > 0 && (
 <div className="mb-3 space-y-2">
 {faelleListe.map((f) => (
 <div key={f.id} className="p-2 rounded-lg border text-xs" style={{ borderColor: f.arztnachweis_erledigt ? '#BBF7D0' : '#FECACA', background: f.arztnachweis_erledigt ? '#F0FDF4' : '#FEF2F2' }}>
 <div className="flex items-center justify-between gap-2">
 <span className="font-medium text-[var(--text-secondary)]">
 {fmtDate(f.von_datum)}{f.bis_datum ? ` – ${fmtDate(f.bis_datum)}` : ''}
 </span>
 {f.arztnachweis_erledigt
 ? <span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: '#DCFCE7', color: '#166534' }}>{t('krankenstand.quittiertBadge', 'erledigt')}</span>
 : <span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: '#FEE2E2', color: '#991B1B' }}>{t('krankenstand.arztnachweisFehlt', 'Arztnachweis fehlt')}</span>}
 </div>

 {f.arztnachweis_erledigt ? (
 <div className="mt-1.5">
 <div className="text-[var(--text-tertiary)]">
 {t('krankenstand.quittiertVon', 'erledigt von')} <strong className="text-[var(--text-secondary)]">{f.erledigt_von_name || '–'}</strong>
 {f.arztnachweis_erledigt_am ? ` ${t('krankenstand.quittiertAm', 'am')} ${fmtDate(f.arztnachweis_erledigt_am)}` : ''}
 {f.arztnachweis_erledigt_grund ? ` — „${f.arztnachweis_erledigt_grund}"` : ''}
 </div>
 {canQuittung && (
 <button onClick={() => quittung(f.id, false)} disabled={busyId === f.id}
 className="mt-1.5 px-2.5 py-1 rounded-md border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-50">
 {busyId === f.id ? '…' : t('krankenstand.zuruecksetzen', 'Zurücksetzen')}
 </button>
 )}
 </div>
 ) : canQuittung ? (
 <div className="mt-1.5 flex flex-col gap-1.5 sm:flex-row sm:items-center">
 <input
 type="text"
 value={grundDraft[f.id] || ''}
 onChange={(e) => setGrundDraft((p) => ({ ...p, [f.id]: e.target.value }))}
 placeholder={t('krankenstand.grundOptional', 'Grund (optional)')}
 className="flex-1 px-2 py-1 rounded-md border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[var(--text-primary)]"
 />
 <button onClick={() => quittung(f.id, true)} disabled={busyId === f.id}
 className="px-2.5 py-1 rounded-md bg-[#338edc] text-white font-medium hover:bg-[#2b7bc4] disabled:opacity-50 whitespace-nowrap">
 {busyId === f.id ? '…' : t('krankenstand.alsErledigt', 'Erledigt')}
 </button>
 </div>
 ) : null}
 </div>
 ))}
 </div>
 )}

 <div className="grid grid-cols-3 gap-4 text-sm">
 <div>
 <span className="text-[var(--text-tertiary)]">{t('krankenstand.tageJahr')}</span>
 <div className="font-semibold text-[var(--text-primary)]">{fmtNum(tage, tage % 1 ? 1 : 0, locale)}</div>
 </div>
 <div>
 <span className="text-[var(--text-tertiary)]">{t('krankenstand.faelle')}</span>
 <div className="font-semibold text-[var(--text-primary)]">{faelle}</div>
 </div>
 <div>
 <span className="text-[var(--text-tertiary)]">{t('krankenstand.letzter')}</span>
 <div className="font-semibold text-[var(--text-primary)]">
 {aktuell.letzter_von
 ? `${fmtDate(aktuell.letzter_von)}${aktuell.letzter_bis ? ` – ${fmtDate(aktuell.letzter_bis)}` : ''}`
 : '–'}
 </div>
 </div>
 </div>
 {historie.length > 1 && (
 <div className="mt-3 pt-3 border-t border-[var(--line-card,#E5E7EB)] flex gap-4 text-xs text-[var(--text-tertiary)]">
 {historie.filter(h => h.jahr !== aktuell.jahr).slice(0, 3).map(h => (
 <span key={h.jahr}>{h.jahr}: <strong className="text-[var(--text-secondary)]">{h.tage} {t('urlaub.tage')}</strong></span>
 ))}
 </div>
 )}
 </Card>
 );
}

// ═══ EIGENINITIATIVE: Nächste Termine Card ═══
function NextDatesCard({ ma }) {
 const { t, locale } = useLanguage();
 const events = useMemo(() => {
 const items = [];
 const today = new Date();
 today.setHours(0, 0, 0, 0);

 // Birthday
 if (ma.birthday) {
 const bd = new Date(ma.birthday);
 const thisYearBd = new Date(today.getFullYear(), bd.getMonth(), bd.getDate());
 if (thisYearBd < today) thisYearBd.setFullYear(thisYearBd.getFullYear() + 1);
 const daysUntil = Math.ceil((thisYearBd - today) / 86400000);
 const age = thisYearBd.getFullYear() - bd.getFullYear();
 items.push({ date: thisYearBd, label: t('employees.detail.next_birthday', { age }), icon: '🎂', daysUntil, color: 'text-purple-500' });
 }

 // Probezeit (first 1 month)
 if (ma.eintritt_datum) {
 const eintritt = new Date(ma.eintritt_datum);
 const probezeitEnde = new Date(eintritt);
 probezeitEnde.setMonth(probezeitEnde.getMonth() + 1);
 if (probezeitEnde >= today) {
 const daysUntil = Math.ceil((probezeitEnde - today) / 86400000);
 items.push({ date: probezeitEnde, label: t('employees.detail.probation_end'), icon: '⏰', daysUntil, color: 'text-amber-500' });
 }
 }

 // Dienstjubiläum (next anniversary)
 if (ma.eintritt_datum) {
 const eintritt = new Date(ma.eintritt_datum);
 let nextAnni = new Date(today.getFullYear(), eintritt.getMonth(), eintritt.getDate());
 if (nextAnni < today) nextAnni.setFullYear(nextAnni.getFullYear() + 1);
 const jahre = nextAnni.getFullYear() - eintritt.getFullYear();
 if (jahre > 0) {
 const daysUntil = Math.ceil((nextAnni - today) / 86400000);
 items.push({ date: nextAnni, label: t('employees.detail.anniversary', { n: jahre }), icon: '🎉', daysUntil, color: 'text-[#338edc]' });
 }
 }

 return items.sort((a, b) => a.daysUntil - b.daysUntil);
 }, [ma, t]);

 if (events.length === 0) return null;

 return (
 <Card title={t('employees.detail.upcoming_dates')} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>}>
 <div className="space-y-2.5">
 {events.map((e, i) => (
 <div key={i} className="flex items-center gap-3">
 <span className="text-base">{e.icon}</span>
 <div className="flex-1 min-w-0">
 <div className="text-xs text-content-secondary">{e.label}</div>
 <div className="text-[10px] text-[var(--text-tertiary)]">
 {e.date.toLocaleDateString(locale.startsWith('de') ? 'de-AT' : 'en-US', { day: '2-digit', month: 'long', year: 'numeric' })}
 </div>
 </div>
 <span className={`text-xs font-mono font-bold ${e.daysUntil <= 7 ? 'text-amber-500' : e.daysUntil <= 30 ? 'text-[var(--text-secondary)]' : 'text-[var(--text-tertiary)]'}`}>
 {e.daysUntil === 0 ? t('employees.detail.today') : t('employees.detail.in_days', { n: e.daysUntil })}
 </span>
 </div>
 ))}
 </div>
 </Card>
 );
}

// ═══ Aktivitäts-Timeline ═══
// ═══ KARTE: Änderungshistorie (Changelog) ═══
function ChangelogCard({ id }) {
 const { t, locale } = useLanguage();
 const [entries, setEntries] = useState([]);
 const [total, setTotal] = useState(0);
 const [loading, setLoading] = useState(true);
 const [expanded, setExpanded] = useState(false);

 const loadData = useCallback((limit = 10) => {
 fetch(`/api/mitarbeiter/${id}/changelog?limit=${limit}`, { credentials: 'include' })
 .then(r => r.json())
 .then(d => { if (d.rows) { setEntries(d.rows); setTotal(d.total); } })
 .catch(() => {})
 .finally(() => setLoading(false));
 }, [id]);

 useEffect(() => { loadData(10); }, [loadData]);

 const fieldLabel = (f) => t('mitarbeiter.felder.' + f, { defaultValue: f });

 const entityIcon = (type) => {
 switch (type) {
 case 'zeitmodell': return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#338edc" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>;
 case 'gehalt': return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>;
 default: return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
 }
 };

 const fmtVal = (v) => {
 if (v == null || v === '' || v === 'null') return '–';
 if (v === 'true') return t('common.yes');
 if (v === 'false') return t('common.no');
 return v.length > 30 ? v.slice(0, 30) + '…' : v;
 };

 if (loading || entries.length === 0) return null;

 return (
 <Card title={t('employees.detail.changelog_title')} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/></svg>}>
 <div className="space-y-0">
 {entries.map((e, i) => (
 <div key={e.id} className="flex gap-3 py-2 border-b border-line/30 last:border-0">
 <div className="flex flex-col items-center pt-0.5">
 <div className="w-6 h-6 rounded-full bg-[var(--bg-inset,#F8F9FC)] border border-line flex items-center justify-center">
 {entityIcon(e.entity_type)}
 </div>
 {i < entries.length - 1 && <div className="w-px flex-1 bg-line/50 mt-1" />}
 </div>
 <div className="flex-1 min-w-0 pb-1">
 <div className="text-xs text-[var(--text-primary)]">
 <span className="font-medium">{fieldLabel(e.field_name)}</span>
 {': '}
 <span className="text-[var(--text-tertiary)]">{fmtVal(e.old_value)}</span>
 {' → '}
 <span className="font-medium">{fmtVal(e.new_value)}</span>
 </div>
 <div className="text-[10px] text-[var(--text-tertiary)]">
 {new Date(e.created_at).toLocaleDateString(locale.startsWith('de') ? 'de-AT' : 'en-US', { day: '2-digit', month: '2-digit', year: 'numeric' })}
 {' · '}
 {e.user_name}
 </div>
 </div>
 </div>
 ))}
 </div>
 {total > 10 && !expanded && (
 <button onClick={() => { setExpanded(true); loadData(100); }}
 className="mt-3 w-full text-center text-xs text-primary hover:underline">
 {t('employees.detail.changelog_show_all', { n: total })}
 </button>
 )}
 </Card>
 );
}

function AktivitaetenCard({ id }) {
 const { t, locale } = useLanguage();
 const [events, setEvents] = useState([]);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 fetch(`/api/mitarbeiter/${id}/aktivitaeten`, { credentials: 'include' })
 .then(r => r.json())
 .then(d => { if (Array.isArray(d)) setEvents(d); })
 .catch(() => {})
 .finally(() => setLoading(false));
 }, [id]);

 const iconMap = {
 urlaub: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>,
 abwesenheit: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#eab308" strokeWidth="2"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>,
 dokument: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#338edc" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/></svg>,
 vertrag: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/></svg>,
 notiz: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
 };

 const descMap = (e) => {
 const m = e.meta || {};
 switch (e.typ) {
 case 'urlaub': return `${t('employees.detail.activity_leave')} (${m.typ || e.detail}) — ${m.tage || ''} ${t('employees.detail.days')}`;
 case 'abwesenheit': return `${t('employees.detail.activity_absence')}: ${m.art || e.detail}`;
 case 'dokument': return `${t('employees.detail.activity_document')}: ${m.name || ''}`;
 case 'vertrag': return `${t('employees.detail.activity_contract')}: ${m.typ || ''}`;
 case 'notiz': return `${t('employees.detail.activity_note')}: ${m.text || ''}`;
 default: return e.detail || '';
 }
 };

 if (loading) return null;
 if (events.length === 0) return null;

 return (
 <Card title={t('employees.detail.activity_timeline')} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>}>
 <div className="space-y-0">
 {events.map((e, i) => (
 <div key={i} className="flex gap-3 py-2 border-b border-line/30 last:border-0">
 <div className="flex flex-col items-center pt-0.5">
 <div className="w-7 h-7 rounded-full bg-[var(--bg-inset,#F8F9FC)] border border-line flex items-center justify-center">
 {iconMap[e.typ] || iconMap.notiz}
 </div>
 {i < events.length - 1 && <div className="w-px flex-1 bg-line/50 mt-1" />}
 </div>
 <div className="flex-1 min-w-0 pb-1">
 <div className="text-xs text-[var(--text-primary)] truncate">{descMap(e)}</div>
 <div className="text-[10px] text-[var(--text-tertiary)]">
 {new Date(e.created_at).toLocaleDateString(locale.startsWith('de') ? 'de-AT' : 'en-US', { day: '2-digit', month: 'short', year: 'numeric' })}
 {' '}
 {new Date(e.created_at).toLocaleTimeString(locale.startsWith('de') ? 'de-AT' : 'en-US', { hour: '2-digit', minute: '2-digit' })}
 </div>
 </div>
 </div>
 ))}
 </div>
 </Card>
 );
}

// ═══ HR-Notizen ═══
function HrNotizenCard({ id }) {
 const { t } = useLanguage();
 const [notizen, setNotizen] = useState([]);
 const [text, setText] = useState('');
 const [saving, setSaving] = useState(false);

 const load = useCallback(() => {
 fetch(`/api/mitarbeiter/${id}/notizen`, { credentials: 'include' })
 .then(r => r.json())
 .then(d => { if (Array.isArray(d)) setNotizen(d); })
 .catch(() => {});
 }, [id]);

 useEffect(() => { load(); }, [load]);

 const save = async () => {
 if (!text.trim()) return;
 setSaving(true);
 try {
 const res = await fetch(`/api/mitarbeiter/${id}/notizen`, {
 method: 'POST', headers: { 'Content-Type': 'application/json' },
 credentials: 'include', body: JSON.stringify({ text: text.trim() }),
 });
 if (res.ok) { setText(''); load(); }
 } catch {}
 setSaving(false);
 };

 const remove = async (nId) => {
 await fetch(`/api/mitarbeiter/${id}/notizen/${nId}`, { method: 'DELETE', credentials: 'include' });
 load();
 };

 return (
 <Card title={t('employees.detail.hr_notes')} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>}>
 <div className="flex gap-2 mb-3">
 <textarea
 value={text}
 onChange={e => setText(e.target.value)}
 placeholder={t('employees.detail.hr_notes_placeholder')}
 rows={2}
 className="flex-1 bg-[var(--bg-input,#fff)] border border-[var(--border-input,#E5E7EB)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] resize-none focus:outline-none focus:border-[#338edc]"
 />
 <button
 onClick={save}
 disabled={saving || !text.trim()}
 className="self-end px-3 py-2 text-xs font-medium rounded-lg bg-[#338edc] text-white hover:bg-[#2b7bc7] disabled:opacity-40 transition-colors"
 >
 {t('employees.detail.hr_notes_save')}
 </button>
 </div>
 {notizen.length > 0 && (
 <div className="space-y-2 max-h-64 overflow-y-auto">
 {notizen.map(n => (
 <div key={n.id} className="flex gap-2 py-2 border-b border-line/30 last:border-0 group">
 <div className="flex-1 min-w-0">
 <div className="text-xs text-[var(--text-primary)] whitespace-pre-wrap">{n.text}</div>
 <div className="text-[10px] text-[var(--text-tertiary)] mt-0.5">
 {n.user_name || '?'} — {new Date(n.created_at).toLocaleDateString('de-AT')} {new Date(n.created_at).toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' })}
 </div>
 </div>
 <button onClick={() => remove(n.id)} className="opacity-0 group-hover:opacity-100 text-[var(--text-tertiary)] hover:text-red-500 transition-opacity p-1 self-start" title={t('employees.detail.hr_notes_delete')}>
 <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
 </button>
 </div>
 ))}
 </div>
 )}
 {notizen.length === 0 && (
 <EmptyState
 variant="compact"
 icon={<StickyNote className="w-5 h-5" strokeWidth={1.5} />}
 title={t('employees.detail.hr_notes_empty', 'Noch keine HR-Notizen')}
 description={t('employees.detail.hr_notes_empty_hint', 'Notizen sind nur für HR sichtbar. Verwende sie für Gespräche, Vereinbarungen und Beobachtungen.')}
 />
 )}
 </Card>
 );
}

// ═══ EIGENINITIATIVE: Profil-Vollständigkeit ═══
function ProfilVollstaendigkeit({ ma, onFieldClick, compact = false }) {
 const { t } = useLanguage();
 const [hoverOpen, setHoverOpen] = useState(false);

 const checks = useMemo(() => {
 const fields = [
 { key: 'name', label: t('employees.detail.stammdaten_name'), filled: !!(ma.vorname && ma.nachname) },
 { key: 'email', label: 'E-Mail', filled: !!ma.work_email },
 { key: 'svnr', label: 'SVNR', filled: !!ma.svnr },
 { key: 'iban', label: 'IBAN', filled: !!ma.iban },
 { key: 'adresse', label: t('employees.detail.stammdaten_strasse'), filled: !!(ma.strasse && ma.plz && ma.ort) },
 { key: 'eintritt', label: t('employees.detail.stammdaten_eintritt'), filled: !!ma.eintritt_datum },
 { key: 'birthday', label: t('employees.detail.birthday'), filled: !!ma.birthday },
 { key: 'barcode', label: 'Barcode', filled: !!ma.barcode },
 { key: 'notfallkontakt', label: t('employees.detail.emergency_contact'), filled: !!ma.notfallkontakt_name },
 { key: 'brutto', label: t('employees.detail.stammdaten_brutto'), filled: !!ma.brutto_monatlich },
 ];
 const filled = fields.filter(f => f.filled).length;
 const pct = Math.round((filled / fields.length) * 100);
 return { fields, filled, total: fields.length, pct };
 }, [ma, t]);

 if (checks.pct === 100) return null;

 const missing = checks.fields.filter(f => !f.filled);
 const color = checks.pct >= 80 ? '#34d399' : checks.pct >= 50 ? '#eab308' : '#ef4444';

 // #979 — Compact-Variante: Inline-Pill in Header-Sub-Zeile
 if (compact) {
 return (
 <span className="relative inline-flex items-center gap-1.5"
 onMouseEnter={() => setHoverOpen(true)}
 onMouseLeave={() => setHoverOpen(false)}>
 <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium"
 style={{ borderColor: color, color }}>
 <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
 {t('employees.detail.profile_completeness_short', 'Profil')} {checks.pct}%
 </span>
 {hoverOpen && missing.length > 0 && (
 <span className="absolute left-0 top-full mt-1 z-30 w-64 p-3 rounded-lg border border-[var(--border-default,#E5E7EB)] bg-[var(--bg-card,#fff)] shadow-lg">
 <span className="block text-[10px] font-medium text-[var(--text-secondary)] mb-1.5">
 {t('employees.detail.missing_fields')} ({missing.length})
 </span>
 <span className="flex flex-wrap gap-1">
 {missing.map((m, i) => (
 <button
 key={m.key}
 onMouseDown={(e) => { e.preventDefault(); onFieldClick?.(m.key); setHoverOpen(false); }}
 className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-inset,#F8F9FC)] text-[#338edc] hover:underline cursor-pointer"
 >
 {m.label}
 </button>
 ))}
 </span>
 </span>
 )}
 </span>
 );
 }

 return (
 <div className="flex items-center gap-3 mb-4 px-4 py-3 border border-[var(--border-default,#E5E7EB)] rounded-xl bg-[var(--bg-card)]">
 <div className="relative w-10 h-10 flex-shrink-0">
 <svg viewBox="0 0 36 36" className="w-10 h-10">
 <circle cx="18" cy="18" r="15.5" fill="none" stroke="var(--border-card, #E5E7EB)" strokeWidth="3" />
 <circle cx="18" cy="18" r="15.5" fill="none" stroke={color} strokeWidth="3"
 strokeDasharray={`${checks.pct} ${100 - checks.pct}`} strokeDashoffset="25"
 strokeLinecap="round" />
 </svg>
 <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-[var(--text-primary)]">{checks.pct}%</span>
 </div>
 <div className="flex-1 min-w-0">
 <div className="text-xs font-medium text-[var(--text-primary)]">{t('employees.detail.profile_completeness')}</div>
 <div className="text-[10px] text-[var(--text-tertiary)] flex flex-wrap gap-1 mt-1">
 <span>{t('employees.detail.missing_fields')}:</span>
 {missing.map((m, i) => (
 <button
 key={m.key}
 onClick={() => onFieldClick?.(m.key)}
 className="text-[#338edc] hover:underline cursor-pointer"
 >
 {m.label}{i < missing.length - 1 ? ',' : ''}
 </button>
 ))}
 </div>
 </div>
 </div>
 );
}

// ═══ #1159: Urlaubs-Anspruch mit Override-Toggle + Manual-Recalc ═══
function UrlaubsAnspruchBlock({ ma, setMa, canEdit, t }) {
  const [recalcing, setRecalcing] = React.useState(false);
  const [recalcMsg, setRecalcMsg] = React.useState(null);
  const aktiv = !!ma.urlaub_anspruch_override_aktiv;
  const autoWert = ma.urlaub_anspruch_tage ?? '—';
  const overrideWert = ma.urlaub_anspruch_override;

  async function patch(body) {
    const r = await fetch(`/api/mitarbeiter/${ma.id}/stammdaten`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      credentials: 'include', body: JSON.stringify(body),
    });
    const d = await r.json();
    if (!d.error) setMa(prev => ({ ...prev, ...body, ...d }));
    return d;
  }

  async function handleToggle(newVal) {
    if (newVal) {
      await patch({ urlaub_anspruch_override_aktiv: true, urlaub_anspruch_override: overrideWert ?? autoWert });
    } else {
      await patch({ urlaub_anspruch_override_aktiv: false, urlaub_anspruch_override: null, urlaub_anspruch_override_grund: null });
    }
  }

  async function handleOverrideChange(val) {
    const n = val === '' ? null : parseFloat(val);
    await patch({ urlaub_anspruch_override: n });
  }

  async function handleGrundChange(val) {
    await patch({ urlaub_anspruch_override_grund: val || null });
  }

  async function handleRecalc() {
    setRecalcing(true);
    setRecalcMsg(null);
    try {
      const r = await fetch(`/api/urlaub/recompute/${ma.id}`, { method: 'POST', credentials: 'include' });
      const d = await r.json();
      if (d.success) {
        setRecalcMsg(`OK: Anspruch ${d.anspruch_tage ?? '—'} Tage, Aufgebaut ${d.aufgebaut_tage ?? 0} Tage`);
        setMa(prev => ({ ...prev, urlaub_anspruch_tage: d.anspruch_tage }));
      } else {
        setRecalcMsg(`Fehler: ${d.error || 'unbekannt'}`);
      }
    } catch (e) {
      setRecalcMsg(`Fehler: ${e.message}`);
    } finally {
      setRecalcing(false);
      setTimeout(() => setRecalcMsg(null), 5000);
    }
  }

  // #1208 Pre-Eintritts-State + Wartefrist-Hinweise
  const vorEintritt = isVorEintritt(ma);
  const wartefrist = inWartefrist(ma);

  return (
    <>
      <div className="flex justify-between items-center py-1.5 border-b border-line/50">
        <span className="text-xs text-[var(--text-secondary)]">
          {t('employees.detail.stammdaten_urlaub_anspruch')}
          <span className="ml-2 text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">
            {aktiv ? t('urlaub.anspruch.quelle_override', 'Override') : t('urlaub.anspruch.quelle_auto', 'Auto')}
          </span>
        </span>
        <span className="text-sm font-semibold text-[var(--text-primary)]">
          {aktiv && overrideWert != null ? overrideWert : autoWert} {t('employees.detail.days_unit', 'Tage')}
        </span>
      </div>
      {vorEintritt && (
        <div className="px-2 py-1.5 text-[11px] text-blue-800 bg-blue-50 border border-blue-200 rounded-lg my-1.5">
          {t('urlaub.anspruch.vor_eintritt_hinweis', { defaultValue: 'Anteilig ab {{datum}} — Aufbau startet mit Eintritt.', datum: fmtDate(ma.eintritt_datum) })}
        </div>
      )}
      {wartefrist && !vorEintritt && (
        <div className="px-2 py-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg my-1.5">
          {t('urlaub.anspruch.wartefrist_hinweis', { defaultValue: 'Wartefrist bis {{datum}} — voller Anspruch erst danach.', datum: fmtDate(wartefristEndeStr(ma)) })}
        </div>
      )}
      <div className="flex justify-between items-center py-1.5 border-b border-line/50">
        <span className="text-xs text-[var(--text-secondary)]" title={t('urlaub.anspruch.override_tooltip', 'Auto = AT/Woche × 5. Override für Sonderfälle (z.B. Vor-Dienstzeiten).')}>
          {t('urlaub.anspruch.override_label', 'Override aktivieren')}
        </span>
        {canEdit ? (
          <ToggleSwitch checked={aktiv} onChange={handleToggle} />
        ) : (
          <span className="text-sm text-[var(--text-primary)]">{aktiv ? t('common.yes','Ja') : t('common.no','Nein')}</span>
        )}
      </div>
      {aktiv && (
        <>
          <div className="flex justify-between items-center py-1.5 border-b border-line/50">
            <span className="text-xs text-[var(--text-secondary)]">{t('urlaub.anspruch.override_wert', 'Override-Wert (Tage)')}</span>
            {canEdit ? (
              <input
                type="number"
                step="0.5"
                min="0"
                max="60"
                defaultValue={overrideWert ?? ''}
                onBlur={(e) => handleOverrideChange(e.target.value)}
                className="w-24 px-2 py-1 text-sm text-right border border-line rounded bg-white"
              />
            ) : (
              <span className="text-sm text-[var(--text-primary)]">{overrideWert ?? '—'}</span>
            )}
          </div>
          <div className="flex justify-between items-center py-1.5 border-b border-line/50">
            <span className="text-xs text-[var(--text-secondary)]">{t('urlaub.anspruch.override_grund', 'Grund')}</span>
            {canEdit ? (
              <input
                type="text"
                defaultValue={ma.urlaub_anspruch_override_grund ?? ''}
                onBlur={(e) => handleGrundChange(e.target.value)}
                placeholder={t('urlaub.anspruch.grund_placeholder', 'z.B. Vor-Dienstzeiten')}
                className="w-60 px-2 py-1 text-sm border border-line rounded bg-white"
              />
            ) : (
              <span className="text-sm text-[var(--text-primary)]">{ma.urlaub_anspruch_override_grund || '—'}</span>
            )}
          </div>
        </>
      )}
      {canEdit && (
        <div className="flex justify-between items-center py-1.5 border-b border-line/50">
          <span className="text-xs text-[var(--text-secondary)]">{t('urlaub.anspruch.manual_recalc', 'Urlaubskonto neu berechnen')}</span>
          <button
            onClick={handleRecalc}
            disabled={recalcing}
            className="px-3 py-1 text-xs font-medium rounded border border-line bg-white hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-50"
          >
            {recalcing ? t('common.loading','...') : t('urlaub.anspruch.recalc_btn', 'Neu berechnen')}
          </button>
        </div>
      )}
      {recalcMsg && (
        <div className="text-xs text-[var(--text-tertiary)] py-1.5 border-b border-line/50 px-2 bg-[var(--bg-inset,#F8F9FC)] rounded">{recalcMsg}</div>
      )}
    </>
  );
}

// ═══ MAIN PAGE ═══
export default function MitarbeiterDetail() {
 const { t, locale } = useLanguage();
 const { id } = useParams();
 const { user } = useAuth();
 const dialog = useDialog();
 const isAdmin = user?.rolle === 'admin' || user?.rolle === 'administrator';
 const [ma, setMa] = useState(null);
 const [loading, setLoading] = useState(true);
 const [dabeiSeit, setDabeiSeit] = useState(null);
 // editRate/rateValue/rateSaving removed — Persönliche Daten card moved to Stammdaten
 const [activeTab, setActiveTab] = useTabParam('zeit');
 // #979 — Backward-Compat: alter overview-Key → zeit (Default)
 const normalizedTab = activeTab === 'overview' ? 'zeit' : activeTab;
 const [aktionenOpen, setAktionenOpen] = useState(false);
 const [showVertragModal, setShowVertragModal] = useState(false);
 const [abwesenheitOpen, setAbwesenheitOpen] = useState(false);
 const [portalInvSending, setPortalInvSending] = useState(false);
 const [portalInvResult, setPortalInvResult] = useState(null); // 'ok' | 'error' | null
 const [pinResetOpen, setPinResetOpen] = useState(false);
 const [pinResetManual, setPinResetManual] = useState('');
 const [pinResetSms, setPinResetSms] = useState(true);
 const [pinResetSaving, setPinResetSaving] = useState(false);
 const [pinResetResult, setPinResetResult] = useState(null);
 const [fotoKey, setFotoKey] = useState(Date.now());
 const [deleteOpen, setDeleteOpen] = useState(false);
 const [deleting, setDeleting] = useState(false);
 const [austrittModalOpen, setAustrittModalOpen] = useState(false);
 const [austrittModalMode, setAustrittModalMode] = useState('create');
 const [austrittMsg, setAustrittMsg] = useState(null); // { type: 'ok'|'removed', text }
 const navigate = useNavigate();
 const canEditStammdaten = ['admin', 'administrator', 'manager', 'ops_manager'].includes(user?.rolle);
 const canSetAustritt = ['admin', 'administrator', 'ops_manager'].includes(user?.rolle);

 const openAustrittModal = useCallback(() => {
   setAustrittModalMode(ma?.austritt_datum ? 'edit' : 'create');
   setAustrittModalOpen(true);
 }, [ma?.austritt_datum]);

 useEffect(() => {
 setLoading(true);
 Promise.all([
 api(`/api/mitarbeiter/${id}`),
 api(`/api/mitarbeiter/${id}/vertraege`),
 ]).then(([maData, vData]) => {
 if (!maData.error) setMa(maData);
 if (!vData.error && vData.dabei_seit) setDabeiSeit(vData.dabei_seit);
 }).finally(() => setLoading(false));
 }, [id]);

 if (loading) return (
 <div className="p-8 text-center text-[var(--text-tertiary)]">{t('employees.detail.loading')}</div>
 );

 if (!ma) return (
 <div className="p-8 text-center">
 <div className="text-[var(--text-tertiary)] mb-4">{t('employees.detail.not_found')}</div>
 <BackLink to="/mitarbeiter" label={t('employees.detail.back') || t('common.backToList')} className="justify-center" />
 </div>
 );

 return (
 <div className="p-page">
 <BackLink to="/mitarbeiter" label={t('common.backToList')} className="mb-3" />
 {/* Header */}
 <div className="flex items-start gap-5 mb-8">
 {/* Avatar with photo upload */}
 <div className="flex-shrink-0 flex flex-col items-center gap-1.5">
 <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" id={`foto-upload-${id}`}
 onChange={async (e) => {
 const file = e.target.files?.[0];
 if (!file) return;
 if (file.size > 5 * 1024 * 1024) { dialog.alert(t('employees.detail.fotoMaxSize', 'Max. 5 MB')); return; }
 const fd = new FormData();
 fd.append('file', file);
 try {
 const res = await fetch(`/api/mitarbeiter/${id}/foto`, { method: 'POST', credentials: 'include', body: fd });
 if (res.ok) { setFotoKey(Date.now()); setMa(prev => ({ ...prev, foto_pfad: 'uploaded' })); }
 } catch { /* ignore */ }
 e.target.value = '';
 }}
 />
 <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-line bg-surface-inset">
 {(ma.foto_pfad || ma.image_url) ? (
 <img src={`/api/mitarbeiter/${id}/foto?v=${fotoKey}`} alt={ma.name} className="w-full h-full object-cover" />
 ) : (
 <span className="flex items-center justify-center w-full h-full text-xl font-semibold text-[var(--text-secondary)]">
 {(ma.vorname?.[0] || '') + (ma.nachname?.[0] || '') || ma.kuerzel || '?'}
 </span>
 )}
 </div>
 {canEditStammdaten && (
 <div className="flex items-center gap-2">
 <button onClick={() => document.getElementById(`foto-upload-${id}`).click()} className="text-xs hover:underline" style={{ color: '#338edc' }}>
 {ma.foto_pfad ? t('employees.detail.fotoUpload', 'Foto ändern') : t('employees.detail.fotoUploadNew', 'Foto hochladen')}
 </button>
 {ma.foto_pfad && (
 <>
 <span className="text-[var(--text-muted)]">·</span>
 <button onClick={async () => {
 try {
 await fetch(`/api/mitarbeiter/${id}/foto`, { method: 'DELETE', credentials: 'include' });
 setMa(prev => ({ ...prev, foto_pfad: null }));
 setFotoKey(Date.now());
 } catch { /* ignore */ }
 }} className="text-xs text-red-500 hover:underline">
 {t('employees.detail.fotoRemove', 'Foto entfernen')}
 </button>
 </>
 )}
 </div>
 )}
 </div>
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-3 flex-wrap">
 <h1 className="text-xl font-semibold text-content">{ma.name}</h1>
 {ma.personalnummer && <span className="px-2 py-0.5 text-xs font-mono font-medium rounded bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-secondary)] border border-line">{ma.personalnummer}</span>}
 <Badge variant={ma.aktiv ? 'success' : ma.austritt_datum ? 'default' : 'error'}>
 {ma.aktiv ? t('employees.detail.active') : ma.austritt_datum ? t('employees.detail.ehemalig', 'Ehemalig') : t('employees.detail.inactive')}
 </Badge>
 {ma.abteilung_name && <Badge variant="info">{ma.abteilung_name}</Badge>}
 {ma.standort_name && <Badge>{ma.standort_name}</Badge>}
 {ma.typ && ma.typ !== 'person' && <Badge variant="warning">{ma.typ === 'shared' ? t('employees.detail.type_shared') : ma.typ === 'system' ? t('employees.detail.type_system') : ma.typ}</Badge>}
 <Link to={`/mitarbeiter/${id}/pulse`} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border transition-colors hover:bg-[var(--bg-inset)]" style={{ borderColor: 'var(--border-card, #E5E7EB)', color: '#338edc' }}>
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
 Pulse
 </Link>
 </div>
 {ma.berufsbezeichnung && (
 <div className="text-sm text-content-secondary mt-0.5">{ma.berufsbezeichnung}</div>
 )}
 <div className="flex items-center gap-3 mt-2 flex-wrap">
 {ma.work_email && (
 <a href={`mailto:${ma.work_email}`} className="text-xs text-primary-400 hover:underline flex items-center gap-1">
 <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 7l-10 7L2 7"/></svg>
 {ma.work_email}
 </a>
 )}
 {ma.work_phone && (
 <a href={`tel:${ma.work_phone}`} className="text-xs text-content-tertiary hover:text-content flex items-center gap-1">
 <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72"/></svg>
 {ma.work_phone}
 </a>
 )}
 {ma.mobile_phone && (
 <a href={`tel:${ma.mobile_phone}`} className="text-xs text-content-tertiary hover:text-content flex items-center gap-1">
 <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="2" width="14" height="20" rx="2"/><path d="M12 18h.01"/></svg>
 {ma.mobile_phone}
 </a>
 )}
 {dabeiSeit && (
 <span className="text-xs text-content-tertiary flex items-center gap-1">
 <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
 {t('employees.detail.since_prefix')} {fmtDate(dabeiSeit, locale)} ({timeSince(dabeiSeit, t)})
 </span>
 )}
 {/* #979 — Profil-Vollständigkeit inline (kein eigenes Banner) */}
 <ProfilVollstaendigkeit ma={ma} compact onFieldClick={(key) => {
 setActiveTab('stammdaten');
 setTimeout(() => {
 const fieldMap = { email: 'work_email', svnr: 'svnr', iban: 'iban', adresse: 'strasse', eintritt: 'eintritt_datum', brutto: 'brutto_monatlich', notfallkontakt: 'notfallkontakt_name', barcode: 'barcode', birthday: 'birthday' };
 const fieldId = fieldMap[key];
 if (fieldId) {
 const el = document.querySelector(`[data-field="${fieldId}"]`);
 if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.classList.add('ring-2', 'ring-[#338edc]/50', 'rounded-lg'); setTimeout(() => el.classList.remove('ring-2', 'ring-[#338edc]/50', 'rounded-lg'), 3000); }
 }
 }, 150);
 }} />
 </div>
 </div>
 </div>

 {/* KPI Summary */}
 <KpiSummaryCard id={id} />

 {/* Quick-Actions (#979): 1 Primary + Aktionen-Dropdown */}
 <div className="flex items-center gap-2 mb-5 flex-wrap">
 <Link to={`/zeitkonto?ma=${id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-[#338edc]/40 text-[#338edc] hover:bg-[#338edc]/10 transition-colors">
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
 {t('employees.detail.qa_zeitkonto')}
 </Link>

 <div className="relative">
 <button
 onClick={() => setAktionenOpen(v => !v)}
 className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-[var(--border-default,#E5E7EB)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-inset)] transition-colors"
 >
 {t('employees.detail.qa_actions', 'Aktionen')}
 <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
 </button>
 {aktionenOpen && (
 <>
 <div className="fixed inset-0 z-40" onClick={() => setAktionenOpen(false)} />
 {/* #1209 A1: left-0 statt right-0 — Dropdown linksbündig zum Aktionen-Trigger,
      erstreckt sich nach rechts. Vorher wirkte es durch right-0 + w-64 als
      würde es unter den Zeitkonto-Button rutschen. */}
 <div className="absolute left-0 mt-1 z-50 w-64 bg-[var(--bg-card,#fff)] border border-[var(--border-default,#E5E7EB)] rounded-lg shadow-lg py-1.5">
 <Link to={`/abwesenheiten/kalender?ma=${id}`} onClick={() => setAktionenOpen(false)}
 className="flex items-center gap-2 w-full px-3 py-2 text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] hover:text-[var(--text-primary)]">
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
 {t('employees.detail.qa_urlaub')}
 </Link>
 <Link to={`/anwesenheiten?ma=${id}`} onClick={() => setAktionenOpen(false)}
 className="flex items-center gap-2 w-full px-3 py-2 text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] hover:text-[var(--text-primary)]">
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="8.5" cy="7" r="4"/><path d="M20 8v6M23 11h-6"/></svg>
 {t('employees.detail.qa_anwesenheiten')}
 </Link>
 {/* #1209 A2: navigate zum #1205-Wizard auf /vertraege?tab=digital statt
      lokales setShowVertragModal — letzteres triggert das alte DienstvertraegeCard-Modal,
      und das ist nur im 'anstellung'-Tab gemounted (→ silent fail auf anderen Tabs). */}
 <button onClick={() => { setAktionenOpen(false); navigate(`/vertraege?tab=digital&ma=${id}&open=1`); }}
 className="flex items-center gap-2 w-full px-3 py-2 text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] hover:text-[var(--text-primary)]">
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6"/></svg>
 {t('employees.detail.qa_vertrag')}
 </button>
 <button onClick={() => { setAktionenOpen(false); setPinResetOpen(true); }}
 className="flex items-center gap-2 w-full px-3 py-2 text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] hover:text-[var(--text-primary)]">
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
 {t('employees.detail.qa_pinReset', 'PIN zurücksetzen')}
 </button>
 {(ma?.personalnummer || ma?.barcode) && ma?.stempel_pin && (
 <>
 <button
 disabled={portalInvSending}
 onClick={async () => {
 setAktionenOpen(false);
 setPortalInvSending(true);
 setPortalInvResult(null);
 try {
 const res = await fetch(`/api/mitarbeiter/${id}/portal-einladung`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({}) });
 if (res.ok) { setPortalInvResult('ok'); setTimeout(() => setPortalInvResult(null), 5000); }
 else { const d = await res.json().catch(() => ({})); setPortalInvResult(d.error || 'Fehler'); setTimeout(() => setPortalInvResult(null), 5000); }
 } catch { setPortalInvResult('Netzwerkfehler'); setTimeout(() => setPortalInvResult(null), 5000); }
 setPortalInvSending(false);
 }}
 className="flex items-center gap-2 w-full px-3 py-2 text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] hover:text-[var(--text-primary)] disabled:opacity-50">
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></svg>
 {t('employees.detail.qa_portalEinladung', 'Portal-Einladung senden')}
 </button>
 <button
 disabled={portalInvSending}
 onClick={async () => {
 setAktionenOpen(false);
 setPortalInvSending(true);
 setPortalInvResult(null);
 try {
 const res = await fetch(`/api/mitarbeiter/${id}/portal-einladung`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ test: true }) });
 if (res.ok) { setPortalInvResult('ok'); setTimeout(() => setPortalInvResult(null), 5000); }
 else { const d = await res.json().catch(() => ({})); setPortalInvResult(d.error || 'Fehler'); setTimeout(() => setPortalInvResult(null), 5000); }
 } catch { setPortalInvResult('Netzwerkfehler'); setTimeout(() => setPortalInvResult(null), 5000); }
 setPortalInvSending(false);
 }}
 className="flex items-center gap-2 w-full px-3 py-2 text-xs text-[var(--text-tertiary)] hover:bg-[var(--bg-inset)] hover:text-[var(--text-primary)] disabled:opacity-50">
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
 {t('employees.detail.qa_portalTestSms', 'Test an mich')}
 </button>
 </>
 )}
 {canSetAustritt && !ma?.ist_pool_account && (
 <button onClick={() => { setAktionenOpen(false); openAustrittModal(); }}
 className="flex items-center gap-2 w-full px-3 py-2 text-xs text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] hover:text-[var(--text-primary)]">
 <LogOut size={14} />
 {ma?.austritt_datum ? t('austritt.toolbar_edit', 'Austritt bearbeiten') : t('austritt.toolbar_create', 'Austritt erfassen')}
 </button>
 )}
 {isAdmin && (
 <>
 <div className="my-1 border-t border-[var(--border-default,#E5E7EB)]" />
 <button onClick={() => { setAktionenOpen(false); setDeleteOpen(true); }}
 className="flex items-center gap-2 w-full px-3 py-2 text-xs text-red-600 hover:bg-red-50">
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
 {t('employees.detail.delete', 'Mitarbeiter löschen')}
 </button>
 </>
 )}
 </div>
 </>
 )}
 </div>

 {portalInvResult && (
 <span className={`text-xs px-2 py-1 rounded ${
 portalInvResult === 'ok' ? 'text-green-700 bg-green-50 border border-green-300' : 'text-red-600 bg-red-50 border border-red-300'
 }`}>
 {portalInvResult === 'ok' ? t('employees.detail.qa_portalSent', 'Einladung gesendet') : portalInvResult}
 </span>
 )}
 </div>

 {/* PIN Reset Modal */}
 {pinResetOpen && (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setPinResetOpen(false)}>
 <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] shadow-xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
 <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
 <h2 className="text-base font-semibold text-[var(--text-primary)]">{t('employees.detail.pinResetTitle', 'PIN zurücksetzen')}</h2>
 <button onClick={() => setPinResetOpen(false)} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
 <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
 </button>
 </div>
 <div className="p-6 space-y-4">
 <p className="text-sm text-[var(--text-secondary)]">
 {t('employees.detail.pinResetDesc', 'Neuen PIN generieren oder manuell setzen.')}
 </p>
 {ma?.telefon_verifiziert && (
 <p className="text-xs text-green-600 flex items-center gap-1">
 <span>&#10003;</span> {t('employees.detail.phoneVerifiedBadge', 'Telefon verifiziert')}
 </p>
 )}
 <div>
 <label className="text-xs font-medium text-[var(--text-secondary)] block mb-1">{t('employees.detail.pinManualLabel', 'Manueller PIN (optional)')}</label>
 <input
 type="text"
 inputMode="numeric"
 maxLength={4}
 value={pinResetManual}
 onChange={e => setPinResetManual(e.target.value.replace(/\D/g, '').slice(0, 4))}
 placeholder={t('employees.detail.pinAutoGenerate', 'Leer = automatisch generieren')}
 className="w-full px-3 py-2 border border-[var(--border-input,#E5E7EB)] rounded-lg text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-[#338edc]"
 />
 </div>
 {(ma?.mobiltelefon || ma?.telefon) && (
 <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
 <input type="checkbox" checked={pinResetSms} onChange={e => setPinResetSms(e.target.checked)} className="rounded" />
 {t('employees.detail.pinSendSms', 'Neuen PIN per SMS senden')}
 </label>
 )}
 {pinResetResult && (
 <div className={`text-xs p-2 rounded-lg ${pinResetResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
 {pinResetResult.success
 ? t('employees.detail.pinResetSuccess', 'Neuer PIN: {pin}').replace('{pin}', pinResetResult.pin) + (pinResetResult.sms ? ' (SMS gesendet)' : '')
 : pinResetResult.error}
 </div>
 )}
 </div>
 <div className="flex justify-end gap-2 px-6 py-4 border-t border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)]">
 <button onClick={() => setPinResetOpen(false)} className="px-4 py-2 rounded-[10px] border border-[var(--border-card,#E5E7EB)] text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover,#FAFAFE)]">
 {t('common.close', 'Schließen')}
 </button>
 <button
 disabled={pinResetSaving}
 onClick={async () => {
 setPinResetSaving(true);
 setPinResetResult(null);
 try {
 const res = await fetch(`/api/admin/mitarbeiter/${id}/pin-reset`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ sms_senden: pinResetSms, manueller_pin: pinResetManual || undefined }),
 });
 const data = await res.json();
 if (res.ok) {
 setPinResetResult({ success: true, pin: data.neuer_pin, sms: data.sms_gesendet });
 setMa(prev => ({ ...prev, stempel_pin: data.neuer_pin }));
 setPinResetManual('');
 } else {
 setPinResetResult({ success: false, error: data.error });
 }
 } catch { setPinResetResult({ success: false, error: 'Netzwerkfehler' }); }
 setPinResetSaving(false);
 }}
 className="px-4 py-2 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-sm font-semibold disabled:opacity-50 transition-colors"
 >
 {pinResetSaving ? '...' : t('employees.detail.pinResetBtn', 'PIN zurücksetzen')}
 </button>
 </div>
 </div>
 </div>
 )}

 {/* Delete Confirm Modal */}
 {deleteOpen && (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => !deleting && setDeleteOpen(false)}>
 <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] shadow-xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
 <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
 <h2 className="text-base font-semibold text-red-600">{t('employees.detail.deleteTitle', 'Mitarbeiter loeschen')}</h2>
 <button onClick={() => !deleting && setDeleteOpen(false)} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
 <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
 </button>
 </div>
 <div className="px-6 py-4">
 <p className="text-sm text-[var(--text-secondary)]">
 {t('employees.detail.deleteWarning', 'Mitarbeiter {{name}} wirklich loeschen? Alle zugehoerigen Daten (Stempelzeiten, Abwesenheiten, Vertraege, Dokumente) werden ebenfalls geloescht. Diese Aktion kann NICHT rueckgaengig gemacht werden.').replace('{{name}}', ma?.name || '')}
 </p>
 </div>
 <div className="flex justify-end gap-3 px-6 py-4 border-t border-[var(--border-card,#E5E7EB)]">
 <button onClick={() => setDeleteOpen(false)} disabled={deleting}
 className="px-4 py-2 rounded-[10px] border border-[var(--border-default)] text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-inset)] disabled:opacity-50">
 {t('common.cancel', 'Abbrechen')}
 </button>
 <button
 disabled={deleting}
 onClick={async () => {
 setDeleting(true);
 try {
 const res = await fetch(`/api/mitarbeiter/${id}/permanent`, { method: 'DELETE', credentials: 'include' });
 if (res.ok) {
 setDeleteOpen(false);
 navigate('/mitarbeiter');
 } else {
 const d = await res.json().catch(() => ({}));
 dialog.alert(t('common.error', 'Fehler'), d.error || t('employees.detail.deleteError', 'Fehler beim Loeschen'));
 }
 } catch { dialog.alert(t('common.error', 'Fehler'), t('common.networkError', 'Netzwerkfehler')); }
 setDeleting(false);
 }}
 className="px-4 py-2 rounded-[10px] bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors"
 >
 {deleting ? '...' : t('employees.detail.deleteConfirm', 'Endgueltig loeschen')}
 </button>
 </div>
 </div>
 </div>
 )}

 {/* Tab Bar (#979 — 5 fokussierte Tabs + Dokumente + Schulungen) */}
 <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)] inline-flex gap-1 mb-6 flex-wrap">
 {[
 { key: 'zeit', label: t('employees.detail.tab_zeit', 'Zeit & Abwesenheit') },
 { key: 'anstellung', label: t('employees.detail.tab_anstellung', 'Anstellung') },
 { key: 'stammdaten', label: t('employees.detail.tab_stammdaten') },
 { key: 'performance', label: t('employees.detail.tab_performance', 'Performance') },
 { key: 'historie', label: t('employees.detail.tab_historie', 'Historie') },
 { key: 'dokumente', label: t('mitarbeiter.dokumente.title') },
 { key: 'schulungen', label: t('qmTest.tab') || 'Schulungen' },
 ].map(tab => (
 <button
 key={tab.key}
 className={`px-4 py-2 rounded-[8px] text-sm font-medium transition-all ${
 normalizedTab === tab.key
 ? 'bg-white shadow-sm text-[var(--text-primary)] font-semibold dark:bg-[var(--bg-card)]'
 : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
 }`}
 onClick={() => setActiveTab(tab.key)}
 >
 {tab.label}
 </button>
 ))}
 </div>

 {/* Tab Content */}
 {/* Tab 1: Zeit & Abwesenheit (Default — HR-optimiert) */}
 {normalizedTab === 'zeit' && (
 <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
 <OvertimeCard id={id} />
 <VacationCard id={id} />
 <KrankenstandCard maId={parseInt(id)} t={t} canQuittung={['administrator', 'ops_manager'].includes(user?.rolle)} />
 <WeeklyAttendance id={id} />
 <MonthlyAttendance id={id} />
 <LeavesCard id={id} />
 <WorkScheduleCard id={id} className="md:col-span-2" modellTyp={ma?.modell_typ || 'vollzeit'} onModellTypChange={async (v) => {
 await fetch(`/api/mitarbeiter/${id}/stammdaten`, { method: 'PUT', headers: {'Content-Type':'application/json'}, credentials: 'include', body: JSON.stringify({ modell_typ: v }) });
 setMa(prev => prev ? { ...prev, modell_typ: v } : prev);
 }} />
 {canEditStammdaten && (
 <div className="bg-surface-card border border-line rounded-card p-4 flex items-center justify-center">
 <button onClick={() => setAbwesenheitOpen(true)}
 className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs border border-dashed border-line rounded-lg text-[var(--text-secondary)] hover:border-primary hover:text-primary transition-colors">
 <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
 {t('absence.record_absence')}
 </button>
 </div>
 )}
 <AbwesenheitModal id={id} open={abwesenheitOpen} onClose={() => setAbwesenheitOpen(false)} gleitzeitAktiv={!!ma?.gleitzeit_aktiv} />
 </div>
 )}

 {/* Tab 2: Anstellung — 60/40 Layout, Stammdaten-Anstellung mit eigenem 60/40 unten */}
 {normalizedTab === 'anstellung' && (
 <div className="space-y-5">
 <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
 <div className="lg:col-span-3 space-y-5">
 <DienstvertraegeCard id={id} isAdmin={isAdmin} showCreateModal={showVertragModal} setShowCreateModal={setShowVertragModal} austrittDatum={ma?.austritt_datum} />
 {ma?.gleitzeit_aktiv && <GleitzeitPeriodenCard id={id} gleitzeitAktiv={ma.gleitzeit_aktiv} />}
 </div>
 <div className="lg:col-span-2 space-y-5">
 {(isAdmin || user?.rolle === 'manager' || user?.rolle === 'ops_manager') && <AppZugangCard id={id} />}
 </div>
 </div>
 <StammdatenTab
 ma={ma}
 setMa={setMa}
 canEdit={canEditStammdaten}
 userRole={user?.rolle}
 canSetAustritt={canSetAustritt}
 onOpenAustrittModal={openAustrittModal}
 section="anstellung"
 />
 </div>
 )}

 {/* Tab 3: Stammdaten (Persönliche Daten + Kinder + Urlaub-Stammdaten) */}
 {normalizedTab === 'stammdaten' && (
 <StammdatenTab
 ma={ma}
 setMa={setMa}
 canEdit={canEditStammdaten}
 userRole={user?.rolle}
 canSetAustritt={canSetAustritt}
 onOpenAustrittModal={openAustrittModal}
 section="persoenlich"
 />
 )}

 {/* Tab 4: Performance */}
 {normalizedTab === 'performance' && (
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
 <PerformanceCard id={id} />
 <EffektiveArbeitszeitCard id={id} maName={ma.name} />
 <Link to={`/mitarbeiter/${id}/pulse`} className="bg-surface-card border border-line rounded-card p-5 flex flex-col items-start gap-2 hover:border-[#338edc] transition-colors group">
 <div className="flex items-center gap-2 text-sm font-medium text-[var(--text-primary)]">
 <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#338edc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
 {t('employees.detail.pulse_link', 'Pulse-Modul öffnen')}
 </div>
 <p className="text-xs text-[var(--text-tertiary)]">{t('employees.detail.pulse_hint', 'Detailliertes Pulse-Profil mit KPIs, Trends und Vergleichen.')}</p>
 </Link>
 </div>
 )}

 {/* Tab 5: Historie — 60/40 Layout */}
 {normalizedTab === 'historie' && (
 <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
 <div className="lg:col-span-3 space-y-5">
 <ChangelogCard id={id} />
 <NextDatesCard ma={ma} />
 </div>
 <div className="lg:col-span-2 space-y-5">
 {canEditStammdaten && <HrNotizenCard id={id} />}
 <AktivitaetenCard id={id} />
 </div>
 </div>
 )}

 {activeTab === 'dokumente' && (
 <DokumenteTab maId={id} ma={ma} />
 )}

 {activeTab === 'schulungen' && (
 <SchulungenTab maId={id} />
 )}

 {austrittModalOpen && (
 <AustrittModal
 ma={ma}
 mode={austrittModalMode}
 onClose={() => setAustrittModalOpen(false)}
 onSaved={(updatedMa, action) => {
 setMa(prev => ({ ...prev, ...updatedMa }));
 setAustrittModalOpen(false);
 setAustrittMsg({
 type: action === 'removed' ? 'removed' : 'ok',
 text: action === 'removed'
 ? t('austritt.removed', 'Austritt entfernt')
 : t('austritt.saved', 'Austritt gespeichert'),
 });
 setTimeout(() => setAustrittMsg(null), 3000);
 }}
 />
 )}

 {austrittMsg && (
 <div className="fixed bottom-6 right-6 z-[60] px-4 py-2 rounded-lg border border-line bg-surface-card text-sm text-[var(--text-primary)]" style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
 {austrittMsg.text}
 </div>
 )}
 {dialog.element}
 </div>
 );
}
